/**
 * @typedef {Object} BikeData
 * @property {string} id - Identifiant unique du vélo
 * @property {string} marque - Marque du vélo
 * @property {string} modele - Modèle du vélo
 * @property {string} annee - Année du vélo
 * @property {string} energie - Type d'énergie (musculaire/electrique)
 * @property {string} type - Type de vélo (vtt/route/gravel/urbain)
 * @property {number|null} kilometrage - Kilométrage pour vélos électriques
 * @property {number|null} priceEstimate - Prix estimé
 */

/**
 * Custom element pour le funnel de reprise de vélos
 */
class SellYourBikesFunnel extends HTMLElement {
  constructor() {
    super();

    /** @type {BikeData[]} */
    this.bikes = [];

    /** @type {number} */
    this.currentBikeIndex = 0;

    /** @type {string} */
    this.currentStep = 'infos';

    /** @type {Object} */
    this.formData = {};

    this.isInitialized = false;
    this.isAddingNewBike = false;
    this.bikePhotoUrls = {};
    this.isSubmitting = false;
  }

  connectedCallback() {
    if (this.isInitialized) return;

    this.init();
    this.isInitialized = true;
  }

  /**
   * Initialise le composant
   */
  init() {
    this.isDesignMode = this.dataset.designMode === 'true';
    this.parseFieldConfig();
    this.setupEventListeners();
    this.initializeForm();
    this.setupAutocomplete();
    this.updateMobileCounter();
    this.listenToShopifyBlockSelection();

    // GLOBAL LISTENER sur document pour capturer TOUS les changements

    document.addEventListener('change', (e) => {
      // Capturer les changements de bike_brand pour les vélos ajoutés
      if (e.target.name === 'bike_brand') {
        const component = e.target.closest('sell-your-bikes-funnel');
        if (component) {
          component.handleDynamicFieldChange(e.target, '.syb-funnel__bike-brand', '.syb-funnel__bike-brand');
        }
      }

      // Capturer les changements BikeMarque pour les vélos originaux
      if (e.target.id && e.target.id.includes('BikeMarque')) {
        // Traitement des changements de marque
      }
    });
  }

  /**
   * Parse la configuration des champs depuis le data-attribute
   */
  parseFieldConfig() {
    try {
      const configString = this.dataset.fieldConfig;

      if (configString) {
        this.fieldConfig = JSON.parse(configString);

        this.validateConfiguration();
        this.buildFieldMappings();

        this.buildReverseMapping();
        this.extractAvailableBikeFields();
      } else {
        this.fieldConfig = { fieldMappings: {} };
        this.allFieldMappings = {};
        this.reverseFieldMappings = {};
        this.availableBikeFields = [];
      }
    } catch (error) {
      this.fieldConfig = { fieldMappings: {} };
      this.allFieldMappings = {};
      this.reverseFieldMappings = {};
      this.availableBikeFields = [];
    }
  }

  /**
   * Construit un mapping unifié de tous les champs avec validation
   */
  buildFieldMappings() {
    this.allFieldMappings = {};

    if (this.fieldConfig && this.fieldConfig.fieldMappings) {
      try {
        for (const [stepName, stepMappings] of Object.entries(this.fieldConfig.fieldMappings)) {
          if (typeof stepMappings !== 'object' || stepMappings === null) {
            continue;
          }

          for (const [formField, bikeField] of Object.entries(stepMappings)) {
            if (typeof formField !== 'string' || typeof bikeField !== 'string') {
              continue;
            }
            this.allFieldMappings[formField] = bikeField;
          }
        }
      } catch (error) {
        this.allFieldMappings = {};
      }
    }
  }

  /**
   * Valide la configuration JSON
   * @returns {boolean} True si la configuration est valide
   */
  validateConfiguration() {
    if (!this.fieldConfig) {
      return false;
    }

    if (typeof this.fieldConfig !== 'object') {
      return false;
    }

    if (!this.fieldConfig.fieldMappings) {
      return false;
    }

    if (typeof this.fieldConfig.fieldMappings !== 'object') {
      return false;
    }

    return true;
  }

  /**
   * Construit le mapping inversé (bikeField → formField)
   */
  buildReverseMapping() {
    this.reverseFieldMappings = {};

    for (const [formField, bikeField] of Object.entries(this.allFieldMappings)) {
      this.reverseFieldMappings[bikeField] = formField;
    }
  }

  /**
   * Extrait la liste des champs bike disponibles depuis la configuration
   */
  extractAvailableBikeFields() {
    this.availableBikeFields = Array.from(new Set(Object.values(this.allFieldMappings)));
  }

  /**
   * Récupère tous les champs bike configurés
   * @returns {string[]} Liste des champs bike
   */
  getAllBikeFields() {
    return [...this.availableBikeFields];
  }

  /**
   * Parse la configuration pour une étape donnée
   * @param {string} stepName - nom de l'étape (step_infos, step_type, etc.)
   * @returns {Object|null} configuration de l'étape
   */
  parseStepConfiguration(stepName) {
    if (!this.fieldConfig?.fieldMappings) return null;
    return this.fieldConfig.fieldMappings[stepName] || null;
  }

  /**
   * Récupère tous les champs configurés pour une étape
   * @param {string} stepName
   * @returns {Array} [{fieldName, cssSelector, hasDisplay}]
   */
  getStepFieldsConfig(stepName) {
    const stepConfig = this.parseStepConfiguration(stepName);

    if (!stepConfig) {
      return [];
    }

    const fieldsConfig = Object.entries(stepConfig).map(([fieldName, cssSelector]) => ({
      fieldName,
      cssSelector: cssSelector || '',
      hasDisplay: cssSelector && cssSelector.trim() !== '',
    }));

    return fieldsConfig;
  }

  /**
   * Récupère les champs d'une étape spécifique (méthode de compatibilité)
   * @param {string} stepName - Nom de l'étape
   * @returns {Object} Mappings de l'étape
   */
  getStepFields(stepName) {
    if (!this.fieldConfig || !this.fieldConfig.fieldMappings) {
      return {};
    }
    return this.fieldConfig.fieldMappings[stepName] || {};
  }

  /**
   * Récupère le nom du champ form depuis le nom du champ bike (reverse mapping)
   * @param {string} bikeField - Nom du champ bike
   * @returns {string|null} Nom du champ form ou null
   */
  getFormFieldName(bikeField) {
    return this.reverseFieldMappings[bikeField] || null;
  }

  /**
   * Récupère le nom du champ bike card depuis le nom du champ form
   * @param {string} fieldName - Nom du champ dans le formulaire
   * @returns {string|null} Nom du champ dans la bike card ou null
   */
  getBikeCardFieldName(fieldName) {
    return this.allFieldMappings[fieldName] || null;
  }

  /**
   * Configure les écouteurs d'événements
   */
  setupEventListeners() {
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('change', this.handleChange.bind(this));
    this.addEventListener('input', this.handleInput.bind(this));
    this.addEventListener('submit', this.handleSubmit.bind(this));

    document.addEventListener('keydown', this.handleKeydown.bind(this));

    // Restreindre la saisie aux chiffres pour les champs téléphone
    this.setupPhoneInputRestriction();

    // Écouteurs pour mise à jour temps réel de la bike card
    this.setupFormFieldListeners();
  }

  /**
   * Configure la restriction de saisie sur les champs téléphone
   */
  setupPhoneInputRestriction() {
    const phoneInput = this.querySelector('[data-field="phone"]');
    if (!phoneInput) return;

    // Restreindre la saisie aux chiffres et caractères téléphoniques uniquement
    phoneInput.addEventListener('keypress', (e) => {
      const allowedChars = /[0-9+\-\s()]/;
      if (!allowedChars.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
        e.preventDefault();
      }
    });

    // Nettoyer les caractères non autorisés lors du collage
    phoneInput.addEventListener('input', (e) => {
      const cleaned = e.target.value.replace(/[^0-9+\-\s()]/g, '');
      if (cleaned !== e.target.value) {
        e.target.value = cleaned;
      }
    });
  }

  /**
   * Configure les écouteurs pour la mise à jour temps réel - VERSION 100% DYNAMIQUE
   */
  setupFormFieldListeners() {
    try {
      // Initialiser bikeData si pas encore fait
      if (!this.bikeData) {
        this.bikeData = {};
      }

      // Configuration dynamique pour toutes les étapes configurées
      if (this.fieldConfig?.fieldMappings) {
        for (const stepName of Object.keys(this.fieldConfig.fieldMappings)) {
          this.setupStepEventListeners(stepName);
        }
      } else {
      }

      // Gestionnaires spéciaux qui nécessitent une logique particulière
      this.setupSpecialFieldListeners();

      // Listener pour toggle du champ kms selon l'énergie sélectionnée
      const energyInputs = this.querySelectorAll('.syb-funnel__energy-input');
      energyInputs.forEach((input) => {
        input.addEventListener('change', (e) => {
          this.toggleKmsField(e.target.value === 'electrique');
          this.syncElectricYearNotice();
        });
      });

      // Validation du champ kms au blur
      const kmsInput = this.querySelector('input[data-field="bike_kms"]');
      if (kmsInput) {
        kmsInput.addEventListener('blur', (e) => {
          if (kmsInput.required) {
            this.validateKmsFieldOnBlur(e.target.value, e.target);
          }
        });
      }

      // Note: La validation pour step_coordinates sera configurée dynamiquement lors de la navigation

      // Fallback uniquement si pas de configuration
      if (!this.fieldConfig?.fieldMappings || Object.keys(this.fieldConfig.fieldMappings).length === 0) {
        this.setupLegacyFieldListeners();
      }

      // FORCER le système legacy pour tester nos modifications

      this.setupLegacyFieldListeners();

      // GLOBAL LISTENER pour capturer TOUS les changements

      this.addEventListener('change', (e) => {
        // Global change event handling
      });
    } catch (error) {
      // Fallback vers l'ancienne méthode en cas d'erreur
      this.setupLegacyFieldListeners();
    }
  }
  /**
   * Configure les listeners de validation spécifiques à step_coordinates
   */
  setupCoordinatesValidationListeners() {
    const coordinatesStep = this.querySelector('[data-step="coordinates"]');
    if (!coordinatesStep) return;

    // Champs à valider
    const fieldsToValidate = ['firstname', 'lastname', 'email', 'phone'];

    fieldsToValidate.forEach((fieldName) => {
      const field = coordinatesStep.querySelector(`[data-field="${fieldName}"]`);
      if (!field) return;

      // Validation au blur
      field.addEventListener('blur', () => {
        this.validateCoordinatesFieldOnBlur(field.value, field);
      });

      // Formatage en temps réel pour prénom/nom
      if (fieldName === 'firstname' || fieldName === 'lastname') {
        field.addEventListener('input', () => {
          this.formatNameFieldRealTime(field);
        });
      }

      // Filtrage en temps réel pour téléphone + nettoyage erreur
      if (fieldName === 'phone') {
        field.addEventListener('input', () => {
          this.formatPhoneFieldRealTime(field);
          this.clearFieldError(field);
        });
      }

      // Nettoyer l'erreur email dès que l'utilisateur retape
      if (fieldName === 'email') {
        field.addEventListener('input', () => {
          this.clearFieldError(field);
        });
      }
    });
  }

  /**
   * Valide un champ de l'étape coordinates au blur
   * @param {string} value - Valeur du champ
   * @param {HTMLElement} element - Élément DOM
   * @returns {boolean} true si valide
   */
  validateCoordinatesFieldOnBlur(value, element) {
    if (!element) return false;

    const fieldName = element.dataset.field;
    let isValid = false;

    switch (fieldName) {
      case 'firstname':
      case 'lastname':
        isValid = this.validateNameFieldOnBlur(value, element);
        break;
      case 'email':
        isValid = this.validateEmailFieldOnBlur(value, element);
        break;
      case 'phone':
        isValid = this.validatePhoneFieldOnBlur(value, element);
        break;
      default:
        isValid = true;
    }

    return isValid;
  }

  /**
   * Valide un champ nom/prénom au blur
   * @param {string} value - Valeur à valider
   * @param {HTMLElement} element - Élément DOM
   * @returns {boolean} true si valide
   */
  validateNameFieldOnBlur(value, element) {
    if (!value || value.trim() === '') {
      this.showFieldError(element, 'Ce champ est requis');
      return false;
    }

    // Vérifier qu'il n'y a pas de chiffres
    if (/\d/.test(value)) {
      this.showFieldError(element, 'Le nom ne peut pas contenir de chiffres');
      return false;
    }

    // Si validation OK, supprimer l'erreur
    this.clearFieldError(element);
    return true;
  }

  /**
   * Valide un champ email au blur
   * @param {string} value - Valeur à valider
   * @param {HTMLElement} element - Élément DOM
   * @returns {boolean} true si valide
   */
  validateEmailFieldOnBlur(value, element) {
    if (!value || value.trim() === '') {
      this.showFieldError(element, "L'email est requis");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      this.showFieldError(element, "Format d'email invalide");
      return false;
    }

    this.clearFieldError(element);
    return true;
  }

  /**
   * Formate le champ téléphone en temps réel (retire lettres, garde chiffres et +)
   * @param {HTMLElement} element - Élément DOM
   */
  formatPhoneFieldRealTime(element) {
    if (!element) return;

    let value = element.value;
    // Garder seulement les chiffres, espaces, tirets, parenthèses et le +
    value = value.replace(/[^0-9\s\-\(\)\+]/g, '');

    element.value = value;
  }

  /**
   * Valide un champ téléphone au blur
   * @param {string} value - Valeur à valider
   * @param {HTMLElement} element - Élément DOM
   * @returns {boolean} true si valide
   */
  validatePhoneFieldOnBlur(value, element) {
    if (!value || value.trim() === '') {
      this.showFieldError(element, 'Le téléphone est requis');
      return false;
    }

    // Nettoyer le numéro (garder seulement chiffres et +)
    const cleanedValue = value.replace(/[^\d+]/g, '');

    // Vérifier format: + optionnel suivi de chiffres (min 10 chiffres)
    const phoneRegex = /^(\+\d{1,4})?\d{9,}$/;
    if (!phoneRegex.test(cleanedValue)) {
      this.showFieldError(element, 'Format de téléphone invalide');
      return false;
    }

    this.clearFieldError(element);
    return true;
  }

  /**
   * Formate le champ nom en temps réel (retire chiffres + capitalize)
   * @param {HTMLElement} element - Élément DOM
   */
  formatNameFieldRealTime(element) {
    if (!element) return;

    let value = element.value;
    // Retirer les chiffres
    value = value.replace(/\d/g, '');
    // Capitaliser première lettre de chaque mot
    value = value.replace(/\b\w/g, (l) => l.toUpperCase());

    element.value = value;
  }

  /**
   * Toggle l'affichage du champ kilomètres selon le type d'énergie
   * @param {boolean} showKms - True si doit afficher le champ kms
   */
  toggleKmsField(showKms) {
    const kmsField = this.querySelector('.syb-funnel__field--kms');
    const kmsInput = kmsField?.querySelector('input[data-field="bike_kms"]');

    if (!kmsField || !kmsInput) return;

    if (showKms) {
      kmsField.style.display = 'block';
      kmsInput.required = true;
    } else {
      kmsField.style.display = 'none';
      kmsInput.required = false;
      kmsInput.value = '';
      this.clearFieldError(kmsInput);
    }
  }

  /**
   * Valide le champ kilomètres au blur
   * @param {string} value - Valeur à valider
   * @param {HTMLElement} element - Élément DOM
   * @returns {boolean} true si valide
   */
  validateKmsFieldOnBlur(value, element) {
    if (!element) return false;

    const numValue = parseInt(value, 10);

    if (!value || value.trim() === '') {
      this.showFieldError(element, 'Vous devez renseigner ce champ');
      return false;
    }

    if (isNaN(numValue) || numValue < 0 || numValue > 100000) {
      this.showFieldError(element, 'Le kilométrage doit être compris entre 0 et 100 000 km');
      return false;
    }

    this.clearFieldError(element);
    return true;
  }

  /**
   * Configure les listeners dynamiques basés sur la configuration
   */
  setupDynamicFieldListeners() {
    // Parcourir tous les champs configurés
    for (const [formFieldName, bikeFieldName] of Object.entries(this.allFieldMappings)) {
      if (!formFieldName || !bikeFieldName) continue;

      // Recherche par attribut name
      let elements = this.querySelectorAll(`[name="${formFieldName}"]`);

      // Si pas trouvé, recherche par data-field
      if (elements.length === 0) {
        elements = this.querySelectorAll(`[data-field="${formFieldName}"]`);
      }

      // Si pas trouvé, recherche par ID (pour marque/modèle avec section ID)
      if (elements.length === 0 && this.dataset.sectionId) {
        const elementWithId = this.querySelector(`#${formFieldName}-${this.dataset.sectionId}`);
        if (elementWithId) {
          elements = [elementWithId];
        }
      }

      // Attacher les listeners aux éléments trouvés
      elements.forEach((element) => {
        this.attachElementListener(element, bikeFieldName, formFieldName);
      });
    }
  }

  /**
   * Attache un listener à un élément spécifique
   * @param {HTMLElement} element - Élément DOM
   * @param {string} bikeFieldName - Nom du champ bike
   * @param {string} formFieldName - Nom du champ form
   */
  attachElementListener(element, bikeFieldName, formFieldName) {
    if (!element || element.dataset.listenerAttached) return;

    const tagName = element.tagName.toLowerCase();
    const inputType = element.type?.toLowerCase();

    // Déterminer les événements à écouter
    const events = this.getElementEvents(tagName, inputType);

    events.forEach((eventType) => {
      element.addEventListener(eventType, () => {
        this.handleDynamicFieldChange(element, bikeFieldName, formFieldName);
      });
    });

    // Marquer comme traité pour éviter les doublons
    element.dataset.listenerAttached = 'true';
  }

  /**
   * Détermine les événements à écouter selon le type d'élément
   * @param {string} tagName - Tag de l'élément
   * @param {string} inputType - Type d'input
   * @returns {string[]} Liste des événements
   */
  getElementEvents(tagName, inputType) {
    const events = [];

    switch (tagName) {
      case 'select':
        events.push('change');
        break;
      case 'textarea':
        events.push('change', 'input', 'blur');
        break;
      case 'input':
        switch (inputType) {
          case 'radio':
          case 'checkbox':
            events.push('change');
            break;
          case 'text':
          case 'email':
          case 'tel':
          case 'number':
            events.push('change', 'input', 'blur');
            break;
          case 'file':
            events.push('change');
            break;
          default:
            events.push('change', 'input', 'blur');
            break;
        }
        break;
      default:
        events.push('change');
        break;
    }

    return events;
  }

  /**
   * Gère les changements des champs dynamiques
   * @param {HTMLElement} element - Élément modifié
   * @param {string} bikeFieldName - Nom du champ bike
   * @param {string} formFieldName - Nom du champ form
   */
  handleDynamicFieldChange(element, bikeFieldName, formFieldName) {
    try {
      let value = this.getElementValue(element);

      // Validation et transformation de la valeur
      value = this.processFieldValue(value, element, formFieldName);

      // Mise à jour de la bike card
      if (value !== null) {
        this.updateBikeField(bikeFieldName, value);
      }

      // Actions spéciales selon le type de champ
      this.handleSpecialFieldActions(bikeFieldName, value, element);
    } catch (error) {}
  }

  /**
   * Récupère la valeur d'un élément selon son type
   * @param {HTMLElement} element - Élément DOM
   * @returns {string|File[]|null} Valeur de l'élément
   */
  getElementValue(element) {
    const tagName = element.tagName.toLowerCase();
    const inputType = element.type?.toLowerCase();

    switch (tagName) {
      case 'input':
        if (inputType === 'radio' || inputType === 'checkbox') {
          return element.checked ? element.value : null;
        }
        if (inputType === 'file') {
          return element.files ? Array.from(element.files) : null;
        }
        return element.value;
      case 'select':
      case 'textarea':
      default:
        return element.value;
    }
  }

  /**
   * Traite et valide la valeur d'un champ
   * @param {string|File[]} value - Valeur brute
   * @param {HTMLElement} element - Élément DOM
   * @param {string} formFieldName - Nom du champ form
   * @returns {string|File[]|null} Valeur traitée
   */
  processFieldValue(value, element, formFieldName) {
    if (value === null) return null;

    // Gestion spéciale pour les fichiers
    if (element.type === 'file' && Array.isArray(value)) {
      return this.processFileUploads(value, element);
    }

    // Gestion spéciale pour les années avec "other"
    if (formFieldName === 'year' && value === 'other') {
      const yearSelect = this.querySelector('.syb-funnel__year-other-select');
      return yearSelect?.value || '';
    }

    // Validation et nettoyage de base
    const cleanValue = typeof value === 'string' ? value.trim() : String(value);

    // Validation pour step_coordinates
    if (element.dataset.field) {
      return this.validateCoordinatesField(cleanValue, element);
    }

    return cleanValue;
  }

  /**
   * Valide les champs de l'étape coordonnées
   * @param {string} value - Valeur à valider
   * @param {HTMLElement} element - Élément DOM
   * @returns {string|null} Valeur validée ou null si erreur
   */
  validateCoordinatesField(value, element) {
    const fieldType = element.dataset.field;
    const fieldContainer = element.closest('.syb-funnel__field');

    if (!fieldType) return value;

    // Nettoyer les erreurs précédentes
    this.clearFieldError(element);

    switch (fieldType) {
      case 'firstname':
      case 'lastname':
        return this.validateNameField(value, element);

      case 'email':
        return this.validateEmailField(value, element);

      case 'phone':
        return this.validatePhoneField(value, element);

      default:
        return value;
    }
  }

  /**
   * Valide et formate un champ nom/prénom
   * @param {string} value - Valeur à valider
   * @param {HTMLElement} element - Élément DOM
   * @returns {string|null} Valeur formatée ou null si erreur
   */
  validateNameField(value, element) {
    if (!value) {
      this.showFieldError(element, 'Ce champ est requis');
      return null;
    }

    // Vérifier qu'il n'y a pas de chiffres
    if (/\d/.test(value)) {
      this.showFieldError(element, 'Le nom ne doit pas contenir de chiffres');
      return null;
    }

    // Formater en capitalize (première lettre majuscule, reste minuscule)
    const formattedValue = value.toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

    // Mettre à jour la valeur dans l'input
    element.value = formattedValue;

    return formattedValue;
  }

  /**
   * Valide un champ email
   * @param {string} value - Valeur à valider
   * @param {HTMLElement} element - Élément DOM
   * @returns {string|null} Valeur validée ou null si erreur
   */
  validateEmailField(value, element) {
    if (!value) {
      this.showFieldError(element, "L'email est requis");
      return null;
    }

    // Expression régulière pour validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(value)) {
      this.showFieldError(element, 'Veuillez saisir un email valide');
      return null;
    }

    return value;
  }

  /**
   * Valide un champ téléphone
   * @param {string} value - Valeur à valider
   * @param {HTMLElement} element - Élément DOM
   * @returns {string|null} Valeur validée ou null si erreur
   */
  validatePhoneField(value, element) {
    if (!value) {
      this.showFieldError(element, 'Le téléphone est requis');
      return null;
    }

    // Nettoyer la valeur (enlever espaces, tirets, points)
    let cleanValue = value.replace(/[\s\-\.]/g, '');

    // Vérifier format : soit +XX suivi de chiffres, soit que des chiffres
    const phoneRegex = /^(\+\d{1,3})?\d{8,15}$/;

    if (!phoneRegex.test(cleanValue)) {
      this.showFieldError(element, 'Veuillez saisir un numéro de téléphone valide');
      return null;
    }

    // Vérifier qu'il n'y a pas de lettres
    if (/[a-zA-Z]/.test(cleanValue)) {
      this.showFieldError(element, 'Le téléphone ne doit contenir que des chiffres');
      return null;
    }

    return cleanValue;
  }

  /**
   * Affiche une erreur spécifique pour un champ en utilisant les messages pré-rendus
   * @param {HTMLElement} element - Élément en erreur
   * @param {string} errorKey - Clé d'erreur (ex: 'bike_size_required')
   */
  showFieldError(element, errorKey) {
    const fieldContainer = element.closest('.syb-funnel__field') || element.closest('.syb-funnel__checkbox-field');
    if (!fieldContainer) return;

    const errorContainer = fieldContainer.querySelector('.syb-funnel__field-error');
    if (!errorContainer) return;

    // Vérifier si on utilise le nouveau système avec des messages pré-rendus
    const errorMessage = errorContainer.querySelector(`[data-error="${errorKey}"]`);

    if (errorMessage) {
      // Nouveau système : cacher tous les messages et afficher le bon
      const allErrorMessages = errorContainer.querySelectorAll('.syb-funnel__error-message');
      allErrorMessages.forEach((msg) => (msg.style.display = 'none'));

      errorMessage.style.display = 'block';
      errorContainer.style.display = 'block';
    } else {
      // Fallback : créer le message d'erreur dynamiquement depuis les traductions
      const errorMessages = {
        firstname_required: 'Le prénom est requis',
        lastname_required: 'Le nom est requis',
        email_required: "L'email est requis",
        email_invalid: "Format d'email invalide",
        phone_required: 'Le téléphone est requis',
        phone_invalid: 'Format de téléphone invalide',
      };

      const messageText = errorMessages[errorKey] || errorKey;
      errorContainer.textContent = messageText;
      errorContainer.style.display = 'block';
    }

    // Ajouter classe d'erreur à l'élément et au conteneur
    element.classList.add('syb-funnel__field-input--error');
    fieldContainer.classList.add('syb-funnel__field--error');
  }

  /**
   * Nettoie les erreurs d'un champ spécifique
   * @param {HTMLElement} element - Élément à nettoyer
   */
  clearFieldError(element) {
    const fieldContainer = element.closest('.syb-funnel__field') || element.closest('.syb-funnel__checkbox-field');
    if (!fieldContainer) return;

    const errorContainer = fieldContainer.querySelector('.syb-funnel__field-error');
    if (errorContainer) {
      // Cacher tous les messages d'erreur pré-rendus
      const allErrorMessages = errorContainer.querySelectorAll('.syb-funnel__error-message');
      allErrorMessages.forEach((msg) => (msg.style.display = 'none'));

      // Nettoyer aussi le contenu texte pour l'ancien système
      errorContainer.style.display = 'none';
      if (!errorContainer.closest('.syb-funnel__field--bike-size')) {
        errorContainer.textContent = '';
      }
    }

    // Retirer classe d'erreur de l'élément et du conteneur
    element.classList.remove('syb-funnel__field-input--error');
    fieldContainer.classList.remove('syb-funnel__field--error');
  }

  /**
   * Traite les uploads de fichiers avec validation
   * @param {File[]} files - Fichiers uploadés
   * @param {HTMLElement} input - Input file
   * @returns {File[]|null} Fichiers validés
   */
  processFileUploads(files, input) {
    if (!files || files.length === 0) return null;

    const validFiles = [];
    const maxSize = parseInt(input.dataset.maxSize) || 5242880; // 5MB par défaut
    const maxFiles = parseInt(input.dataset.maxFiles) || 1;
    const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'];

    for (const file of files) {
      // Validation format
      if (!acceptedTypes.includes(file.type)) {
        this.showFileError(input, 'invalid_format');
        continue;
      }

      // Validation taille
      if (file.size > maxSize) {
        this.showFileError(input, 'file_too_large');
        continue;
      }

      validFiles.push(file);

      // Limite nombre de fichiers
      if (validFiles.length >= maxFiles) break;
    }

    if (validFiles.length > 0) {
      this.createPhotoPreview(validFiles[0], input);
      this.refreshAllPhotosDisplay();
      this.clearFileError(input);
    }

    return validFiles.length > 0 ? validFiles : null;
  }

  /**
   * Crée un aperçu de la photo dans le label
   * @param {File} file - Fichier image
   * @param {HTMLElement} input - Input file
   */
  createPhotoPreview(file, input, mockFile = null) {
    // Chercher le label par attribut for= car input est avant le label
    const inputId = input.id;
    const label = inputId ? this.querySelector(`label[for="${inputId}"]`) : input.closest('label');

    if (!label) {
      return;
    }

    // Supprimer aperçu existant
    const existingPreview = label.querySelector('.syb-funnel__photo-preview-container');
    if (existingPreview) {
      existingPreview.remove();
    }

    // Créer l'image de préview
    const img = document.createElement('img');

    // Gérer le cas où file est un dataUrl (string) ou un File object
    let imageUrl;
    let fileName;

    if (typeof file === 'string') {
      // C'est un dataUrl
      imageUrl = file;
      fileName = mockFile ? mockFile.name : 'photo.jpg';
    } else if (file instanceof File) {
      // C'est un File object valide
      imageUrl = URL.createObjectURL(file);
      fileName = file.name;
    } else {
      // Fallback si file n'est pas valide

      return;
    }

    img.src = imageUrl;
    img.className = 'syb-funnel__photo-preview';
    img.alt = fileName;

    // Créer le bouton de suppression
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'syb-funnel__photo-remove';
    removeBtn.innerHTML = '×';
    removeBtn.setAttribute('aria-label', 'Supprimer la photo');

    removeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.handlePhotoRemoval(input);
    });

    // Conteneur de l'aperçu
    const previewContainer = document.createElement('div');
    previewContainer.className = 'syb-funnel__photo-preview-container';
    previewContainer.appendChild(img);
    previewContainer.appendChild(removeBtn);

    // Ajouter au label et marquer comme rempli
    label.appendChild(previewContainer);
    label.classList.add('syb-funnel__photo-area--filled');
  }

  /**
   * Supprime l'aperçu photo du label
   * @param {HTMLElement} input - Input file
   */
  removePhotoPreview(input) {
    // Chercher le label par attribut for= car input est avant le label
    const inputId = input.id;
    const label = inputId ? this.querySelector(`label[for="${inputId}"]`) : input.closest('label');

    if (!label) return;

    const previewContainer = label.querySelector('.syb-funnel__photo-preview-container');
    if (previewContainer) {
      previewContainer.remove();
      label.classList.remove('syb-funnel__photo-area--filled');
    }

    // Réinitialiser l'input pour le vider complètement
    input.value = '';
    input.files = null;

    // Mettre à jour la bike card avec toutes les photos restantes

    this.refreshAllPhotosDisplay();
  }

  /**
   * Met à jour l'affichage des photos dans la bike card courante
   * @param {File[]} photos - Array des fichiers photos
   */
  updateBikeCardPhotos(photos) {
    const bikeCards = this.querySelectorAll(`[data-bike-index="${this.currentBikeIndex}"]`);

    if (!bikeCards.length) {
      return;
    }

    const hasPhotos = photos && photos.length > 0;

    bikeCards.forEach((bikeCard, cardIndex) => {
      const photosContainer = bikeCard.querySelector('.syb-funnel__bike-photos');
      const iconPlaceholder = bikeCard.querySelector('.syb-funnel__bike-icon');

      if (!photosContainer || !iconPlaceholder) return;

      if (hasPhotos) {
        photosContainer.style.display = 'flex';
        iconPlaceholder.style.display = 'none';
        this.updatePhotoSlots(photos, bikeCard);
      } else {
        photosContainer.style.display = 'none';
        iconPlaceholder.style.display = 'flex';
      }
    });
  }

  /**
   * Met à jour les slots individuels de photos dans la bike card
   * @param {File[]} photos - Array des fichiers photos
   * @param {Element} bikeCard - Bike card spécifique à modifier
   */
  updatePhotoSlots(photos, bikeCard) {
    const photoSlots = [
      '.syb-funnel__bike-photo--main',
      '.syb-funnel__bike-photo--additional-1',
      '.syb-funnel__bike-photo--additional-2',
      '.syb-funnel__bike-photo--additional-3',
      '.syb-funnel__bike-photo--additional-4',
    ];

    // Obtenir l'index de la bike card pour isolation des URLs
    const bikeIndex = parseInt(bikeCard.dataset.bikeIndex) || this.currentBikeIndex;
    const hasPhotos = photos && photos.length > 0;

    // Initialiser le stockage des URLs pour ce vélo
    if (!this.bikePhotoUrls[bikeIndex]) {
      this.bikePhotoUrls[bikeIndex] = [];
    }

    photoSlots.forEach((selector, index) => {
      const slot = bikeCard.querySelector(selector);

      if (!slot) return;

      if (photos[index]) {
        // Afficher le slot et injecter l'image
        const img = slot.querySelector('img') || document.createElement('img');

        // FORCER la recréation de l'URL pour le remplacement d'image
        const photo = photos[index];
        let photoUrl;

        if (photo instanceof File || photo instanceof Blob) {
          // File ou Blob object → créer URL (FORCE la recréation)
          photoUrl = URL.createObjectURL(photo);
        } else if (typeof photo === 'string') {
          // Déjà une URL string → utiliser directement
          photoUrl = photo;
        } else {
          // Type inattendu → essayer de gérer ou ignorer

          return; // Skip cette photo
        }

        // FORCE la mise à jour de l'URL dans le cache
        this.bikePhotoUrls[bikeIndex][index] = photoUrl;

        img.src = this.bikePhotoUrls[bikeIndex][index];
        img.className = 'syb-funnel__bike-photo-img';
        img.alt = photos[index]?.name || `Photo ${index + 1}`;

        if (!slot.querySelector('img')) {
          slot.innerHTML = '';
          slot.appendChild(img);
        } else {
        }
      }
    });

    if (hasPhotos) {
      const imagesContainer = bikeCard.querySelector('.syb-funnel__bike-images');
      imagesContainer?.classList.add('syb-funnel__bike-images--has-photos');
    }
  }

  /**
   * Collecte les photos du vélo courant avec logique hybride
   * @returns {Object} Objet des photos {fieldName: File}
   */
  getCurrentBikePhotos() {
    const photos = {};

    // 1. D'abord essayer les inputs (nouvelles photos)
    const inputs = this.querySelectorAll('input[type="file"][data-field*="photo"]');
    for (const input of inputs) {
      if (input.files && input.files[0]) {
        const fieldName = this.getPhotoFieldName(input);
        if (fieldName) {
          photos[fieldName] = input.files[0];
        }
      }
    }

    // 2. Fallback vers photos stockées (modification)
    const currentBike = this.getCurrentBike();
    if (currentBike?.photos) {
      for (const [field, file] of Object.entries(currentBike.photos)) {
        if (!photos[field] && file instanceof File) {
          photos[field] = file;
        }
      }
    }

    return photos;
  }

  /**
   * Collecte toutes les photos actuellement uploadées (legacy)
   * @deprecated Utiliser getCurrentBikePhotos() à la place
   * @returns {File[]} Array des fichiers photos
   */
  getAllUploadedPhotos() {
    const photos = [];
    const photoInputs = this.querySelectorAll('input[type="file"][name*="photo"]');

    photoInputs.forEach((input) => {
      if (input.files && input.files.length > 0) {
        photos.push(...Array.from(input.files));
      }
    });

    return photos;
  }

  /**
   * Affiche une erreur de fichier
   * @param {HTMLElement} input - Input file
   * @param {string} errorType - Type d'erreur
   */
  showFileError(input, errorType) {
    const errorElement = input.parentNode.querySelector('.syb-funnel__error');
    if (!errorElement) return;

    const errorMessages = {
      file_too_large: 'Fichier trop volumineux (max 5 Mo)',
      invalid_format: 'Format non supporté (JPG, PNG, HEIC uniquement)',
      max_files_reached: 'Maximum 4 photos autorisées',
    };

    errorElement.textContent = errorMessages[errorType] || 'Erreur de fichier';
    errorElement.style.display = 'block';
  }

  /**
   * Efface l'erreur de fichier
   * @param {HTMLElement} input - Input file
   */
  clearFileError(input) {
    const errorElement = input.parentNode.querySelector('.syb-funnel__error');
    if (errorElement) {
      errorElement.style.display = 'none';
    }
  }

  /**
   * Gère les actions spéciales selon le type de champ
   * @param {string} bikeFieldName - Nom du champ bike
   * @param {string} value - Valeur du champ
   * @param {HTMLElement} element - Élément DOM
   */
  handleSpecialFieldActions(bikeFieldName, value, element) {
    // Mise à jour du placeholder pour la taille selon le type de vélo
    if (bikeFieldName === 'type') {
      this.updateBikeSizePlaceholder(value);
    }
  }

  /**
   * Configure les listeners spéciaux qui nécessitent une logique particulière
   */
  setupSpecialFieldListeners() {
    // Gestion spéciale pour l'année "autre" (select)
    const yearSelect = this.querySelector('.syb-funnel__year-other-select');
    if (yearSelect && !yearSelect.dataset.listenerAttached) {
      yearSelect.addEventListener('change', () => {
        const otherRadio = this.querySelector('.syb-funnel__year-input--other');
        if (otherRadio?.checked) {
          // Mise à jour dynamique avec le nouveau système
          if (this.fieldConfig?.fieldMappings?.step_infos) {
            for (const stepName of Object.keys(this.fieldConfig.fieldMappings)) {
              this.saveBikeDataDynamically(stepName);
            }
          } else {
            // Fallback vers l'ancien système
            const yearBikeCardField = this.getBikeCardFieldName('year') || 'annee';
            this.updateBikeCardField(yearBikeCardField, yearSelect.value);
          }
        }
      });
      yearSelect.dataset.listenerAttached = 'true';
    }

    // Gestion manuelle dédiée pour les inputs photos
    this.setupPhotoEventListeners();

    // Correction bug: Forcer la synchronisation initiale bike-card au chargement
    this.initializeBikeCardSync();
  }

  /**
   * Configure les événements spécifiques aux inputs photos
   */
  setupPhotoEventListeners() {
    const photoInputs = this.querySelectorAll('input[type="file"][name*="photo"]');

    photoInputs.forEach((input) => {
      if (input.dataset.listenerAttached) return;

      input.addEventListener('change', async (e) => {
        try {
          if (e.target.files && e.target.files.length > 0) {
            await this.handlePhotoUpload(e.target, Array.from(e.target.files));
          } else {
            this.handlePhotoRemoval(e.target);
          }
        } catch (error) {
          this.handlePhotoUploadError(error, e.target);
        }
      });

      this.setupPhotoCapture(input);

      input.dataset.listenerAttached = 'true';
    });
  }

  /**
   * Détecte Android en navigateur Chromium (Chrome/Edge/Samsung Internet).
   * Sur ces navigateurs (Android 14+), accept="image/*" sans capture ouvre le
   * Photo Picker (galerie seule, pas de caméra) — bug Chromium #317289301.
   * iOS Safari et Firefox offrent déjà la caméra nativement → on ne les touche pas.
   * @returns {boolean}
   */
  isAndroidChromium() {
    const ua = navigator.userAgent || '';
    return /Android/i.test(ua) && !/Firefox|FxiOS/i.test(ua);
  }

  /**
   * Sur Android Chromium uniquement : intercepte le tap sur l'emplacement photo
   * pour proposer « Prendre une photo » (input dédié capture) ou « Choisir un
   * fichier » (galerie). Les deux alimentent le même handlePhotoUpload.
   * @param {HTMLInputElement} galleryInput - Input photo (galerie) existant
   */
  setupPhotoCapture(galleryInput) {
    if (!this.isAndroidChromium()) return;
    const label = galleryInput.id ? this.querySelector(`label[for="${galleryInput.id}"]`) : null;
    if (!label) return;

    // Input caméra dédié (capture) : alimente le même pipeline que la galerie.
    const cameraInput = document.createElement('input');
    cameraInput.type = 'file';
    cameraInput.accept = 'image/*';
    cameraInput.setAttribute('capture', 'environment');
    cameraInput.style.display = 'none';
    cameraInput.dataset.photoCapture = 'true';
    cameraInput.addEventListener('change', async () => {
      try {
        if (cameraInput.files && cameraInput.files.length > 0) {
          await this.handlePhotoUpload(galleryInput, Array.from(cameraInput.files));
        }
      } catch (error) {
        this.handlePhotoUploadError(error, galleryInput);
      } finally {
        cameraInput.value = '';
      }
    });
    galleryInput.parentNode.appendChild(cameraInput);

    // Intercepte le clic sur la zone (sauf le bouton de suppression).
    label.addEventListener('click', (e) => {
      if (e.target.closest('.syb-funnel__photo-remove')) return;
      e.preventDefault();
      this.showPhotoSourceChooser(galleryInput, cameraInput);
    });
  }

  /**
   * Affiche un sélecteur bas d'écran : caméra ou galerie. Réutilise un singleton.
   * @param {HTMLInputElement} galleryInput - Input galerie
   * @param {HTMLInputElement} cameraInput - Input caméra (capture)
   */
  showPhotoSourceChooser(galleryInput, cameraInput) {
    this.injectPhotoChooserStyles();
    let sheet = document.querySelector('.syb-photo-sheet');
    if (!sheet) {
      sheet = document.createElement('div');
      sheet.className = 'syb-photo-sheet';
      sheet.innerHTML =
        '<div class="syb-photo-sheet__backdrop"></div>' +
        '<div class="syb-photo-sheet__panel" role="dialog" aria-modal="true" aria-label="Ajouter une photo">' +
        '<span class="syb-photo-sheet__handle" aria-hidden="true"></span>' +
        '<p class="syb-photo-sheet__title">Ajouter une photo</p>' +
        '<button type="button" class="syb-photo-sheet__btn syb-photo-sheet__btn--camera" data-action="camera">Prendre une photo</button>' +
        '<button type="button" class="syb-photo-sheet__btn syb-photo-sheet__btn--gallery" data-action="gallery">Choisir dans la galerie</button>' +
        '<button type="button" class="syb-photo-sheet__btn syb-photo-sheet__btn--cancel" data-action="cancel">Annuler</button>' +
        '</div>';
      document.body.appendChild(sheet);
    }

    const close = () => sheet.classList.remove('syb-photo-sheet--open');
    sheet.onclick = (e) => {
      const action = e.target.dataset ? e.target.dataset.action : null;
      if (e.target.classList.contains('syb-photo-sheet__backdrop') || action === 'cancel') {
        close();
      } else if (action === 'camera') {
        close();
        cameraInput.click();
      } else if (action === 'gallery') {
        close();
        galleryInput.click();
      }
    };

    requestAnimationFrame(() => sheet.classList.add('syb-photo-sheet--open'));
  }

  /**
   * Injecte une seule fois les styles du sélecteur de source photo.
   */
  injectPhotoChooserStyles() {
    if (document.getElementById('syb-photo-sheet-styles')) return;
    const style = document.createElement('style');
    style.id = 'syb-photo-sheet-styles';
    style.textContent =
      '.syb-photo-sheet{position:fixed;inset:0;z-index:10000;visibility:hidden;pointer-events:none;' +
      'font-family:"Archivo",sans-serif;transition:visibility 0s linear .32s;}' +
      '.syb-photo-sheet--open{visibility:visible;pointer-events:auto;transition:visibility 0s;}' +
      '.syb-photo-sheet__backdrop{position:absolute;inset:0;background:rgba(0,0,0,.5);opacity:0;' +
      'transition:opacity .3s cubic-bezier(.4,0,.2,1);}' +
      '.syb-photo-sheet--open .syb-photo-sheet__backdrop{opacity:1;}' +
      '.syb-photo-sheet__panel{position:absolute;left:0;right:0;bottom:0;display:flex;flex-direction:column;' +
      'gap:10px;padding:10px 16px calc(18px + env(safe-area-inset-bottom,0px));background:#fff;' +
      'border-radius:16px 16px 0 0;box-shadow:0 -8px 28px rgba(0,0,0,.18);transform:translateY(100%);' +
      'transition:transform .32s cubic-bezier(.4,0,.2,1);}' +
      '.syb-photo-sheet--open .syb-photo-sheet__panel{transform:translateY(0);}' +
      '.syb-photo-sheet__handle{align-self:center;width:40px;height:4px;margin:2px 0 6px;border-radius:999px;' +
      'background:rgba(20,20,20,.15);}' +
      '.syb-photo-sheet__title{margin:0 0 2px;text-align:center;font-size:13px;font-weight:600;' +
      'letter-spacing:.04em;text-transform:uppercase;color:#6b7280;}' +
      '.syb-photo-sheet__btn{appearance:none;width:100%;padding:15px;border-radius:999px;border:1px solid transparent;' +
      'font-family:inherit;font-size:16px;font-weight:600;color:#141414;cursor:pointer;text-align:center;' +
      'transition:transform .1s ease;}' +
      '.syb-photo-sheet__btn:active{transform:scale(.99);}' +
      '.syb-photo-sheet__btn--camera{background:#a2e4b8;color:#141414;}' +
      '.syb-photo-sheet__btn--gallery{background:#fff;color:#141414;border-color:rgba(20,20,20,.18);}' +
      '.syb-photo-sheet__btn--cancel{background:transparent;border:none;color:#6b7280;font-weight:500;' +
      'padding:8px 15px 2px;}' +
      '@media (prefers-reduced-motion: reduce){.syb-photo-sheet__panel,.syb-photo-sheet__backdrop{transition:none;}}';
    document.head.appendChild(style);
  }

  /**
   * Gère l'upload d'une photo pour le vélo courant
   * @param {HTMLInputElement} input - Input file
   * @param {File[]} files - Fichiers uploadés
   */
  /**
   * Affiche le popover d'erreur photo
   * @param {string} errorType - Type d'erreur ('format', 'conversion', 'size', 'dimension', 'upload_error', 'generic')
   */
  showPhotoErrorPopover(errorType = 'format') {
    const popover = this.querySelector('.syb-funnel__photo-error-popover');
    if (!popover) return;

    const titleEl = popover.querySelector('[data-error-title]');
    const messageEl = popover.querySelector('[data-error-message]');

    const errorMessages = {
      format: {
        title: 'Format de fichier non valide',
        message: 'Veuillez utiliser un fichier JPEG, PNG ou HEIC',
      },
      conversion: {
        title: 'Conversion impossible',
        message: 'La conversion de votre photo a échoué. Réessayez, ou envoyez une photo au format JPEG ou PNG.',
      },
      size: {
        title: 'Fichier trop volumineux',
        message: 'La taille du fichier ne doit pas dépasser 10 Mo',
      },
      dimension: {
        title: 'Image trop grande',
        message: 'Les dimensions de votre image dépassent la limite autorisée',
      },
      upload_error: {
        title: 'Erreur de traitement',
        message: 'Une erreur est survenue lors du traitement de votre photo',
      },
      generic: {
        title: 'Erreur de téléchargement',
        message: 'Une erreur est survenue lors du téléchargement de votre photo',
      },
    };

    const error = errorMessages[errorType] || errorMessages.generic;
    if (titleEl && error) titleEl.textContent = error.title;
    if (messageEl && error) messageEl.textContent = error.message;

    popover.classList.remove('syb-funnel__photo-error-popover--hide');
    popover.classList.add('syb-funnel__photo-error-popover--show');

    setTimeout(() => {
      this.hidePhotoErrorPopover();
    }, 5000);
  }

  /**
   * Cache le popover d'erreur photo
   */
  hidePhotoErrorPopover() {
    const popover = this.querySelector('.syb-funnel__photo-error-popover');
    if (!popover) return;

    popover.classList.remove('syb-funnel__photo-error-popover--show');
    popover.classList.add('syb-funnel__photo-error-popover--hide');

    setTimeout(() => {
      popover.classList.remove('syb-funnel__photo-error-popover--hide');
    }, 300);
  }

  /**
   * Gère les erreurs d'upload de photos
   * @param {Error} error - Erreur rencontrée
   * @param {HTMLInputElement} input - Input file
   */
  handlePhotoUploadError(error, input) {
    if (input) {
      input.value = '';
    }
    this.showPhotoErrorPopover('upload_error');
  }

  async handlePhotoUpload(input, files) {
    // Le décodage HEIC (libheif) est SYNCHRONE et bloque le thread principal
    // plusieurs secondes. Si un HEIC est présent : afficher le loader PUIS laisser
    // le navigateur peindre AVANT de lancer le décodage, sinon le freeze masque le
    // loader. Le spinner (animation transform, compositée GPU) continue de tourner
    // pendant le blocage. Sinon (JPEG/PNG, rapide) : loader différé anti-flash.
    const hasHeic = Array.from(files).some((file) => this.isHeicFile(file));
    let loadingTimer = null;
    if (hasHeic) {
      this.setPhotoLoading(input, true);
      await this.nextPaint();
    } else {
      loadingTimer = setTimeout(() => this.setPhotoLoading(input, true), 250);
    }
    try {
      const validFiles = await this.validatePhotoFiles(files, input);

      if (!validFiles || validFiles.length === 0) {
        return;
      }

      this.storePhotoInCurrentBike(input, validFiles[0]);
      this.createPhotoPreview(validFiles[0], input);
      this.refreshCurrentBikePhotosDisplay();
    } catch (error) {
      throw error;
    } finally {
      if (loadingTimer) clearTimeout(loadingTimer);
      this.setPhotoLoading(input, false);
    }
  }

  /**
   * Attend la prochaine peinture du navigateur (double rAF) pour garantir que le
   * loader est rendu à l'écran AVANT une opération synchrone bloquante.
   * @returns {Promise<void>}
   */
  nextPaint() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }

  /**
   * Affiche/masque un loader sur la cellule photo pendant le traitement
   * (conversion HEIC + compression). Style auto-injecté (pas de dépendance build).
   * @param {HTMLInputElement} input - Input file concerné
   * @param {boolean} isLoading - true pour afficher, false pour masquer
   */
  setPhotoLoading(input, isLoading) {
    const container = input.closest('.syb-funnel__photo-container');
    if (!container) return;
    const existing = container.querySelector('.syb-photo-loading');
    if (isLoading) {
      if (existing) return;
      this.injectPhotoLoadingStyles();
      if (getComputedStyle(container).position === 'static') {
        container.style.position = 'relative';
      }
      const overlay = document.createElement('div');
      overlay.className = 'syb-photo-loading';
      overlay.innerHTML =
        '<span class="syb-photo-loading__spinner" aria-hidden="true"></span>' +
        '<span class="syb-photo-loading__text">Traitement…</span>';
      container.appendChild(overlay);
    } else if (existing) {
      existing.remove();
    }
  }

  /**
   * Injecte une seule fois les styles du loader photo (spinner + overlay).
   */
  injectPhotoLoadingStyles() {
    if (document.getElementById('syb-photo-loading-styles')) return;
    const style = document.createElement('style');
    style.id = 'syb-photo-loading-styles';
    style.textContent =
      '.syb-photo-loading{position:absolute;inset:0;z-index:5;display:flex;flex-direction:column;' +
      'align-items:center;justify-content:center;gap:8px;background:rgba(255,255,255,.85);' +
      'border-radius:inherit;font-size:13px;color:#333;pointer-events:none;}' +
      '.syb-photo-loading__spinner{width:28px;height:28px;border-radius:50%;' +
      'border:3px solid rgba(0,0,0,.15);border-top-color:#333;animation:syb-photo-spin .8s linear infinite;}' +
      '@keyframes syb-photo-spin{to{transform:rotate(360deg);}}';
    document.head.appendChild(style);
  }

  /**
   * Gère la suppression d'une photo du vélo courant
   * @param {HTMLInputElement} input - Input file
   */
  handlePhotoRemoval(input) {
    // Supprimer la photo du vélo courant
    this.removePhotoFromCurrentBike(input);

    this.removePhotoPreview(input);

    // Mettre à jour seulement la bike card courante
    this.refreshCurrentBikePhotosDisplay();
  }

  /**
   * Actualise l'affichage des photos pour le vélo courant
   */
  refreshCurrentBikePhotosDisplay() {
    const currentBike = this.getCurrentBike();
    const bikePhotos = currentBike?.photos || {};

    this.updateBikeCardWithMappedPhotos(bikePhotos);
  }

  /**
   * Stocke une photo dans le vélo courant
   * @param {HTMLInputElement} input - Input file
   * @param {File} file - Fichier photo
   */
  storePhotoInCurrentBike(input, file) {
    const currentBike = this.getCurrentBike();
    if (!currentBike) {
      return;
    }

    // Initialiser l'objet photos si nécessaire
    if (!currentBike.photos) {
      currentBike.photos = {};
    }

    // Déterminer le nom du champ photo basé sur l'input
    const photoFieldName = this.getPhotoFieldName(input);

    if (photoFieldName) {
      currentBike.photos[photoFieldName] = file;
      this.bikes[this.currentBikeIndex] = currentBike;
    }
  }

  /**
   * Supprime une photo du vélo courant
   * @param {HTMLInputElement} input - Input file
   */
  removePhotoFromCurrentBike(input) {
    const currentBike = this.getCurrentBike();
    if (!currentBike || !currentBike.photos) return;

    const photoFieldName = this.getPhotoFieldName(input);
    if (photoFieldName && currentBike.photos[photoFieldName]) {
      delete currentBike.photos[photoFieldName];
    }
  }

  /**
   * Détermine le nom du champ photo basé sur l'input
   * @param {HTMLInputElement} input - Input file
   * @returns {string|null} Nom du champ photo
   */
  getPhotoFieldName(input) {
    const dataField = input.dataset.field;
    const name = input.name || input.id;
    const fieldName = dataField || name;

    if (fieldName && fieldName.startsWith('additional_photo_')) {
      return fieldName.replace('additional_photo_', 'additional_photos_');
    }

    return fieldName;
  }

  /**
   * Actualise l'affichage des photos pour le vélo courant (LEGACY - sera supprimée)
   */
  refreshCurrentBikePhotosDisplayLegacy() {
    const currentBike = this.getCurrentBike();

    if (!currentBike) {
      return;
    }

    const rawPhotos = currentBike?.photos ? Object.values(currentBike.photos) : [];

    // Filtrer seulement les File objects valides (exclure null, arrays vides, etc.)
    const bikePhotos = rawPhotos.filter((photo) => photo instanceof File);

    this.updateBikeCardPhotos(bikePhotos);
  }

  /**
   * Force la mise à jour complète des photos dans la bike card
   */
  forceRefreshBikeCardPhotos() {
    const currentBike = this.getCurrentBike();

    if (!currentBike) {
      return;
    }

    // Récupérer toutes les photos du vélo courant
    const allPhotos = [];
    if (currentBike.photos) {
      for (const [fieldName, photo] of Object.entries(currentBike.photos)) {
        if (photo instanceof File) {
          allPhotos.push(photo);
        }
      }
    }

    // Forcer la mise à jour des bike cards
    const bikeCards = this.querySelectorAll(`[data-bike-index="${this.currentBikeIndex}"]`);

    bikeCards.forEach((bikeCard, index) => {
      this.forceUpdateSingleBikeCard(bikeCard, allPhotos);
    });
  }

  /**
   * Force la mise à jour d'une bike card spécifique
   * @param {HTMLElement} bikeCard - La bike card à mettre à jour
   * @param {File[]} photos - Les photos à afficher
   */
  forceUpdateSingleBikeCard(bikeCard, photos) {
    const photosContainer = bikeCard.querySelector('.syb-funnel__bike-photos');
    const iconPlaceholder = bikeCard.querySelector('.syb-funnel__bike-icon');

    if (!photosContainer || !iconPlaceholder) {
      return;
    }

    const hasPhotos = photos && photos.length > 0;

    if (hasPhotos) {
      // Afficher le conteneur photos et cacher l'icône
      photosContainer.style.display = 'flex';
      iconPlaceholder.style.display = 'none';

      // Forcer la mise à jour des slots photo
      this.forceUpdatePhotoSlots(photos, bikeCard);
    } else {
      // Cacher les photos et afficher l'icône
      photosContainer.style.display = 'none';
      iconPlaceholder.style.display = 'flex';
    }
  }

  /**
   * Force la mise à jour des slots de photos dans une bike card
   * @param {File[]} photos - Les photos à afficher
   * @param {HTMLElement} bikeCard - La bike card concernée
   */
  forceUpdatePhotoSlots(photos, bikeCard) {
    const photoSlots = bikeCard.querySelectorAll('.syb-funnel__bike-photo');

    // Réinitialiser tous les slots d'abord
    photoSlots.forEach((slot) => {
      slot.innerHTML = '';
      slot.style.display = 'none';
    });

    // Remplir avec les nouvelles photos
    photos.forEach((photo, index) => {
      if (index < photoSlots.length) {
        const slot = photoSlots[index];

        const img = document.createElement('img');
        img.src = URL.createObjectURL(photo);
        img.className = 'syb-funnel__bike-photo-img';
        img.alt = photo.name;

        slot.innerHTML = '';
        slot.appendChild(img);
        slot.style.display = 'block';
      }
    });
  }

  /**
   * Compresse une image automatiquement
   * @param {File} file - Fichier image original
   * @param {number} maxWidth - Largeur maximale
   * @param {number} maxHeight - Hauteur maximale
   * @param {number} quality - Qualité JPEG (0-1)
   * @returns {Promise<File>} Fichier compressé
   */
  async compressImage(file, maxSizeBytes = 2 * 1024 * 1024) {
    const MAX_SIZE = maxSizeBytes;
    const maxDimension = 3000;

    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      const timeout = setTimeout(() => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Image compression timeout'));
      }, 15000);

      img.onload = async () => {
        clearTimeout(timeout);

        const ratio = Math.min(maxDimension / img.width, maxDimension / img.height, 1);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        let quality = 0.9;

        while (quality >= 0.3) {
          try {
            const blob = await new Promise((resolveBlob) => {
              canvas.toBlob(resolveBlob, 'image/jpeg', quality);
            });

            if (!blob) {
              quality -= 0.1;
              continue;
            }

            if (blob.size <= MAX_SIZE) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });

              URL.revokeObjectURL(img.src);
              resolve(compressedFile);
              return;
            }

            quality -= 0.1;
          } catch (error) {
            quality -= 0.1;
          }
        }

        URL.revokeObjectURL(img.src);
        resolve(file);
      };

      img.onerror = (error) => {
        clearTimeout(timeout);
        URL.revokeObjectURL(img.src);
        resolve(file);
      };

      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Valide les dimensions d'une image
   * @param {File} file - Fichier image
   * @param {number} maxDimension - Dimension maximale
   * @returns {Promise<boolean>} True si valide
   */
  async validateImageDimensions(file, maxDimension = 6000) {
    return new Promise((resolve) => {
      const img = new Image();

      const timeout = setTimeout(() => {
        console.error('validateImageDimensions - Timeout after 5 seconds');
        URL.revokeObjectURL(img.src);
        resolve(false);
      }, 5000);

      img.onload = () => {
        clearTimeout(timeout);

        const isValid = img.width <= maxDimension && img.height <= maxDimension;

        URL.revokeObjectURL(img.src);
        resolve(isValid);
      };

      img.onerror = (error) => {
        clearTimeout(timeout);
        console.error('validateImageDimensions - Image load error:', error);
        URL.revokeObjectURL(img.src);
        resolve(false);
      };

      img.src = URL.createObjectURL(file);
    });
  }

  isHeicFile(file) {
    const type = (file.type || '').toLowerCase();
    if (type === 'image/heic' || type === 'image/heif') return true;
    return /\.(heic|heif)$/i.test(file.name || '');
  }

  isAllowedImageFile(file) {
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'];
    const type = (file.type || '').toLowerCase();
    if (allowed.includes(type)) return true;
    if (type === '' || type === 'application/octet-stream') {
      return /\.(jpe?g|png|heic|heif)$/i.test(file.name || '');
    }
    return false;
  }

  ensureLibheif() {
    // window.libheif est une FACTORY Emscripten (wasm inliné) : l'appeler retourne
    // une Promise résolvant le module, lequel expose HeifDecoder une fois le wasm
    // initialisé. On met en cache le module résolu (_libheifModule).
    if (this._libheifModule) return Promise.resolve(this._libheifModule);
    if (this._libheifPromise) return this._libheifPromise;
    const url = this.dataset.libheifUrl;
    if (!url) return Promise.reject(new Error('libheif URL manquante'));
    const loadFactory = window.libheif
      ? Promise.resolve(window.libheif)
      : new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = url;
          script.onload = () => (window.libheif ? resolve(window.libheif) : reject(new Error('libheif indisponible')));
          script.onerror = () => reject(new Error('Échec chargement libheif'));
          document.head.appendChild(script);
        });
    this._libheifPromise = loadFactory
      .then((factory) => factory())
      .then((module) => {
        this._libheifModule = module;
        return module;
      });
    // En cas d'échec, ne pas mettre en cache la promesse rejetée (retry possible).
    this._libheifPromise.catch(() => {
      this._libheifPromise = null;
    });
    return this._libheifPromise;
  }

  async convertHeicToJpeg(file) {
    // libheif-js (wasm inliné, thread principal, pas de Web Worker) : décode le
    // HEIC en pixels bruts, rendu sur canvas, ré-encodage JPEG. Aucun fetch wasm
    // ni worker → pas de risque de hang lié à la CSP storefront.
    const libheif = await this.ensureLibheif();
    const buffer = await file.arrayBuffer();
    const decoder = new libheif.HeifDecoder();
    const images = decoder.decode(new Uint8Array(buffer));
    if (!images || images.length === 0) throw new Error('Conversion HEIC : aucune image décodée');
    const image = images[0];
    const width = image.get_width();
    const height = image.get_height();
    if (!width || !height) throw new Error('Conversion HEIC : dimensions invalides');
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.createImageData(width, height);
    await new Promise((resolve, reject) => {
      image.display(imageData, (displayData) => {
        if (!displayData) {
          reject(new Error('Conversion HEIC : échec du rendu'));
          return;
        }
        resolve();
      });
    });
    ctx.putImageData(imageData, 0, 0);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    // Libère la mémoire libheif si l'API l'expose (évite l'accumulation sur multi-photos).
    if (typeof image.free === 'function') image.free();
    if (!blob) throw new Error('Conversion HEIC : échec encodage JPEG');
    const name = (file.name || 'photo').replace(/\.(heic|heif)$/i, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  }

  /**
   * Valide les fichiers photos avec compression automatique
   * @param {File[]} files - Fichiers à valider
   * @param {HTMLInputElement} input - Input file
   * @returns {Promise<File[]|null>} Fichiers valides et compressés ou null
   */
  async validatePhotoFiles(files, input) {
    const maxSize = 10 * 1024 * 1024; // 10 Mo (très permissif pour iOS)
    const maxDimension = 12000; // Pixels (très permissif pour photos iPhone)
    const validFiles = [];
    let hasError = false;
    let errorType = 'generic';

    for (const file of files) {
      let workingFile = file;

      // Conversion HEIC → JPEG (desktop : file.type souvent vide / image/heic)
      if (this.isHeicFile(workingFile)) {
        try {
          workingFile = await this.convertHeicToJpeg(workingFile);
        } catch (error) {
          console.error('[SYB] Conversion HEIC échouée:', error);
          hasError = true;
          errorType = 'conversion';
          break;
        }
      }

      // Vérifier le type (tolérant : fallback extension si file.type vide)
      if (!this.isAllowedImageFile(workingFile)) {
        hasError = true;
        errorType = 'format';
        break;
      }

      // Validation des dimensions
      const dimensionsValid = await this.validateImageDimensions(workingFile, maxDimension);

      if (!dimensionsValid) {
        hasError = true;
        errorType = 'dimension';
        break;
      }

      let processedFile = workingFile;

      // Compression automatique si fichier > 1 Mo
      if (workingFile.size > 1024 * 1024) {
        try {
          processedFile = await this.compressImage(workingFile);
        } catch (error) {
          // En cas d'erreur de compression, continuer avec le fichier original
          processedFile = workingFile;
        }
      }

      // Vérifier la taille finale après compression
      if (processedFile.size > maxSize) {
        hasError = true;
        errorType = 'size';
        break;
      }

      validFiles.push(processedFile);
    }

    if (hasError) {
      this.showPhotoErrorPopover(errorType);
      input.value = '';
      return null;
    }

    return validFiles.length > 0 ? validFiles : null;
  }

  /**
   * Prérempli les formulaires avec les données de test
   */
  prefillFormData() {
    setTimeout(() => {
      const currentBike = this.getCurrentBike();
      if (!currentBike) return;

      // Préremplir les champs du vélo avec les bons sélecteurs [name="..."]
      Object.entries(currentBike).forEach(([field, value]) => {
        this.prefillFormField(field, value);
      });

      // Forcer la mise à jour de la bike card avec les bons noms d'étapes
      this.saveBikeDataDynamically('step_infos');
      this.saveBikeDataDynamically('step_type');
      this.saveBikeDataDynamically('step_details');
      this.toggleBikeCardState();
      this.updateAllBikeCardFields();
      this.syncBikeCardDynamic();
    }, 200);
  }

  /**
   * Prefill un champ spécifique du formulaire
   * @param {string} fieldName - Nom du champ
   * @param {string|boolean} value - Valeur à définir
   */
  prefillFormField(fieldName, value) {
    if (!fieldName || value === undefined || value === null || value === '') return;

    // Mapping des noms de champs pour le prefill
    const prefillFieldMapping = {
      marque: 'bike_brand',
      modele: 'bike_model',
      annee: 'year',
      energie: 'type',
      typevelo: 'typevelo',
    };

    // Utiliser le mapping si disponible
    const actualFieldName = prefillFieldMapping[fieldName] || fieldName;

    // Chercher l'input avec le bon name, puis par data-field en fallback
    let input = this.querySelector(`[name="${actualFieldName}"]`);
    if (!input) {
      input = this.querySelector(`[data-field="${actualFieldName}"]`);
    }

    // Si toujours pas trouvé, essayer avec le nom original
    if (!input) {
      input = this.querySelector(`[name="${fieldName}"]`);
    }
    if (!input) {
      input = this.querySelector(`[data-field="${fieldName}"]`);
    }

    // Vérifier si l'input trouvé est un input hidden d'autocomplete
    if (input && input.type === 'hidden' && input.dataset.hiddenInput) {
      const autocompleteType = input.dataset.hiddenInput;
      if (this.prefillAutocompleteField(autocompleteType, value)) {
        return;
      }
    }

    // Si pas trouvé, chercher l'input d'autocomplete par ID
    if (!input && this.dataset.sectionId) {
      const fieldNameCapitalized = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
      const autocompleteInput = this.querySelector(`#Bike${fieldNameCapitalized}-${this.dataset.sectionId}`);

      if (autocompleteInput && autocompleteInput.dataset.autocomplete) {
        // Utiliser la méthode spécialisée pour l'autocomplete
        const autocompleteType = autocompleteInput.dataset.autocomplete;
        if (this.prefillAutocompleteField(autocompleteType, value)) {
          return;
        }
      }
    }

    if (input) {
      if (input.type === 'radio') {
        // Pour les radios, cocher le bon choix
        const radioToCheck =
          this.querySelector(`[name="${actualFieldName}"][value="${value}"]`) ||
          this.querySelector(`[name="${fieldName}"][value="${value}"]`);
        if (radioToCheck) {
          radioToCheck.checked = true;
          radioToCheck.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else if (input.type === 'checkbox') {
        // Pour les checkboxes
        input.checked = Boolean(value);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (input.tagName === 'SELECT') {
        // Pour les selects, définir la valeur
        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        // Pour les inputs text/email/tel, définir la valeur
        input.value = value;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /**
   * Met à jour tous les champs de la bike card avec les données actuelles
   */
  updateAllBikeCardFields() {
    const currentBike = this.getCurrentBike();
    if (!currentBike) return;

    // Mettre à jour chaque champ de la bike card avec les méthodes correctes
    if (currentBike.marque) this.updateBikeField('marque', currentBike.marque);
    if (currentBike.modele) this.updateBikeField('modele', currentBike.modele);
    if (currentBike.annee) this.updateBikeField('annee', currentBike.annee);
    if (currentBike.energie) this.updateBikeField('energie', currentBike.energie);
    if (currentBike.type) this.updateBikeField('type', currentBike.type);
    if (currentBike.bike_size) this.updateBikeField('bike_size', currentBike.bike_size);
  }

  /**
   * Collecte les photos mappées par input spécifique
   * @returns {Object} Mapping input name → File
   */
  getPhotosByInput() {
    const photosByInput = {};
    const photoInputs = this.querySelectorAll('input[type="file"][name*="photo"]');

    photoInputs.forEach((input) => {
      if (input.files && input.files.length > 0) {
        photosByInput[input.name] = input.files[0]; // Une photo par input
      }
    });

    return photosByInput;
  }

  /**
   * Met à jour l'affichage complet des photos (bike card)
   */
  refreshAllPhotosDisplay() {
    const photosByInput = this.getPhotosByInput();

    this.updateBikeCardWithMappedPhotos(photosByInput);
  }
  /**
   * Met à jour la bike card avec les photos mappées
   * @param {Object} photosByInput - Mapping input name → File
   */
  updateBikeCardWithMappedPhotos(photosByInput) {
    const bikeCards = this.querySelectorAll(`[data-bike-index="${this.currentBikeIndex}"]`);
    if (!bikeCards.length) return;

    const hasPhotos = Object.keys(photosByInput).length > 0;

    for (const bikeCard of bikeCards) {
      const imagesContainer = bikeCard.querySelector('.syb-funnel__bike-images');

      if (hasPhotos) {
        // Toggle classe pour afficher photos, cacher icône via CSS
        imagesContainer?.classList.add('syb-funnel__bike-images--has-photos');

        // Injecter les images dans les slots correspondants
        this.injectMappedPhotosInSlots(photosByInput, bikeCard);
      } else {
        // Retirer classe pour afficher icône, cacher photos via CSS
        imagesContainer?.classList.remove('syb-funnel__bike-images--has-photos');
      }
    }
  }

  /**
   * Injecte les photos dans les slots correspondant aux noms d'inputs
   * @param {Object} photosByInput - Mapping input name → File
   * @param {Element} bikeCard - Bike card spécifique à modifier
   */
  injectMappedPhotosInSlots(photosByInput, bikeCard) {
    const inputToSlotMapping = {
      main_photo: '.syb-funnel__bike-photo--main',
      additional_photos_1: '.syb-funnel__bike-photo--additional-1',
      additional_photos_2: '.syb-funnel__bike-photo--additional-2',
      additional_photos_3: '.syb-funnel__bike-photo--additional-3',
      additional_photos_4: '.syb-funnel__bike-photo--additional-4',
    };

    Object.entries(inputToSlotMapping).forEach(([baseInputName, slotSelector]) => {
      const slot = bikeCard.querySelector(slotSelector);
      if (!slot) return;

      const matchingPhoto = this.findPhotoForSlot(baseInputName, photosByInput);

      if (matchingPhoto) {
        this.injectPhotoInSlot(slot, matchingPhoto);
      } else {
        const iconTemplate = bikeCard.querySelector('.syb-funnel__bike-icon');
        if (iconTemplate) {
          slot.innerHTML = iconTemplate.innerHTML;
        }
      }
    });
  }

  /**
   * Trouve la photo correspondant à un slot donné
   * @param {string} baseInputName - Nom de base du slot
   * @param {Object} photosByInput - Mapping input name → File
   * @returns {File|null} Photo correspondante ou null
   */
  findPhotoForSlot(baseInputName, photosByInput) {
    for (const [inputName, file] of Object.entries(photosByInput)) {
      const extractedName = this.extractBaseInputName(inputName);

      if (extractedName === baseInputName) {
        return file;
      }
    }

    return null;
  }

  /**
   * Extrait le nom de base de l'input (sans suffix settings)
   * @param {string} inputName - Nom complet de l'input
   * @returns {string} Nom de base
   */
  extractBaseInputName(inputName) {
    if (inputName.includes('main_photo')) return 'main_photo';
    if (inputName.includes('additional_photos_1')) return 'additional_photos_1';
    if (inputName.includes('additional_photos_2')) return 'additional_photos_2';
    if (inputName.includes('additional_photos_3')) return 'additional_photos_3';
    if (inputName.includes('additional_photos_4')) return 'additional_photos_4';
    return inputName;
  }

  /**
   * Injecte une photo dans un slot spécifique avec gestion mémoire
   * @param {HTMLElement} slot - Slot de destination
   * @param {File} file - Fichier photo
   */
  injectPhotoInSlot(slot, file) {
    const existingImg = slot.querySelector('img');

    if (existingImg && existingImg.src.startsWith('blob:')) {
      URL.revokeObjectURL(existingImg.src);
    }

    const objectURL = URL.createObjectURL(file);

    // NE PAS révoquer l'URL au onload : elle reste le src de l'<img>. Si la carte
    // est ré-affichée/re-layoutée (ex. étape de finalisation) le navigateur re-fetch
    // le src ; une URL révoquée donnerait une image cassée (alt = nom du fichier).
    // La mémoire est gérée par la révocation au remplacement (cf. plus haut).
    if (existingImg) {
      existingImg.src = objectURL;
      existingImg.alt = file.name;
    } else {
      const img = document.createElement('img');
      img.src = objectURL;
      img.className = 'syb-funnel__bike-photo-img';
      img.alt = file.name;
      slot.innerHTML = '';
      slot.appendChild(img);
    }
  }

  /**
   * Initialise la synchronisation bike-card au chargement
   */
  initializeBikeCardSync() {
    // S'assurer que bikeData existe
    if (!this.bikeData) {
      this.bikeData = {};
    }

    // Synchroniser avec les données existantes du bike actuel si présent
    const currentBike = this.getCurrentBike();
    if (currentBike) {
      // Copier les données du bike vers bikeData pour le nouveau système
      Object.assign(this.bikeData, currentBike);

      // Mettre à jour l'affichage avec les nouvelles méthodes dynamiques
      this.updateBikeCardStateDynamic();
    }
  }

  /**
   * Méthode de fallback vers l'ancien système (pour compatibilité)
   */
  setupLegacyFieldListeners() {
    // Marque
    const marqueSelect = this.querySelector('#BikeMarque-' + this.dataset.sectionId);
    if (marqueSelect && !marqueSelect.dataset.listenerAttached) {
      marqueSelect.addEventListener('change', () =>
        this.handleDynamicFieldChange(marqueSelect, 'marque', '.syb-funnel__bike-brand'),
      );
      marqueSelect.dataset.listenerAttached = 'true';
    }

    // Modèle
    const modeleSelect = this.querySelector('#BikeModele-' + this.dataset.sectionId);
    if (modeleSelect && !modeleSelect.dataset.listenerAttached) {
      modeleSelect.addEventListener('change', () =>
        this.handleDynamicFieldChange(modeleSelect, 'modele', '.syb-funnel__bike-model'),
      );
      modeleSelect.dataset.listenerAttached = 'true';
    }

    // Fallback pour autres champs critiques
    const fallbackFields = [
      { selector: '[name="year"]', bikeField: 'annee' },
      { selector: '[name="type"]', bikeField: 'energie' },
      { selector: '[name="typevelo"]', bikeField: 'type' },
      { selector: '[data-field="bike_size"]', bikeField: 'bike_size' },
    ];

    fallbackFields.forEach(({ selector, bikeField }) => {
      const elements = this.querySelectorAll(selector);
      elements.forEach((element) => {
        if (!element.dataset.listenerAttached) {
          const events = this.getElementEvents(element.tagName.toLowerCase(), element.type);
          events.forEach((eventType) => {
            element.addEventListener(eventType, () => {
              const value = this.getElementValue(element);
              if (value !== null) {
                this.updateBikeCardField(bikeField, value);
              }
            });
          });
          element.dataset.listenerAttached = 'true';
        }
      });
    });
  }
  /**
   * Sauvegarde tous les champs d'une étape dynamiquement
   * @param {string} stepName - nom de l'étape
   */
  saveBikeDataDynamically(stepName) {
    const fieldsConfig = this.getStepFieldsConfig(stepName);
    if (!fieldsConfig.length) {
      return;
    }

    for (const { fieldName, cssSelector, hasDisplay } of fieldsConfig) {
      // Récupération valeur depuis le formulaire
      const fieldValue = this.getFormFieldValueByName(fieldName);

      // Stockage dans bikeData
      if (!this.bikeData) this.bikeData = {};
      this.bikeData[fieldName] = fieldValue;

      // Affichage dans bike-card si configuré
      if (hasDisplay && fieldValue) {
        this.updateBikeCardFieldDynamic(cssSelector, fieldValue);
      } else {
      }
    }

    // Toggle vers état "filled" si au moins un champ a une valeur
    this.updateBikeCardStateDynamic();
  }

  /**
   * Récupère la valeur d'un champ par son name
   * @param {string} fieldName - name du champ
   * @returns {string} valeur du champ
   */
  getFormFieldValueByName(fieldName) {
    // Mapping de correction pour les noms de champs incorrects
    const fieldNameMapping = {
      brand: 'marque',
      model: 'modele',
      year: 'annee',
      type: 'energie',
      typevelo: 'typevelo', // Celui-ci fonctionne déjà
    };

    // Utiliser le vrai nom du champ ou le nom original si pas de mapping
    const realFieldName = fieldNameMapping[fieldName] || fieldName;

    // Input text/select standard
    const input = this.querySelector(`[name="${realFieldName}"]`);

    if (input) {
      if (input.type === 'radio') {
        const checked = this.querySelector(`[name="${realFieldName}"]:checked`);

        if (checked?.value === 'other') {
          // Cas spécial années "autre"
          const otherSelect = this.querySelector('.syb-funnel__year-other-select');
          const value = otherSelect?.value || checked.value;
          return value;
        }
        const value = checked?.value || '';
        return value;
      }
      const value = input.value || '';
      return value;
    }

    return '';
  }

  /**
   * Met à jour l'affichage d'un champ dans la bike-card courante (version dynamique)
   * @param {string} cssSelector - sélecteur CSS
   * @param {string} value - valeur à afficher
   */
  updateBikeCardFieldDynamic(cssSelector, value) {
    if (!cssSelector || cssSelector.trim() === '') {
      return;
    }

    const currentBikeCard = this.getBikeCard(this.currentBikeIndex);
    if (!currentBikeCard) return;

    const element = currentBikeCard.querySelector(cssSelector);

    if (element) {
      element.textContent = value;
      element.style.display = value && value.trim() ? 'flex' : 'none';

      // S'assurer que le parent filled est visible si on a des données
      const filledContainer = element.closest('[data-state="filled"]');
      if (filledContainer && value && value.trim()) {
        filledContainer.style.display = 'flex';
      }
    } else {
      // Fallback: essayer de trouver l'élément par data-field
      const fallbackSelector = cssSelector.replace('.syb-funnel__bike-', '');
      const fallbackElement = currentBikeCard.querySelector(`[data-field="${fallbackSelector}"]`);

      if (fallbackElement) {
        fallbackElement.textContent = value;
        fallbackElement.style.display = value && value.trim() ? 'flex' : 'none';
      }
    }
  }

  /**
   * Met à jour l'état de la bike-card (placeholder vs filled) - version dynamique
   */
  updateBikeCardStateDynamic() {
    const hasAnyValue = Object.values(this.bikeData || {}).some((value) => value && value.trim());

    const bikeCards = this.querySelectorAll('.syb-funnel__bike-card');

    for (const bikeCard of bikeCards) {
      const placeholder = bikeCard.querySelector('[data-state="placeholder"]');
      const filled = bikeCard.querySelector('[data-state="filled"]');

      if (hasAnyValue) {
        if (placeholder) placeholder.style.display = 'none';
        if (filled) filled.style.display = 'flex';
      } else {
        if (placeholder) placeholder.style.display = 'flex';
        if (filled) filled.style.display = 'none';
      }
    }
  }

  /**
   * Configure les event listeners pour une étape dynamiquement
   * @param {string} stepName - nom de l'étape
   */
  setupStepEventListeners(stepName) {
    const fieldsConfig = this.getStepFieldsConfig(stepName);

    if (!fieldsConfig.length) return;

    for (const { fieldName } of fieldsConfig) {
      // Event listeners sur tous les inputs de cette étape
      const inputs = this.querySelectorAll(`[name="${fieldName}"]`);
      inputs.forEach((input) => {
        if (!input.dataset.dynamicListenerAttached) {
          ['change', 'input'].forEach((eventType) => {
            input.addEventListener(eventType, () => {
              this.saveBikeDataDynamically(stepName);
            });
          });
          input.dataset.dynamicListenerAttached = 'true';
        }
      });

      // Cas spécial année "autre"
      if (fieldName.includes('annee') || fieldName.includes('year')) {
        const otherSelect = this.querySelector('.syb-funnel__year-other-select');
        if (otherSelect && !otherSelect.dataset.dynamicListenerAttached) {
          otherSelect.addEventListener('change', () => {
            this.saveBikeDataDynamically(stepName);
          });
          otherSelect.dataset.dynamicListenerAttached = 'true';
        }
      }
    }
  }

  /**
   * Met à jour un champ spécifique de la bike card en temps réel avec validation
   * @param {string} fieldName - Nom du champ
   * @param {string} value - Valeur du champ
   */
  updateBikeCardField(fieldName, value) {
    try {
      // Validation du mapping avant mise à jour
      if (!this.isValidBikeField(fieldName)) {
        return;
      }

      // Validation et nettoyage de la valeur
      const cleanValue = this.validateAndCleanFieldValue(value, fieldName);

      // Mettre à jour le vélo actuel dans le store
      const currentBike = this.getCurrentBike() || this.createEmptyBike();
      currentBike[fieldName] = cleanValue;

      // S'assurer que le vélo existe dans le tableau
      if (this.currentBikeIndex >= this.bikes.length) {
        this.bikes.push(currentBike);
      } else {
        this.bikes[this.currentBikeIndex] = currentBike;
      }

      // Mettre à jour l'affichage de la bike card
      this.updateBikeField(fieldName, cleanValue);

      // Basculer vers l'état filled si on a des données
      this.toggleBikeCardState();

      // Déclencher événement de mise à jour
      this.dispatchBikeFieldUpdate(fieldName, cleanValue);
    } catch (error) {
      // Fallback: continuer avec la méthode basique
      this.updateBikeFieldBasic(fieldName, value);
    }
  }

  /**
   * Valide qu'un champ bike est configuré
   * @param {string} fieldName - Nom du champ
   * @returns {boolean} True si le champ est valide
   */
  isValidBikeField(fieldName) {
    if (!fieldName || typeof fieldName !== 'string') {
      return false;
    }

    // Vérifier si le champ est dans la configuration
    if (this.availableBikeFields && this.availableBikeFields.length > 0) {
      return this.availableBikeFields.includes(fieldName);
    }

    // Fallback: accepter les champs standards connus
    const standardFields = [
      'marque',
      'modele',
      'annee',
      'energie',
      'type',
      'bike_size',
      'serial_number',
      'additional_details',
    ];
    return standardFields.includes(fieldName);
  }

  /**
   * Valide et nettoie la valeur d'un champ
   * @param {any} value - Valeur à valider
   * @param {string} fieldName - Nom du champ
   * @returns {string} Valeur nettoyée
   */
  validateAndCleanFieldValue(value, fieldName) {
    if (value === null || value === undefined) {
      return '';
    }

    let cleanValue = String(value).trim();

    // Validations spécifiques selon le type de champ
    switch (fieldName) {
      case 'annee':
      case 'year':
        // Validation année (doit être un nombre entre 1900 et année actuelle + 1)
        if (cleanValue && !isNaN(cleanValue)) {
          const yearNum = parseInt(cleanValue, 10);
          const currentYear = new Date().getFullYear();
          if (yearNum < 1900 || yearNum > currentYear + 1) {
            return cleanValue; // Garder la valeur mais log l'avertissement
          }
        }
        break;
      case 'bike_size':
        // Limiter la longueur des tailles
        if (cleanValue.length > 10) {
          cleanValue = cleanValue.substring(0, 10);
        }
        break;
      case 'serial_number':
        // Nettoyer les numéros de série (retirer caractères spéciaux)
        cleanValue = cleanValue.replace(/[^\w\-]/g, '');
        break;
    }

    return cleanValue;
  }

  /**
   * Méthode de fallback pour mise à jour basique
   * @param {string} fieldName - Nom du champ
   * @param {string} value - Valeur du champ
   */
  updateBikeFieldBasic(fieldName, value) {
    const currentBike = this.getCurrentBike() || this.createEmptyBike();
    currentBike[fieldName] = value;

    if (this.currentBikeIndex >= this.bikes.length) {
      this.bikes.push(currentBike);
    } else {
      this.bikes[this.currentBikeIndex] = currentBike;
    }

    this.updateBikeField(fieldName, value);
    this.toggleBikeCardState();
  }

  /**
   * Déclenche un événement de mise à jour de champ
   * @param {string} fieldName - Nom du champ
   * @param {string} value - Valeur du champ
   */
  dispatchBikeFieldUpdate(fieldName, value) {
    const event = new CustomEvent('bike:field:update', {
      detail: {
        fieldName,
        value,
        bikeIndex: this.currentBikeIndex,
      },
    });
    this.dispatchEvent(event);
  }

  /**
   * Met à jour le mode de saisie de la taille du vélo selon le type
   * @param {string} bikeType - Type de vélo sélectionné
   */
  updateBikeSizePlaceholder(bikeType) {
    const bikeSizeContainer = this.querySelector('.syb-funnel__field--bike-size');
    const bikeSizeSelect = this.querySelector('.syb-funnel__bike-size-select');
    const bikeSizeInput = this.querySelector('[data-autocomplete="bike-size"]');
    const bikeSizeNumericWrapper = this.querySelector('.syb-funnel__bike-size-numeric');
    const bikeSizeHiddenInput = this.querySelector('[data-hidden-input="bike-size"]');

    if (!bikeSizeContainer || !bikeSizeSelect || !bikeSizeInput || !bikeSizeNumericWrapper) return;

    // Si pas de type fourni, essayer de le récupérer du vélo actuel
    if (!bikeType) {
      const currentBike = this.getCurrentBike();
      bikeType = currentBike && currentBike.type ? currentBike.type : '';
    }

    // Déterminer le mode à utiliser
    const bikeTypeLower = bikeType ? bikeType.toLowerCase() : '';
    const useLettersMode = ['vtt', 'urbain', 'vtc', 'urbain / vtc'].includes(bikeTypeLower);

    if (useLettersMode) {
      // Mode lettres : afficher le select, cacher l'input numérique
      bikeSizeContainer.dataset.sizeMode = 'letters';
      bikeSizeSelect.style.display = 'block';
      bikeSizeNumericWrapper.style.display = 'none';

      // Transférer la valeur du hidden input vers le select si applicable
      if (bikeSizeHiddenInput?.value && !bikeSizeSelect.value) {
        const inputValue = bikeSizeHiddenInput.value.toUpperCase();
        if (['S', 'M', 'L', 'XL'].includes(inputValue)) {
          bikeSizeSelect.value = inputValue;
        }
      }

      // Vider les champs numériques
      bikeSizeInput.value = '';
      if (bikeSizeHiddenInput) bikeSizeHiddenInput.value = '';
    } else {
      // Mode numérique : afficher l'input, cacher le select
      bikeSizeContainer.dataset.sizeMode = 'numeric';
      bikeSizeSelect.style.display = 'none';
      bikeSizeNumericWrapper.style.display = 'block';

      // Vider le select
      bikeSizeSelect.value = '';
    }
  }

  /**
   * Valide les champs de l'étape détails
   * @returns {boolean} True si valide
   */
  validateStepDetails() {
    const bikeSizeContainer = this.querySelector('.syb-funnel__field--bike-size');
    if (!bikeSizeContainer) return true;

    const sizeMode = bikeSizeContainer.getAttribute('data-size-mode');
    let activeBikeSizeInput, hiddenInput, visibleInput;

    if (sizeMode === 'letters') {
      activeBikeSizeInput = bikeSizeContainer.querySelector('[data-mode="letters"]');
    } else {
      // Mode numérique avec autocomplete
      hiddenInput = bikeSizeContainer.querySelector('[data-hidden-input="bike-size"]');
      visibleInput = bikeSizeContainer.querySelector('[data-autocomplete="bike-size"]');
      activeBikeSizeInput = hiddenInput || bikeSizeContainer.querySelector('[data-mode="numeric"]');
    }

    if (!activeBikeSizeInput) return true;

    let isValid = true;

    // Validation spéciale pour l'autocomplete bike-size
    if (sizeMode === 'numeric' && hiddenInput && visibleInput) {
      if (!hiddenInput.value || hiddenInput.value.trim() === '') {
        this.showFieldError(visibleInput, 'bike_size_required');
        isValid = false;
      } else {
        const sizeValue = parseInt(hiddenInput.value, 10);
        if (isNaN(sizeValue) || sizeValue < 47 || sizeValue > 62) {
          this.showFieldError(visibleInput, 'bike_size_range');
          isValid = false;
        } else {
          this.clearFieldError(visibleInput);
        }
      }
    } else {
      // Validation classique pour les autres cas
      if (!activeBikeSizeInput.value.trim()) {
        this.showFieldError(activeBikeSizeInput, 'bike_size_required');
        isValid = false;
      } else {
        // Validation spécifique au mode numeric (ancienne structure)
        if (sizeMode === 'numeric') {
          const sizeValue = parseInt(activeBikeSizeInput.value, 10);
          // Validation stricte : doit être un entier entre 47 et 62 inclus
          if (isNaN(sizeValue) || sizeValue < 47 || sizeValue > 62) {
            this.showFieldError(activeBikeSizeInput, 'bike_size_range');
            isValid = false;
          } else {
            this.clearFieldError(activeBikeSizeInput);
          }
        } else {
          // Mode letters - validation simple de présence
          this.clearFieldError(activeBikeSizeInput);
        }
      }
    }

    // Validation du champ kms si l'énergie est électrique
    const currentBike = this.getCurrentBike();
    if (currentBike && currentBike.energie === 'electrique') {
      const kmsInput = this.querySelector('.syb-funnel__field--kms input');
      if (kmsInput) {
        const kmsValue = kmsInput.value.trim();
        if (!kmsValue || isNaN(kmsValue) || Number(kmsValue) < 0) {
          this.showError(kmsInput, 'Veuillez entrer un kilométrage valide');
          isValid = false;
        } else {
          this.clearError(kmsInput);
        }
      }
    }

    return isValid;
  }

  /**
   * Crée un objet vélo vide basé sur la configuration dynamique
   * @returns {BikeData}
   */
  createEmptyBike() {
    const baseBike = {
      id: 'bike-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
      marque: '',
      modele: '',
      annee: '',
      energie: '',
      type: '',
      taille: '',
      numeroSerie: '',
      details: '',
      photos: {
        main: null,
        additional: [],
      },
      priceEstimate: null,
    };

    if (this.availableBikeFields && this.availableBikeFields.length > 0) {
      for (const fieldName of this.availableBikeFields) {
        if (!baseBike.hasOwnProperty(fieldName)) {
          baseBike[fieldName] = '';
        }
      }
    }

    return baseBike;
  }

  /**
   * Bascule entre l'état placeholder et l'état filled - VERSION OPTIMISÉE
   */
  toggleBikeCardState() {
    try {
      const currentBike = this.getCurrentBike();
      const hasData = this.checkBikeHasData(currentBike);

      const states = this.getBikeCardStates(this.currentBikeIndex);
      if (!states.placeholder || !states.filled) {
        return;
      }

      this.updateBikeCardDisplay(hasData, states);
      this.dispatchBikeStateChange(hasData);
    } catch (error) {
      this.fallbackBikeCardState();
    }
  }
  /**
   * Configure la validation en temps réel pour le champ bike_size
   */
  setupBikeSizeValidation() {
    const bikeSizeContainer = this.querySelector('.syb-funnel__field--bike-size');
    if (!bikeSizeContainer) return;

    const bikeSizeSelect = bikeSizeContainer.querySelector('[data-mode="letters"]');
    const bikeSizeInput = bikeSizeContainer.querySelector('[data-mode="numeric"]');

    // Validation en temps réel pour l'input numérique
    if (bikeSizeInput) {
      bikeSizeInput.addEventListener('input', (e) => {
        const value = parseInt(e.target.value, 10);
        if (e.target.value && (isNaN(value) || value < 47 || value > 62)) {
          this.showFieldError(e.target, 'bike_size_range');
        } else {
          this.clearFieldError(e.target);
        }
      });

      bikeSizeInput.addEventListener('blur', (e) => {
        const value = parseInt(e.target.value, 10);
        if (e.target.value && (isNaN(value) || value < 47 || value > 62)) {
          this.showFieldError(e.target, 'bike_size_range');
        }
      });
    }

    // Clear errors sur sélection select
    if (bikeSizeSelect) {
      bikeSizeSelect.addEventListener('change', (e) => {
        if (e.target.value) {
          this.clearError(e.target);
        }
      });
    }
  }

  /**
   * Vérifie si le vélo actuel a des données - VERSION CACHÉE ET OPTIMISÉE
   * @param {BikeData|null} currentBike - Vélo actuel
   * @returns {boolean} True si des données sont présentes
   */
  checkBikeHasData(currentBike) {
    if (!currentBike) {
      return false;
    }

    // Cache pour éviter les recalculs
    if (!this._cachedBikeFields) {
      this._cachedBikeFields = this.getAllBikeFields();
    }

    const availableFields = this._cachedBikeFields;

    if (availableFields.length > 0) {
      // Utiliser les champs configurés dynamiquement avec optimisation
      return this.checkDynamicFields(currentBike, availableFields);
    } else {
      // Fallback vers les champs hardcodés pour compatibilité
      return this.checkFallbackFields(currentBike);
    }
  }

  /**
   * Vérifie les champs configurés dynamiquement
   * @param {BikeData} currentBike - Vélo actuel
   * @param {string[]} fields - Champs à vérifier
   * @returns {boolean} True si au moins un champ a une valeur
   */
  checkDynamicFields(currentBike, fields) {
    for (const field of fields) {
      const value = currentBike[field];
      if (value && typeof value === 'string' && value.trim() !== '') {
        return true; // Early return pour optimiser
      }
    }
    return false;
  }

  /**
   * Vérifie les champs hardcodés en fallback
   * @param {BikeData} currentBike - Vélo actuel
   * @returns {boolean} True si au moins un champ a une valeur
   */
  checkFallbackFields(currentBike) {
    const fallbackFields = ['marque', 'modele', 'annee', 'energie', 'type'];
    return fallbackFields.some((field) => {
      const value = currentBike[field];
      return value && String(value).trim() !== '';
    });
  }

  /**
   * Créer une nouvelle bike card dynamiquement
   * @param {number} bikeIndex - Index du vélo
   * @returns {HTMLElement} L'élément bike card créé
   */
  createBikeCard(bikeIndex) {
    const template = this.querySelector('[data-bike-card]');
    if (!template) {
      return null;
    }

    // Récupérer le HTML du template
    let templateHTML = template.innerHTML;

    // Calculer le numéro et l'ordinal
    const bikeNumber = bikeIndex + 1;
    const ordinal = bikeNumber === 1 ? 'premier' : `${bikeNumber}<sup>e</sup>`;

    // Remplacer les données dynamiques dans le template
    templateHTML = templateHTML
      .replace(/data-bike-index="[^"]*"/g, `data-bike-index="${bikeIndex}"`)
      .replace(/premier/g, ordinal)
      .replace(/data-bike-number>\s*\d*\s*</g, `data-bike-number>${bikeNumber}<`);

    // Créer l'élément DOM depuis le HTML modifié
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = templateHTML;
    const bikeCard = tempDiv.firstElementChild;

    return bikeCard;
  }

  /**
   * Récupère une bike card spécifique par index
   * @param {number} bikeIndex - Index du vélo
   * @returns {HTMLElement|null} L'élément bike card ou null
   */
  getBikeCard(bikeIndex) {
    return this.querySelector(`[data-bike-index="${bikeIndex}"]`);
  }

  /**
   * Récupère les éléments d'état de la bike card pour un index spécifique
   * @param {number} bikeIndex - Index du vélo (par défaut: currentBikeIndex)
   * @returns {Object} États placeholder et filled
   */
  getBikeCardStates(bikeIndex = this.currentBikeIndex) {
    const bikeCard = this.getBikeCard(bikeIndex);
    if (!bikeCard) {
      return { placeholder: null, filled: null };
    }

    return {
      placeholder: bikeCard.querySelector('[data-state="placeholder"]'),
      filled: bikeCard.querySelector('[data-state="filled"]'),
    };
  }

  /**
   * Affiche toutes les bike cards existantes
   */
  renderAllBikeCards() {
    const bikeCardsContainer = this.querySelector('.syb-funnel__bike-cards');
    if (!bikeCardsContainer) return;

    // Vider le conteneur
    bikeCardsContainer.innerHTML = '';

    // Créer une card pour chaque vélo
    for (let i = 0; i < this.bikes.length; i++) {
      const bikeCard = this.createBikeCard(i);
      bikeCardsContainer.appendChild(bikeCard);

      // Mettre à jour l'état de la card selon les données du vélo
      const bike = this.bikes[i];
      const hasData = this.checkBikeHasData(bike);
      const states = this.getBikeCardStates(i);

      if (states.placeholder && states.filled) {
        this.updateBikeCardDisplay(hasData, states);
        this.updateBikeCardFieldsForIndex(i, bike);
      }
    }
  }

  /**
   * Met à jour les champs d'une bike card spécifique
   * @param {number} bikeIndex - Index du vélo
   * @param {Object} bikeData - Données du vélo
   */
  updateBikeCardFieldsForIndex(bikeIndex, bikeData) {
    if (!bikeData) return;

    // Mettre à jour dans toutes les bike-cards pour cet index (desktop + mobile)
    const allBikeCards = this.querySelectorAll(`[data-bike-index="${bikeIndex}"]`);

    allBikeCards.forEach((card) => {
      // Priorité aux valeurs de base, fallback sur les versions alternatives
      const brandValue = bikeData.marque || bikeData.bike_brand || bikeData['.syb-funnel__bike-brand'];
      const modelValue = bikeData.modele || bikeData.bike_model || bikeData['.syb-funnel__bike-model'];
      const yearValue = bikeData.annee || bikeData.bike_year || bikeData['.syb-funnel__bike-year'];
      const energyValue = bikeData.energie || bikeData.bike_energy || bikeData['.syb-funnel__bike-energy'];
      const typeValue = bikeData.type || bikeData.typevelo || bikeData.bike_type || bikeData['.syb-funnel__bike-type'];

      // Mapping des valeurs et sélecteurs CSS (adapté à la structure HTML réelle)
      const fieldMappings = [
        { value: brandValue, selector: '.syb-funnel__bike-brand' },
        { value: modelValue, selector: '.syb-funnel__bike-model' },
        { value: yearValue, selector: '.syb-funnel__bike-year' },
        { value: energyValue, selector: '.syb-funnel__bike-type' },
        { value: typeValue, selector: '.syb-funnel__bike-energy' },
      ];

      // Mettre à jour les champs texte
      fieldMappings.forEach(({ value, selector }) => {
        if (value) {
          const fieldElement = card.querySelector(selector);

          if (fieldElement) {
            fieldElement.textContent = value;
            fieldElement.style.display = 'block';
          }
        }
      });

      // Mettre à jour les photos - utiliser uniquement l'array photos (pas main_photo)
      if (bikeData.photos && bikeData.photos.length > 0) {
        const photoUrl = bikeData.photos[0];
        const imageElement = card.querySelector('.syb-funnel__bike-image img') || card.querySelector('img');

        if (imageElement && photoUrl) {
          imageElement.src = photoUrl;
          imageElement.style.display = 'block';

          // Cacher l'icône placeholder
          const placeholderIcon =
            card.querySelector('.syb-funnel__bike-image-placeholder') ||
            card.querySelector('[data-field="placeholder-icon"]');
          if (placeholderIcon) {
            placeholderIcon.style.display = 'none';
          }
        }
      }
    });
  }

  /**
   * Met à jour l'affichage de la bike card
   * @param {boolean} hasData - Si le vélo a des données
   * @param {Object} states - États de la bike card
   */
  updateBikeCardDisplay(hasData, states) {
    if (hasData) {
      states.placeholder.style.display = 'none';
      states.filled.style.display = '';
    } else {
      states.placeholder.style.display = '';
      states.filled.style.display = 'none';
    }
  }

  /**
   * Pré-remplit une bike-card avec les données du vélo sauvegardé
   * @param {number} bikeIndex - Index du vélo
   * @param {Object} bikeData - Données du vélo
   */
  populateBikeCardWithData(bikeIndex, bikeData) {
    if (!bikeData) return;

    // Vérifier si le vélo a des données significatives
    const hasData = this.checkBikeHasData(bikeData);
    if (!hasData) return;

    // Obtenir toutes les bike-cards pour cet index (desktop + mobile)
    const allBikeCards = this.querySelectorAll(`[data-bike-index="${bikeIndex}"]`);

    allBikeCards.forEach((bikeCard) => {
      // Mettre à jour les champs avec les données
      this.updateBikeCardFieldsForIndex(bikeIndex, bikeData);

      // Changer l'état d'affichage vers filled
      const states = {
        placeholder: bikeCard.querySelector('[data-state="placeholder"]'),
        filled: bikeCard.querySelector('[data-state="filled"]'),
      };

      if (states.placeholder && states.filled) {
        this.updateBikeCardDisplay(true, states);
      }
    });
  }

  /**
   * Met à jour les numéros de vélo dans tous les éléments
   */
  updateBikeNumbers() {
    // Mettre à jour les numéros dans les bike cards individuelles (EXCLURE recap strictement)
    const allBikeCards = this.querySelectorAll(
      '.syb-funnel__bike-cards .syb-funnel__bike-card[data-bike-index]:not([data-bikes-cards] .syb-funnel__bike-card), .syb-funnel__drawer-bikes .syb-funnel__bike-card[data-bike-index]:not([data-bikes-cards-mobile] .syb-funnel__bike-card)',
    );

    allBikeCards.forEach((bikeCard, cardIndex) => {
      const bikeIndex = parseInt(bikeCard.dataset.bikeIndex);
      const bikeNumber = bikeIndex + 1;

      const bikeNumberElements = bikeCard.querySelectorAll('[data-bike-number]');
      bikeNumberElements.forEach((element) => {
        const hasCounterParent = element.closest('.syb-funnel__bike-counter');

        if (hasCounterParent) {
          element.textContent = bikeNumber;
        }
      });
    });

    // Mettre à jour les éléments globaux (titres d'étapes, etc.)
    const globalBikeNumberElements = this.querySelectorAll('[data-bike-number]');
    const currentBikeNumber = this.currentBikeIndex + 1;

    globalBikeNumberElements.forEach((element) => {
      // Skip les éléments déjà traités dans les bike cards
      if (element.closest('.syb-funnel__bike-card[data-bike-index]')) return;

      const hasStepTitleParent = element.closest('.syb-funnel__step-title');

      if (hasStepTitleParent && currentBikeNumber === 1) {
        element.textContent = '';
      } else if (hasStepTitleParent && currentBikeNumber > 1) {
        element.innerHTML = `${currentBikeNumber}<sup>e</sup>`;
      } else {
        element.textContent = currentBikeNumber;
      }
    });

    // Mettre à jour aussi les textes des bike cards
    this.updateBikeCardTexts();
  }

  /**
   * Met à jour les textes des bike cards pour utiliser le bon format numéral
   */
  updateBikeCardTexts() {
    // Mettre à jour les textes dans la sidebar (mobile et desktop)
    const bikeTexts = this.querySelectorAll('.syb-funnel__bike-text');

    bikeTexts.forEach((textElement, index) => {
      const bikeNum = index + 1;
      let ordinal;

      if (bikeNum === 1) {
        ordinal = 'premier';
      } else {
        ordinal = `${bikeNum}<sup>e</sup>`;
      }

      // Remplacer le texte avec le bon ordinal
      const originalText = textElement.textContent || "Ajoutez votre premier vélo pour obtenir l'estimation complète";
      const newText = originalText.replace(/premier/g, ordinal);
      textElement.innerHTML = newText;
    });
  }

  /**
   * Déclenche un événement de changement d'état
   * @param {boolean} hasData - Si le vélo a des données
   */
  dispatchBikeStateChange(hasData) {
    const event = new CustomEvent('bike:state:change', {
      detail: {
        hasData,
        bikeIndex: this.currentBikeIndex,
        timestamp: Date.now(),
      },
    });
    this.dispatchEvent(event);
  }

  /**
   * Méthode de fallback en cas d'erreur
   */
  fallbackBikeCardState() {
    const placeholderState = this.querySelector('[data-state="placeholder"]');
    const filledState = this.querySelector('[data-state="filled"]');

    if (placeholderState && filledState) {
      // Afficher placeholder par défaut
      placeholderState.style.display = '';
      filledState.style.display = 'none';
    }
  }

  // ==========================================================================
  // STEP RECAP LOGIC
  // ==========================================================================

  /**
   * Initialise la step_recap avec injection des données
   */
  setupStepRecap() {
    this.injectUserDataInRecap();
    this.renderBikesListInRecap();
    this.renderBikesCardsInRecap();
    this.calculateAndDisplayTotal();
    this.setupRecapEventListeners();
  }

  /**
   * Injecte les données utilisateur dans le récapitulatif
   */
  injectUserDataInRecap() {
    const userData = this.getUserData();

    // Desktop
    const userNameEl = this.querySelector('[data-user-name]');
    const userEmailEl = this.querySelector('[data-user-email]');
    const userPhoneEl = this.querySelector('[data-user-phone]');

    // Mobile
    const userNameMobileEl = this.querySelector('[data-user-name-mobile]');
    const userEmailMobileEl = this.querySelector('[data-user-email-mobile]');
    const userPhoneMobileEl = this.querySelector('[data-user-phone-mobile]');

    if (userData.firstname && userData.lastname) {
      const fullName = `${userData.firstname} ${userData.lastname}`;
      if (userNameEl) userNameEl.textContent = fullName;
      if (userNameMobileEl) userNameMobileEl.textContent = fullName;
    }

    if (userData.email) {
      if (userEmailEl) userEmailEl.textContent = userData.email;
      if (userEmailMobileEl) userEmailMobileEl.textContent = userData.email;
    }

    if (userData.phone) {
      if (userPhoneEl) userPhoneEl.textContent = userData.phone;
      if (userPhoneMobileEl) userPhoneMobileEl.textContent = userData.phone;
    }
  }

  /**
   * Récupère les données utilisateur depuis le store
   * @returns {Object} Données utilisateur
   */
  getUserData() {
    const userData = {};

    // Récupérer depuis les inputs step_coordinates
    const firstnameInput = this.querySelector('[data-field="firstname"]');
    const lastnameInput = this.querySelector('[data-field="lastname"]');
    const emailInput = this.querySelector('[data-field="email"]');
    const phoneInput = this.querySelector('[data-field="phone"]');

    if (firstnameInput) userData.firstname = firstnameInput.value;
    if (lastnameInput) userData.lastname = lastnameInput.value;
    if (emailInput) userData.email = emailInput.value;
    if (phoneInput) userData.phone = phoneInput.value;

    return userData;
  }

  /**
   * Préremplit les données utilisateur dans step_coordinates
   */
  prefillUserData() {
    // D'abord récupérer les données existantes depuis les champs si elles existent
    const firstnameInput = this.querySelector('[data-field="firstname"]');
    const lastnameInput = this.querySelector('[data-field="lastname"]');
    const emailInput = this.querySelector('[data-field="email"]');
    const phoneInput = this.querySelector('[data-field="phone"]');

    // Sauvegarder d'abord les données actuelles si elles existent dans les champs
    if (firstnameInput && firstnameInput.value) {
      this.formData.firstname = firstnameInput.value;
    }
    if (lastnameInput && lastnameInput.value) {
      this.formData.lastname = lastnameInput.value;
    }
    if (emailInput && emailInput.value) {
      this.formData.email = emailInput.value;
    }
    if (phoneInput && phoneInput.value) {
      this.formData.phone = phoneInput.value;
    }

    // Ensuite préfiller depuis formData
    if (!this.formData) return;

    if (firstnameInput && this.formData.firstname) {
      firstnameInput.value = this.formData.firstname;
    }
    if (lastnameInput && this.formData.lastname) {
      lastnameInput.value = this.formData.lastname;
    }
    if (emailInput && this.formData.email) {
      emailInput.value = this.formData.email;
    }
    if (phoneInput && this.formData.phone) {
      phoneInput.value = this.formData.phone;
    }
  }

  /**
   * Génère la liste des prix des vélos
   */
  renderBikesListInRecap() {
    const bikesListEl = this.querySelector('[data-bikes-prices]');
    const bikesListMobileEl = this.querySelector('[data-bikes-prices-mobile]');

    if (!bikesListEl && !bikesListMobileEl) return;

    const bikesHtml = this.bikes
      .map((bike, index) => {
        const bikeName = this.getBikeDisplayName(bike);
        const bikePrice = this.calculateBikePrice(bike);

        return `
        <div class="syb-funnel__bike-price" data-bike-index="${index}">
          <span class="syb-funnel__bike-name">${bikeName}</span>
          <span class="syb-funnel__bike-amount">${bikePrice}</span>
        </div>
      `;
      })
      .join('');

    if (bikesListEl) bikesListEl.innerHTML = bikesHtml;
    if (bikesListMobileEl) bikesListMobileEl.innerHTML = bikesHtml;
  }

  /**
   * Génère les cartes détaillées des vélos
   */
  renderBikesCardsInRecap() {
    const bikesCardsEl = this.querySelector('[data-bikes-cards]');
    const bikesCardsMobileEl = this.querySelector('[data-bikes-cards-mobile]');

    if (!bikesCardsEl && !bikesCardsMobileEl) return;
    if (!this.bikes || this.bikes.length === 0) return;

    // Chercher le template récap en priorité, sinon fallback sur template existant
    let template =
      this.querySelector(`template[id^="bike-card-recap-template"]`) ||
      this.querySelector('template[id*="bike-card-recap-template"]');

    // Fallback : utiliser le template existant
    if (!template) {
      template = this.querySelector('template[id*="bike-card-template"]');
    }

    if (!template) return;

    const bikesCardsHtml = this.bikes
      .map((bike, originalIndex) => {
        const bikePrice = this.calculateBikePrice(bike);
        const bikeNumber = originalIndex + 1;

        const clone = template.content.cloneNode(true);

        // Chercher d'abord la card récap, sinon utiliser la card normale
        let card = clone.querySelector('.syb-recap__bike-card');
        let isRecapTemplate = !!card;

        if (!card) {
          card = clone.querySelector('.syb-funnel__bike-card');
        }

        if (!card) return null;

        const brand = bike.brand || bike.marque || '';
        const model = bike.model || bike.modele || '';

        card.setAttribute('data-bike-index', originalIndex);

        // Adapter les sélecteurs selon le type de template
        const brandSelector = isRecapTemplate ? '[data-bike-brand]' : '.syb-funnel__bike-brand, [data-bike-brand]';
        const modelSelector = isRecapTemplate ? '[data-bike-model]' : '.syb-funnel__bike-model, [data-bike-model]';
        const tagSelector = isRecapTemplate ? '[data-bike-tag]' : '.syb-funnel__bike-tag-text, [data-bike-tag]';

        const brandEl = card.querySelector(brandSelector);
        const modelEl = card.querySelector(modelSelector);
        const tagEl = card.querySelector(tagSelector);

        if (brandEl) brandEl.textContent = brand;
        if (modelEl) modelEl.textContent = model;
        if (tagEl) tagEl.textContent = `VÉLO ${bikeNumber}`;

        const photosContainer = card.querySelector('[data-bike-photos]');
        if (photosContainer) {
          photosContainer.innerHTML = this.generateRecapBikePhotosHtml(bike, originalIndex);
        }

        const editBtn = card.querySelector('[data-action="edit-bike"]');
        const deleteBtn = card.querySelector('[data-action="delete-bike"]');
        if (editBtn) editBtn.setAttribute('data-bike-index', originalIndex);
        if (deleteBtn) deleteBtn.setAttribute('data-bike-index', originalIndex);

        return card.outerHTML;
      })
      .filter((html) => html !== null)
      .join('');

    if (bikesCardsEl) bikesCardsEl.innerHTML = bikesCardsHtml;
    if (bikesCardsMobileEl) bikesCardsMobileEl.innerHTML = bikesCardsHtml;
  }

  /**
   * Génère le HTML des photos pour le récap avec design Figma
   * @param {Object} bike - Données du vélo
   * @param {number} index - Index du vélo
   * @returns {string} HTML des photos en style Figma
   */
  generateRecapBikePhotosHtml(bike, index) {
    const photos = this.getBikePhotos(bike);
    const maxPhotos = 5;

    if (!photos || photos.length === 0) {
      return '<div class="syb-recap__bike-images-empty">Aucune photo</div>';
    }

    let photosHtml = '';

    for (let i = 0; i < Math.min(photos.length, maxPhotos); i++) {
      let photoUrl;

      if (photos[i] instanceof File) {
        photoUrl = URL.createObjectURL(photos[i]);
      } else if (typeof photos[i] === 'string' && !photos[i].includes('fakepath')) {
        photoUrl = photos[i];
      } else {
        continue; // Skip cette photo si elle n'est pas valide
      }

      const zIndex = maxPhotos - i;
      const isLast = i === Math.min(photos.length, maxPhotos) - 1;

      photosHtml += `
        <div class="syb-recap__bike-image">
          <img src="${photoUrl}" alt="Photo ${i + 1} du vélo ${index + 1}">
        </div>
      `;
    }

    // Si aucune photo valide n'a été trouvée
    if (photosHtml === '') {
      return '<div class="syb-recap__bike-images-empty">Aucune photo</div>';
    }

    return photosHtml;
  }

  /**
   * Récupère les photos d'un vélo en récupérant les vrais objets File
   * @param {Object} bike - Données du vélo
   * @returns {Array} Tableau des objets File ou URLs
   */
  getBikePhotos(bike) {
    // Source de vérité : bike.photos = fichiers TRAITÉS par le pipeline d'upload
    // (HEIC converti en JPEG + compression). On les privilégie sur les champs
    // individuels bike.main_photo / additional_photos_N qui peuvent contenir le
    // fichier BRUT (ex. HEIC non décodable par le navigateur → image cassée au recap).
    if (bike.photos && typeof bike.photos === 'object') {
      const stored = Object.values(bike.photos).filter((photo) => photo instanceof File);
      if (stored.length > 0) return stored;
    }

    // Fallback : champs individuels du vélo
    const photos = [];
    if (bike.main_photo && bike.main_photo instanceof File) {
      photos.push(bike.main_photo);
    }
    for (let i = 1; i <= 4; i++) {
      const photoKey = `additional_photos_${i}`;
      if (bike[photoKey] && bike[photoKey] instanceof File) {
        photos.push(bike[photoKey]);
      }
    }

    return photos;
  }

  /**
   * Calcule et affiche le total des vélos
   */
  calculateAndDisplayTotal() {
    const total = this.bikes.reduce((sum, bike) => {
      const price = this.calculateBikePrice(bike, true);
      return sum + price;
    }, 0);

    const formattedTotal = this.formatPrice(total);

    // Desktop
    const totalEl = this.querySelector('[data-total-value]');
    if (totalEl) totalEl.textContent = formattedTotal;

    // Mobile
    const totalMobileEl = this.querySelector('[data-total-value-mobile]');
    if (totalMobileEl) totalMobileEl.textContent = formattedTotal;

    // Footer mobile
    const totalFooterEl = this.querySelector('[data-total-value-footer]');
    if (totalFooterEl) totalFooterEl.textContent = formattedTotal;
  }

  /**
   * Calcule le prix d'un vélo (placeholder)
   * @param {Object} bike - Données du vélo
   * @param {boolean} returnNumber - Si true, retourne un nombre
   * @returns {string|number} Prix formaté ou numérique
   */
  calculateBikePrice(bike, returnNumber = false) {
    // Algorithme de pricing (placeholder)
    const basePrice = 1500;
    const yearMultiplier = bike.annee ? (2026 - parseInt(bike.annee)) * 50 : 200;
    const typeMultiplier = bike.type === 'électrique' ? 500 : 0;

    const calculatedPrice = Math.max(basePrice - yearMultiplier + typeMultiplier, 500);

    return returnNumber ? calculatedPrice : this.formatPrice(calculatedPrice);
  }

  /**
   * Formate un prix en euros
   * @param {number} price - Prix numérique
   * @returns {string} Prix formaté
   */
  formatPrice(price) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .format(price)
      .replace('€', ' €');
  }

  /**
   * Récupère le nom d'affichage du vélo
   * @param {Object} bike - Données du vélo
   * @returns {string} Nom formaté
   */
  getBikeDisplayName(bike) {
    const marque = bike.marque || 'Marque inconnue';
    const modele = bike.modele || 'Modèle inconnu';
    return `${marque} ${modele}`;
  }

  /**
   * Nettoie tous les event listeners des boutons du recap
   */
  cleanupRecapEventListeners() {
    const buttonsSelectors = ['[data-action="add-bike"]', '[data-action="edit-bike"]', '[data-action="delete-bike"]'];

    buttonsSelectors.forEach((selector) => {
      const buttons = this.querySelectorAll(selector);
      buttons.forEach((btn) => {
        const newBtn = btn.cloneNode(true);
        btn.parentNode.replaceChild(newBtn, btn);
      });
    });
  }

  /**
   * Configure les événements pour step_recap
   */
  setupRecapEventListeners() {
    // Nettoyer d'abord tous les event listeners existants
    this.cleanupRecapEventListeners();

    // Puis ajouter les nouveaux listeners
    const editBtns = this.querySelectorAll('[data-action="edit-bike"]');
    editBtns.forEach((btn) => {
      if (!btn.dataset.listenerAttached) {
        btn.addEventListener('click', (e) => {
          const bikeIndex = parseInt(e.currentTarget.dataset.bikeIndex);
          this.handleEditBike(bikeIndex);
        });
        btn.dataset.listenerAttached = 'true';
      }
    });

    const deleteBtns = this.querySelectorAll('[data-action="delete-bike"]');
    deleteBtns.forEach((btn) => {
      if (!btn.dataset.listenerAttached) {
        btn.addEventListener('click', (e) => {
          const bikeIndex = parseInt(e.currentTarget.dataset.bikeIndex);
          this.handleDeleteBike(bikeIndex);
        });
        btn.dataset.listenerAttached = 'true';
      }
    });

    // Note: Le bouton submit est géré par le gestionnaire général des clics
  }

  /**
   * Gère l'ajout d'un nouveau vélo
   */
  handleAddBike() {
    this.saveCurrentBikeData();

    // Sauvegarder les données utilisateur avant de nettoyer
    const existingUserData = this.collectUserData();

    const newBike = this.createEmptyBike();
    this.bikes.push(newBike);
    this.currentBikeIndex = this.bikes.length - 1;

    this.clearFormFields();

    // Restaurer les données utilisateur si elles existaient
    if (existingUserData) {
      this.prefillUserDataFields(existingUserData);
    }

    this.bikeData = {};
    this.formData = {};

    // Réinitialiser les titres des étapes pour le nouveau vélo
    this.resetStepTitlesForNewBike();

    // Synchroniser toutes les bike-cards d'étapes avec les vélos réels (inclut déjà la numérotation)
    this.syncStepBikeCards();

    // Mettre à jour les bike-cards du recap (système séparé)
    this.renderBikesCardsInRecap();

    // Si on est sur l'étape recap, forcer la mise à jour
    if (this.currentStep === 'recap') {
      this.setupStepRecap();
    }

    // Nettoyer les previews photos pour le nouveau vélo
    this.clearAllPhotoPreviews();

    this.switchToStep('infos');
  }

  /**
   * Préremplit les champs utilisateur avec les données fournies
   * @param {Object} userData - Données utilisateur à restaurer
   */
  prefillUserDataFields(userData) {
    const userInfoStep = this.querySelector('[data-step="coordinates"]');
    if (!userInfoStep || !userData) return;

    const fields = [
      { field: 'lastname', value: userData.name },
      { field: 'firstname', value: userData.prenom },
      { field: 'email', value: userData.email },
      { field: 'phone', value: userData.phone },
      { field: 'want_new_bike', checked: userData.buyer },
      { field: 'is_professional', checked: userData.is_professional },
    ];

    fields.forEach(({ field, value, checked }) => {
      const element = userInfoStep.querySelector(`[data-field="${field}"]`);
      if (element) {
        if (element.type === 'checkbox' || element.type === 'radio') {
          element.checked = checked;
        } else {
          element.value = value || '';
        }
      }
    });
  }

  /**
   * S'assure qu'une bike-card existe dans les conteneurs d'étapes pour l'index donné
   * @param {number} bikeIndex - Index du vélo
   */
  ensureBikeCardInSteps(bikeIndex) {
    // Ne PAS nettoyer ici pour éviter la suppression/recréation
    const mainContainer = this.querySelector('.syb-funnel__bike-cards');

    if (mainContainer) {
      const existingCards = mainContainer.querySelectorAll(`[data-bike-index="${bikeIndex}"]`);

      if (existingCards.length === 0) {
        const bike = this.bikes[bikeIndex];
        if (bike) {
          const newBikeCard = this.createBikeCard(bikeIndex);
          mainContainer.appendChild(newBikeCard);
        }
      }
    }
  }

  /**
   * Synchronise les bike-cards d'étapes avec les vélos réels (appelé une seule fois)
   */
  syncStepBikeCards() {
    const mainContainer = this.querySelector('.syb-funnel__bike-cards');
    const mobileContainer = this.querySelector('.syb-funnel__drawer-bikes');
    if (!mainContainer) return;

    // Créer UNIQUEMENT pour les vélos jusqu'à l'index courant (inclus)
    const bike = this.bikes[this.currentBikeIndex];
    if (bike) {
      const newBikeCard = this.createBikeCard(this.currentBikeIndex);
      mainContainer.appendChild(newBikeCard);

      if (mobileContainer) {
        mobileContainer.appendChild(newBikeCard.cloneNode(true));
      }

      // Pré-remplir la bike-card avec les données sauvegardées
      this.populateBikeCardWithData(this.currentBikeIndex, bike);
    }

    // Mettre à jour la numérotation immédiatement après création
    this.updateBikeNumbers();
  }

  /**
   * Sauvegarde simple des données du vélo actuel
   */
  saveCurrentBikeData() {
    if (this.currentBikeIndex < 0 || this.currentBikeIndex >= this.bikes.length) {
      return;
    }

    const currentBike = this.bikes[this.currentBikeIndex];
    if (!currentBike) {
      return;
    }

    // Collecter les données de tous les inputs/selects/textareas
    const inputs = this.querySelectorAll('input, select, textarea');
    for (const input of inputs) {
      if (input.name && input.value) {
        currentBike[input.name] = input.value;
      }
    }

    // Sauvegarder dans le tableau
    this.bikes[this.currentBikeIndex] = currentBike;
  }

  /**
   * Force une bike card spécifique en état placeholder
   * @param {number} bikeIndex - Index du vélo
   */
  forceBikeCardToPlaceholder(bikeIndex) {
    const bikeCard = this.querySelector(`[data-bike-index="${bikeIndex}"]`);
    if (bikeCard) {
      bikeCard.setAttribute('data-state', 'placeholder');
    }
  }

  /**
   * Nettoie toutes les photos uploadées
   */
  clearPhotos() {
    // Nettoyer les inputs file
    const fileInputs = this.querySelectorAll('input[type="file"]');
    for (const input of fileInputs) {
      input.value = '';
    }

    // Nettoyer les previews des photos
    const photoPreviews = this.querySelectorAll('[data-photo-preview]');
    for (const preview of photoPreviews) {
      preview.innerHTML = '';
      preview.style.display = 'none';
    }

    // Réinitialiser les conteneurs de photos
    const photoContainers = this.querySelectorAll('[data-photo-container]');
    for (const container of photoContainers) {
      container.classList.remove('syb-funnel__photo-input--filled');
    }

    // Nettoyer les objets File stockés dans le vélo actuel
    const currentBike = this.getCurrentBike();
    if (currentBike) {
      currentBike.main_photo = null;
      for (let i = 1; i <= 4; i++) {
        currentBike[`additional_photos_${i}`] = null;
      }
      if (currentBike.photos) {
        currentBike.photos = [];
      }
    }

    // Mettre à jour l'affichage des bike cards sans photos
    this.updateBikeCardPhotos([]);
  }

  /**
   * Nettoie tous les champs du formulaire (sans affecter les données des autres vélos)
   */
  clearFormFields() {
    // Nettoyer seulement les champs vélo, PAS l'étape coordinates
    const allFormSteps = this.querySelectorAll('[data-form]');

    allFormSteps.forEach((form) => {
      // Ignorer explicitement l'étape coordinates
      if (form.dataset.step === 'coordinates') {
        return;
      }

      const inputs = form.querySelectorAll('input, select, textarea');

      for (const input of inputs) {
        if (input.type === 'file') {
          // Pour les fichiers, nettoyer seulement la valeur, pas les données stockées
          input.value = '';
        } else if (input.type === 'radio' || input.type === 'checkbox') {
          input.checked = false;
        } else {
          input.value = '';
        }
      }
    });

    //Toggle classe for kms fied to be hidden
    const kmsField = this.querySelector('.syb-funnel__field--kms');
    if (kmsField) {
      kmsField.style.display = 'none';
    }

    // Nettoyer seulement les photos du formulaire, pas celles des autres vélos
    this.clearCurrentBikePhotos();

    // Réinitialiser les données temporaires du vélo courant
    this.bikeData = {};
    this.formData = {};
  }

  /**
   * Nettoie les photos du vélo courant uniquement
   */
  clearCurrentBikePhotos() {
    // Nettoyer les inputs file du formulaire
    const fileInputs = this.querySelectorAll('input[type="file"]');
    for (const input of fileInputs) {
      input.value = '';
    }

    // Nettoyer les previews des photos
    const photoPreviews = this.querySelectorAll('[data-photo-preview]');
    for (const preview of photoPreviews) {
      preview.innerHTML = '';
      preview.style.display = 'none';
    }

    // Réinitialiser les conteneurs de photos
    const photoContainers = this.querySelectorAll('[data-photo-container]');
    for (const container of photoContainers) {
      container.classList.remove('syb-funnel__photo-input--filled');
    }

    // Nettoyer les données photos du vélo courant seulement
    const currentBike = this.getCurrentBike();
    if (currentBike) {
      currentBike.main_photo = null;
      for (let i = 1; i <= 4; i++) {
        currentBike[`additional_photos_${i}`] = null;
      }
      if (currentBike.photos) {
        currentBike.photos = [];
      }
    }

    // Mettre à jour l'affichage de la bike card courante sans photos
    this.updateBikeCardPhotos([]);
  }

  /**
   * Remet à zéro toutes les previews de photos dans l'interface
   */
  resetPhotoPreviews() {
    // Nettoyer tous les previews d'images
    const photoPreviewContainers = this.querySelectorAll('.syb-funnel__photo-preview-container');
    for (const container of photoPreviewContainers) {
      container.remove();
    }

    // Remettre tous les labels en état initial
    const photoLabels = this.querySelectorAll('.syb-funnel__photo-area');
    for (const label of photoLabels) {
      label.classList.remove('syb-funnel__photo-area--filled');
    }

    // Vider tous les inputs file
    const photoInputs = this.querySelectorAll('input[type="file"][data-field*="photo"]');
    for (const input of photoInputs) {
      input.value = '';
    }
  }

  /**
   * Nettoie toutes les previews photos de l'interface
   */
  clearAllPhotoPreviews() {
    // Nettoyer tous les conteneurs de preview dans .syb-funnel__photos-layout
    const previewContainers = this.querySelectorAll('.syb-funnel__photos-layout .syb-funnel__photo-preview-container');
    for (const container of previewContainers) {
      // Cacher le conteneur
      container.style.display = 'none';

      // Nettoyer les images à l'intérieur
      const imgs = container.querySelectorAll('img');
      for (const img of imgs) {
        img.removeAttribute('src');
        img.src = '';
      }
    }

    // Nettoyer les inputs
    const photoInputs = this.querySelectorAll('input[type="file"][data-field*="photo"]');
    for (const input of photoInputs) {
      input.value = '';
    }
  }

  /**
   * Charge les photos du vélo courant dans l'interface
   */
  loadCurrentBikePhotos() {
    const currentBike = this.bikes[this.currentBikeIndex];
    if (!currentBike || !currentBike.photos) return;

    // Nettoyer toutes les previews photos avant de charger celles du vélo courant
    this.clearAllPhotoPreviews();

    // Recharger chaque photo stockée
    for (const [fieldName, photoData] of Object.entries(currentBike.photos)) {
      // Convertir le nom stocké vers le nom HTML pour trouver l'input
      const htmlFieldName = fieldName.startsWith('additional_photos_')
        ? fieldName.replace('additional_photos_', 'additional_photo_')
        : fieldName;

      const input = this.querySelector(`input[data-field="${htmlFieldName}"]`);

      if (input && photoData) {
        if (photoData instanceof File) {
          // C'est un File object - créer preview directement et assigner à l'input
          this.createPhotoPreview(photoData, input, photoData);
          this.assignFileToInput(photoData, input);
        } else if (photoData.dataUrl) {
          // C'est un objet avec dataUrl - créer un mock File
          const mockFile = this.createMockFileFromDataUrl(
            photoData.dataUrl,
            photoData.name || 'photo.jpg',
            photoData.type || 'image/jpeg',
          );
          this.createPhotoPreview(photoData.dataUrl, input, mockFile);
          this.assignFileToInput(mockFile, input);
        }
      }
    }
  }

  /**
   * Assigne un fichier à un input de manière programmatique
   * @param {File} file - Fichier à assigner
   * @param {HTMLInputElement} input - Input file
   */
  assignFileToInput(file, input) {
    try {
      // Créer un DataTransfer pour simuler l'ajout de fichier
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      input.files = dataTransfer.files;
    } catch (error) {
      // Fallback pour navigateurs plus anciens
      Object.defineProperty(input, 'files', {
        value: [file],
        writable: false,
      });
    }
  }

  /**
   * Crée un mock File object à partir d'un dataUrl
   * @param {string} dataUrl - URL de données de l'image
   * @param {string} fileName - Nom du fichier
   * @param {string} mimeType - Type MIME du fichier
   * @returns {File} Mock File object
   */
  createMockFileFromDataUrl(dataUrl, fileName, mimeType) {
    // Convertir dataUrl en Blob puis en File
    const base64Data = dataUrl.split(',')[1];
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    return new File([blob], fileName, { type: mimeType });
  }

  /**
   * Gère la modification d'un vélo existant - VERSION COMPLÈTE
   * @param {number} bikeIndex - Index du vélo à modifier
   */
  handleEditBike(bikeIndex) {
    if (bikeIndex < 0 || bikeIndex >= this.bikes.length) return;

    try {
      // Sauvegarder le vélo actuel si nécessaire
      this.saveCurrentBikeData();

      // Définir l'index du vélo en cours d'édition
      this.currentBikeIndex = bikeIndex;

      // Marquer qu'on est en mode édition
      this.isModifying = true;

      // Mettre à jour les titres des étapes avec le numéro du vélo
      this.updateStepTitlesForBikeEdit(bikeIndex);

      // Charger et préfill TOUS les formulaires à travers TOUTES les steps
      this.prefillAllFormsForBike(this.bikes[bikeIndex]);

      // Ajouter l'état visuel d'édition à la bike-card
      this.addEditingStateToBikeCard(bikeIndex);

      // Naviguer vers step_infos
      this.switchToStep('infos');
    } catch (error) {}
  }

  /**
   * Met à jour les titres des étapes avec le numéro du vélo en cours d'édition
   * @param {number} bikeIndex - Index du vélo (0-based)
   */
  updateStepTitlesForBikeEdit(bikeIndex) {
    const bikeNumber = bikeIndex + 1;
    const suffix = bikeNumber === 1 ? '<sup>er</sup>' : '<sup>e</sup>';

    // Mettre à jour tous les éléments [data-bike-number] dans les titres d'étapes
    const stepTitleElements = this.querySelectorAll('.syb-funnel__step-title [data-bike-number]');
    stepTitleElements.forEach((element) => {
      element.innerHTML = `${bikeNumber}${suffix}`;
    });
  }

  /**
   * Réinitialise les titres des étapes en vidant les numéros de vélo
   */
  resetStepTitlesForNewBike() {
    // Vider tous les éléments [data-bike-number] dans les titres d'étapes
    const stepTitleElements = this.querySelectorAll('.syb-funnel__step-title [data-bike-number]');
    stepTitleElements.forEach((element) => {
      element.innerHTML = '';
    });
  }

  /**
   * Préfill TOUS les formulaires à travers TOUTES les steps pour un vélo
   * @param {BikeData} bikeData - Données du vélo à charger
   */
  prefillAllFormsForBike(bikeData) {
    if (!bikeData) return;

    try {
      // Mettre à jour bikeData actuel
      this.bikeData = { ...bikeData };

      // Préfill step_infos : marque, modèle, année
      this.prefillStepInfos(bikeData);

      // Préfill step_type : énergie, type de vélo
      this.prefillStepType(bikeData);

      // Préfill step_details : taille, numéro série, détails
      this.prefillStepDetails(bikeData);

      // Préfill step_photos : charger les photos du vélo courant
      this.loadCurrentBikePhotos();

      // Mettre à jour formData
      this.updateFormDataFromBike(bikeData);

      // Synchroniser la bike card
      this.updateBikeCardDynamic();
    } catch (error) {}
  }

  /**
   * Préfill les champs de l'étape infos
   * @param {BikeData} bikeData - Données du vélo
   */
  prefillStepInfos(bikeData) {
    const stepFields = this.getStepFields('step_infos');

    for (const [formField, bikeField] of Object.entries(stepFields)) {
      if (bikeData[bikeField]) {
        this.prefillFormField(formField, bikeData[bikeField]);
      }
    }

    // Gestion spéciale pour marque/modèle avec dépendance
    if (bikeData.marque) {
      this.prefillFormField('marque', bikeData.marque);
    }
  }

  /**
   * Préfill les champs de l'étape type
   * @param {BikeData} bikeData - Données du vélo
   */
  prefillStepType(bikeData) {
    const stepFields = this.getStepFields('step_type');

    for (const [formField, bikeField] of Object.entries(stepFields)) {
      if (bikeData[bikeField]) {
        this.prefillFormField(formField, bikeData[bikeField]);
      }
    }
  }

  /**
   * Préfill les champs de l'étape détails
   * @param {BikeData} bikeData - Données du vélo
   */
  prefillStepDetails(bikeData) {
    // D'abord configurer le bon mode de saisie selon le type de vélo
    if (bikeData.type) {
      this.updateBikeSizePlaceholder(bikeData.type);
    }

    const stepFields = this.getStepFields('step_details');

    if (stepFields && typeof stepFields === 'object' && stepFields !== null) {
      for (const [formField, bikeField] of Object.entries(stepFields)) {
        if (bikeData[bikeField]) {
          // Traitement spécial pour bike_size après configuration du mode
          if (formField === 'bike_size') {
            this.prefillBikeSizeField(bikeData[bikeField]);
          } else {
            this.prefillFormField(formField, bikeData[bikeField]);
          }
        }
      }
    }
  }

  /**
   * Prérempli le champ bike_size selon le mode configuré
   * @param {string} value - Valeur de la taille du vélo
   */
  prefillBikeSizeField(value) {
    if (!value) return;

    const bikeSizeContainer = this.querySelector('.syb-funnel__field--bike-size');
    if (!bikeSizeContainer) return;

    const sizeMode = bikeSizeContainer.dataset.sizeMode;

    if (sizeMode === 'letters') {
      // Mode lettres : utiliser le select
      const bikeSizeSelect = this.querySelector('.syb-funnel__bike-size-select');
      if (bikeSizeSelect) {
        bikeSizeSelect.value = value.toString().toUpperCase();
        bikeSizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      // Mode numérique : utiliser l'input
      const bikeSizeInput = this.querySelector('.syb-funnel__bike-size-input');
      if (bikeSizeInput) {
        bikeSizeInput.value = value.toString();
        bikeSizeInput.dispatchEvent(new Event('input', { bubbles: true }));
        bikeSizeInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /**
   * Préfill les photos de l'étape photos
   * @param {BikeData} bikeData - Données du vélo
   */
  prefillStepPhotos(bikeData) {
    if (bikeData.photos && Array.isArray(bikeData.photos)) {
      this.prefillPhotos(bikeData.photos);
    }
  }

  /**
   * Met à jour formData depuis les données du vélo
   * @param {BikeData} bikeData - Données du vélo
   */
  updateFormDataFromBike(bikeData) {
    this.formData = { ...this.formData, ...bikeData };
  }

  /**
   * Ajoute l'état d'édition à la bike-card correspondante
   * @param {number} bikeIndex - Index du vélo en cours d'édition
   */
  addEditingStateToBikeCard(bikeIndex) {
    // Retirer l'état d'édition de toutes les bike-cards
    this.removeEditingStateFromAllBikeCards();

    // Ajouter l'état d'édition à la bike-card correspondante
    const bikeCards = this.querySelectorAll('.syb-funnel__bike-card');
    const targetCard = bikeCards[bikeIndex];

    if (targetCard) {
      targetCard.classList.add('syb-funnel__bike-card--editing');
    }
  }

  /**
   * Retire l'état d'édition de toutes les bike-cards
   */
  removeEditingStateFromAllBikeCards() {
    const bikeCards = this.querySelectorAll('.syb-funnel__bike-card');
    bikeCards.forEach((card) => {
      card.classList.remove('syb-funnel__bike-card--editing');
    });
  }

  /**
   * Gère la suppression d'un vélo
   * @param {number} bikeIndex - Index du vélo à supprimer
   */
  handleDeleteBike(bikeIndex) {
    if (!this.isValidBikeIndex(bikeIndex)) return;

    // Supprimer le vélo du store
    this.bikes.splice(bikeIndex, 1);

    // Supprimer la bike-card de TOUS les conteneurs (recap + étapes)
    this.removeBikeCardFromAllContainers(bikeIndex);

    // Mettre à jour les indices après suppression
    this.updateBikeIndicesAfterDeletion(bikeIndex);

    // Gérer la logique post-suppression
    this.handlePostDeletionLogic();
  }

  /**
   * Valide qu'un index de vélo est valide
   * @param {number} bikeIndex - Index à valider
   * @returns {boolean} True si l'index est valide
   */
  isValidBikeIndex(bikeIndex) {
    return typeof bikeIndex === 'number' && bikeIndex >= 0 && bikeIndex < this.bikes.length;
  }

  /**
   * Supprime la bike-card de TOUS les conteneurs (recap, drawer, mobile, etc.)
   * @param {number} bikeIndex - Index du vélo supprimé
   */
  removeBikeCardFromAllContainers(bikeIndex) {
    // Supprimer de TOUS les conteneurs possibles
    const allContainerSelectors = [
      '.syb-funnel__bike-cards',
      '[data-bikes-cards]',
      '[data-bikes-cards-mobile]',
      '.syb-funnel__drawer-bikes',
    ];

    allContainerSelectors.forEach((selector, containerIndex) => {
      const container = this.querySelector(selector);

      if (!container) return;

      // Chercher la bike-card avec l'index spécifique dans ce conteneur
      const targetCard = container.querySelector(`[data-bike-index="${bikeIndex}"]`);

      if (targetCard) {
        targetCard.remove();
      }
    });

    // Réindexer toutes les bike-cards dans tous les conteneurs
    this.reindexBikeCards();
  }

  /**
   * Supprime la bike-card correspondante du DOM (recap uniquement)
   * @param {number} bikeIndex - Index du vélo supprimé
   */
  removeBikeCardFromRecap(bikeIndex) {
    const recapContainer = this.querySelector('.syb-funnel__bike-cards');

    if (!recapContainer) return;

    // AVANT suppression : logger l'état actuel
    const beforeFilledCards = recapContainer.querySelectorAll('.syb-funnel__bike-card--filled');

    beforeFilledCards.forEach((card, idx) => {});

    // Chercher toutes les variantes possibles de bike-cards
    const filledCards = recapContainer.querySelectorAll('.syb-funnel__bike-card--filled');
    const allCards = recapContainer.querySelectorAll('.syb-funnel__bike-card');
    const indexedCards = recapContainer.querySelectorAll(`[data-bike-index="${bikeIndex}"]`);

    // Essayer de supprimer par data-bike-index d'abord
    if (indexedCards.length > 0) {
      indexedCards.forEach((card) => card.remove());
    }
    // Sinon utiliser l'index de position
    else if (allCards[bikeIndex]) {
      allCards[bikeIndex].remove();
    } else if (filledCards[bikeIndex]) {
      filledCards[bikeIndex].remove();
    }

    // APRÈS suppression : logger l'état
    const afterFilledCards = recapContainer.querySelectorAll('.syb-funnel__bike-card--filled');

    afterFilledCards.forEach((card, idx) => {});

    // Réindexer toutes les bike-cards dans tous les conteneurs
    this.reindexBikeCards();
  }

  /**
   * Supprime la bike-card des conteneurs d'étapes
   * @param {number} bikeIndex - Index du vélo supprimé
   */
  removeBikeCardFromSteps(bikeIndex) {
    // Chercher dans TOUS les conteneurs possibles, pas seulement les étapes spécifiques
    const allContainers = this.querySelectorAll('.syb-funnel__bike-cards, .syb-funnel__drawer-bikes');

    allContainers.forEach((container, index) => {
      // Chercher toutes les variantes possibles de bike-cards
      const filledCards = container.querySelectorAll('.syb-funnel__bike-card--filled');
      const allCards = container.querySelectorAll('.syb-funnel__bike-card');
      const indexedCards = container.querySelectorAll(`[data-bike-index="${bikeIndex}"]`);

      // Essayer de supprimer par data-bike-index d'abord
      if (indexedCards.length > 0) {
        indexedCards.forEach((card) => card.remove());
      }
      // Sinon utiliser l'index de position
      else if (allCards[bikeIndex]) {
        allCards[bikeIndex].remove();
      } else if (filledCards[bikeIndex]) {
        filledCards[bikeIndex].remove();
      }
    });
  }

  /**
   * Met à jour les numéros des bike-cards après suppression
   */
  updateBikeCardNumbers() {
    const recapContainer = this.querySelector('.syb-funnel__bike-cards');
    if (!recapContainer) return;

    const bikeCards = recapContainer.querySelectorAll('.syb-funnel__bike-card--filled');
    bikeCards.forEach((card, index) => {
      const numberElement = card.querySelector('.syb-funnel__bike-card-number');
      if (numberElement) {
        numberElement.textContent = `Vélo ${index + 1}`;
      }

      // Mettre à jour l'attribut data-bike-index
      card.dataset.bikeIndex = index;

      // Mettre à jour les boutons d'action
      const editBtn = card.querySelector('[data-action="edit-bike"]');
      const deleteBtn = card.querySelector('[data-action="delete-bike"]');

      if (editBtn) editBtn.dataset.bikeIndex = index;
      if (deleteBtn) deleteBtn.dataset.bikeIndex = index;
    });
  }

  /**
   * Réindexe toutes les bike-cards dans tous les conteneurs après suppression
   */
  reindexBikeCards() {
    // Sélecteur robuste qui trouve TOUTES les bike-cards avec un data-bike-index, peu importe leur état
    const allBikeCards = this.querySelectorAll('.syb-funnel__bike-card[data-bike-index]');

    const containerSelectors = [
      '.syb-funnel__left-content .syb-funnel__bike-cards', // Colonne gauche desktop
      '.syb-funnel__recap-main .syb-funnel__bike-cards', // Récap principal
      '.syb-funnel__drawer-bikes', // Drawer mobile
    ];

    containerSelectors.forEach((selector, containerIndex) => {
      const container = this.querySelector(selector);
      if (container) {
        const rawCards = container.querySelectorAll('.syb-funnel__bike-card[data-bike-index]');
        rawCards.forEach((card) => {});
      }
    });

    // Réindexer chaque container INDÉPENDAMMENT
    containerSelectors.forEach((selector, containerIndex) => {
      const container = this.querySelector(selector);
      if (!container) {
        return;
      }

      const containerCards = container.querySelectorAll('.syb-funnel__bike-card[data-bike-index]');

      // Filtrer les cards valides dans ce container
      const validContainerCards = Array.from(containerCards).filter((card) => {
        const hasFilledState = card.querySelector('[data-state="filled"]');
        const hasEditingState = card.classList.contains('syb-funnel__bike-card--editing');
        const hasValidIndex =
          card.dataset.bikeIndex !== undefined && parseInt(card.dataset.bikeIndex) < this.bikes.length;
        const isValid = hasFilledState || hasEditingState || hasValidIndex;

        return isValid;
      });

      if (validContainerCards.length === 0) {
        return;
      }

      // Trier par index actuel pour maintenir l'ordre dans ce container
      const sortedContainerCards = validContainerCards.sort((a, b) => {
        const indexA = parseInt(a.dataset.bikeIndex) || 0;
        const indexB = parseInt(b.dataset.bikeIndex) || 0;
        return indexA - indexB;
      });

      // Réindexer les cards de ce container (indices 0, 1, 2...)
      sortedContainerCards.forEach((card, newIndex) => {
        const oldIndex = card.dataset.bikeIndex;

        // Mettre à jour l'attribut data-bike-index
        card.dataset.bikeIndex = newIndex;

        // Mettre à jour le numéro visuel
        const numberElements = card.querySelectorAll('.syb-funnel__bike-card-number, [data-bike-number]');
        numberElements.forEach((numberElement) => {
          if (numberElement.dataset.bikeNumber !== undefined) {
            numberElement.textContent = `${newIndex + 1}`;
          } else {
            numberElement.textContent = `Vélo ${newIndex + 1}`;
          }
        });

        // Mettre à jour les boutons d'action
        const editBtn = card.querySelector('[data-action="edit-bike"]');
        const deleteBtn = card.querySelector('[data-action="delete-bike"]');

        if (editBtn) editBtn.dataset.bikeIndex = newIndex;
        if (deleteBtn) deleteBtn.dataset.bikeIndex = newIndex;
      });
    });

    // Mettre à jour le compteur global du drawer
    const drawerCounter = this.querySelector('.syb-funnel__drawer-counter [data-bike-number]');
    if (drawerCounter) {
      drawerCounter.textContent = this.bikes.length;
    }
  }

  /**
   * Gère la logique post-suppression
   */
  handlePostDeletionLogic() {
    if (this.bikes.length === 0) {
      // Aucun vélo restant : rediriger vers step_infos
      this.currentBikeIndex = 0;
      this.switchToStep('infos');
      return;
    }

    // Il reste des vélos : rester sur recap et mettre à jour l'affichage
    this.updateMobileCounter();
    this.calculateAndDisplayTotal();

    // Rester sur l'étape recap
    this.switchToStep('recap');
  }

  /**
   * Met à jour les indices après suppression d'un vélo
   * @param {number} deletedIndex - Index du vélo supprimé
   */
  updateBikeIndicesAfterDeletion(deletedIndex) {
    // Ajuster currentBikeIndex si nécessaire
    if (this.currentBikeIndex > deletedIndex) {
      this.currentBikeIndex--;
    } else if (this.currentBikeIndex === deletedIndex && this.currentBikeIndex >= this.bikes.length) {
      this.currentBikeIndex = this.bikes.length > 0 ? this.bikes.length - 1 : 0;
    }

    // S'assurer que currentBikeIndex reste valide
    if (this.currentBikeIndex >= this.bikes.length) {
      this.currentBikeIndex = this.bikes.length > 0 ? this.bikes.length - 1 : 0;
    }
  }

  /**
   * Prefill les données d'un vélo dans tous les formulaires
   * @param {Object} bike - Données du vélo
   */
  prefillBikeData(bike) {
    // Prefill champs texte
    Object.entries(bike).forEach(([field, value]) => {
      if (field === 'photos' || field === 'id' || field === 'priceEstimate') return;

      const input = this.querySelector(`[data-field="${field}"]`);
      if (input && value) {
        input.value = value;
      }
    });

    // Gestion spéciale pour le champ kilomètres selon l'énergie
    if (bike.energie) {
      this.toggleKmsField(bike.energie === 'electrique');

      // Prefill la valeur kilomètres si le vélo est électrique
      if (bike.energie === 'electrique' && bike.kilometrage) {
        const kmsInput = this.querySelector('input[data-field="bike_kms"]');
        if (kmsInput) {
          kmsInput.value = bike.kilometrage;
        }
      }
    }

    // Prefill photos si elles existent
    if (bike.photos && Object.keys(bike.photos).length > 0) {
      // Convertir l'objet photos en array ordonné pour prefillPhotos
      const photosArray = [
        bike.photos.main_photo,
        bike.photos.additional_photos_1,
        bike.photos.additional_photos_2,
        bike.photos.additional_photos_3,
        bike.photos.additional_photos_4,
      ].filter((photo) => photo instanceof File);

      this.prefillPhotos(photosArray);

      // S'assurer que les bike cards sont aussi mises à jour
      this.refreshCurrentBikePhotosDisplay();
    }

    // Mettre à jour la bike card
    this.toggleBikeCardState();
  }

  /**
   * Prefill les photos dans les inputs
   * @param {Array} photos - Liste des photos
   */
  prefillPhotos(photos) {
    const photoInputs = [
      this.querySelector('[data-field="main_photo"]'),
      this.querySelector('[data-field="additional_photo_1"]'),
      this.querySelector('[data-field="additional_photo_2"]'),
      this.querySelector('[data-field="additional_photo_3"]'),
      this.querySelector('[data-field="additional_photo_4"]'),
    ];

    photos.forEach((photo, index) => {
      const input = photoInputs[index];
      if (input && photo instanceof File) {
        // Créer un nouveau FileList avec le fichier
        const dt = new DataTransfer();
        dt.items.add(photo);
        input.files = dt.files;

        // Créer le preview
        this.createPhotoPreview(photo, input);
      }
    });

    // Rafraîchir l'affichage des photos
    this.refreshAllPhotosDisplay();
  }

  /**
   * Gère la soumission finale du formulaire
   */
  async handleFinalSubmission() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    try {
      this.showLoadingState();
      const result = await this.sendAllBikes();

      if (result.failures && result.failures.length > 0) {
        this.showErrorState(result.failures);
      } else {
        this.showSuccessState();
      }
    } catch (error) {
      this.showErrorState([
        {
          bikeIndex: null,
          error: 'Erreur technique, veuillez réessayer',
        },
      ]);
    }
  }

  /**
   * Prépare les données pour la soumission
   * @returns {Object} Données formatées
   */
  prepareSubmissionData() {
    const userData = this.getUserData();
    const bikesData = this.bikes.map((bike) => ({
      ...bike,
      priceEstimate: this.calculateBikePrice(bike, true),
      kilometrage: bike.energie === 'electrique' ? bike.kilometrage || null : null,
    }));

    return {
      user: userData,
      bikes: bikesData,
      totalEstimate: this.calculateTotalPrice(),
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Calcule le prix total de tous les vélos
   * @returns {number} Prix total
   */
  calculateTotalPrice() {
    return this.bikes.reduce((sum, bike) => {
      return sum + this.calculateBikePrice(bike, true);
    }, 0);
  }

  /**
   * Placeholder pour l'API de soumission
   * @param {Object} data - Données à soumettre
   * @returns {Promise} Résultat de la soumission
   */
  async submitEstimation(data) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          estimationId: 'EST-' + Date.now(),
          data: data,
        });
      }, 1000);
    });
  }

  /**
   * Gère le succès de la soumission
   * @param {Object} result - Résultat de l'API
   */
  handleSubmissionSuccess(result) {}

  /**
   * Gère les erreurs de soumission
   * @param {string} error - Message d'erreur
   */
  handleSubmissionError(error) {}

  /**
   * Initialise le formulaire
   */
  initializeForm() {
    const marqueSelect = this.querySelector('#BikeMarque-' + this.dataset.sectionId);

    if (marqueSelect) {
      marqueSelect.addEventListener('change', () => {
        // Déclencher aussi la synchronisation bike-card
        this.handleDynamicFieldChange(marqueSelect, 'marque', '.syb-funnel__bike-brand');
      });
    }

    this.setupYearToggle();

    // Synchroniser la bike card avec les données existantes si elles existent
    this.syncBikeCardWithData();
  }

  /**
   * Synchronise la bike card avec les données du vélo actuel
   */
  /**
   * Synchronise la bike card avec les données - VERSION DYNAMIQUE SÉCURISÉE
   * @returns {boolean} True si synchronisation réussie
   */
  syncBikeCardDynamic() {
    try {
      const currentBike = this.getCurrentBike();
      if (!currentBike) return false;

      // Cache de synchronisation pour éviter re-synchronisations inutiles
      if (this.isSyncCacheValid(currentBike)) {
        return true;
      }

      // Récupération dynamique de tous les champs configurés
      const allBikeFields = this.getAllBikeFields();
      const syncResults = {};

      // Synchronisation field par field
      for (const bikeField of allBikeFields) {
        const syncResult = this.syncFieldAutomatically(bikeField, currentBike[bikeField]);
        syncResults[bikeField] = syncResult;
      }

      // Validation que tous les champs ont été synchronisés
      const syncSuccess = this.validateSyncResults(syncResults);

      if (syncSuccess) {
        // Mise à jour du cache de synchronisation
        this.updateSyncCache(currentBike);

        // Basculer vers l'état filled si on a des données
        this.toggleBikeCardState();

        return true;
      }

      return false;
    } catch (error) {
      return this.syncBikeCardLegacy();
    }
  }

  /**
   * Synchronise un champ spécifique automatiquement
   * @param {string} bikeField - Nom du champ bike
   * @param {string} value - Valeur à synchroniser
   * @returns {boolean} True si synchronisation réussie
   */
  syncFieldAutomatically(bikeField, value) {
    try {
      // Utiliser la méthode dynamique de mise à jour
      if (value && typeof value === 'string' && value.trim() !== '') {
        this.updateBikeFieldDisplay(bikeField, value);
        return true;
      } else {
        this.hideBikeField(bikeField);
        return true;
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Met à jour l'affichage d'un champ de la bike card de manière dynamique
   * @param {string} fieldName - Nom du champ
   * @param {string} value - Valeur à afficher
   */
  updateBikeFieldDisplay(fieldName, value) {
    // Recherche de l'élément d'affichage
    let fieldElement = this.querySelector(`[data-field="${fieldName}"]`);

    // Fallback vers les anciens noms de champs si nécessaire
    if (!fieldElement) {
      const legacyField = this.mapToLegacyFieldName(fieldName);
      if (legacyField) {
        fieldElement = this.querySelector(`[data-field="${legacyField}"]`);
      }
    }

    if (!fieldElement) return;

    if (value && value.trim()) {
      fieldElement.textContent = value;
      this.showBikeField(fieldName);
    } else {
      this.hideBikeField(fieldName);
    }
  }

  /**
   * Mappe les nouveaux noms de champs vers les anciens pour compatibilité
   * @param {string} newFieldName - Nouveau nom de champ
   * @returns {string|null} Ancien nom de champ ou null
   */
  mapToLegacyFieldName(newFieldName) {
    const mapping = {
      brand: 'marque',
      model: 'modele',
      year: 'annee',
      energy: 'energie',
      type: 'type',
    };
    return mapping[newFieldName] || null;
  }

  /**
   * Vérifie si le cache de synchronisation est valide
   * @param {BikeData} currentBike - Vélo actuel
   * @returns {boolean} True si cache valide
   */
  isSyncCacheValid(currentBike) {
    if (!this._syncCache || !currentBike) return false;

    // Vérifier si l'ID et le timestamp correspondent
    return (
      this._syncCache.bikeId === currentBike.id &&
      this._syncCache.lastSync &&
      Date.now() - this._syncCache.lastSync < 1000
    ); // Cache valide 1 seconde
  }

  /**
   * Met à jour le cache de synchronisation
   * @param {BikeData} currentBike - Vélo actuel
   */
  updateSyncCache(currentBike) {
    this._syncCache = {
      bikeId: currentBike.id,
      lastSync: Date.now(),
    };
  }

  /**
   * Valide les résultats de synchronisation
   * @param {Object} syncResults - Résultats field par field
   * @returns {boolean} True si validation réussie
   */
  validateSyncResults(syncResults) {
    if (!syncResults || typeof syncResults !== 'object') return false;

    // Vérifier qu'au moins un champ a été synchronisé avec succès
    const hasSuccessfulSync = Object.values(syncResults).some((result) => result === true);
    return hasSuccessfulSync;
  }

  /**
   * Méthode de fallback vers l'ancienne logique
   * @returns {boolean} Résultat de la synchronisation legacy
   */
  syncBikeCardLegacy() {
    try {
      this.syncBikeCardWithData();
      return true;
    } catch (error) {
      return false;
    }
  }

  syncBikeCardWithData() {
    const currentBike = this.getCurrentBike();
    if (!currentBike) return;

    // Mapping des champs de données vers les champs de la bike card
    const dataFieldsMapping = {
      brand: 'brand',
      model: 'model',
      year: 'year',
      type: 'type',
      typevelo: 'typevelo',
      size: 'size',
      serial_number: 'serial_number',
      additional_details: 'additional_details',
    };

    // Mettre à jour la bike card pour chaque champ ayant une valeur
    for (const [dataField, cardField] of Object.entries(dataFieldsMapping)) {
      if (currentBike[dataField] && currentBike[dataField].trim() !== '') {
        this.updateBikeField(cardField, currentBike[dataField]);
      }
    }

    // Basculer vers l'état filled si on a des données
    this.toggleBikeCardState();
  }

  /**
   * Écoute la sélection de blocs dans l'éditeur Shopify
   */
  listenToShopifyBlockSelection() {
    if (!this.isDesignMode) return;

    // Détecter l'étape actuellement sélectionnée au chargement
    this.detectCurrentSelectedBlock();

    // Écouter les événements de sélection de blocs Shopify
    document.addEventListener('shopify:block:select', (event) => {
      this.handleBlockSelection(event);
    });
  }

  /**
   * Détecte le bloc actuellement sélectionné au chargement
   */
  detectCurrentSelectedBlock() {
    // Chercher un bloc avec l'attribut data-shopify-editor-block
    const blockElements = this.querySelectorAll('[data-shopify-editor-block]');

    for (const blockElement of blockElements) {
      // Vérifier si ce bloc est actuellement sélectionné
      if (
        blockElement.classList.contains('shopify-section-selected') ||
        blockElement.closest('.shopify-section-selected') ||
        document.activeElement === blockElement ||
        blockElement.contains(document.activeElement)
      ) {
        this.navigateToBlockStep(blockElement);
        break;
      }
    }

    // Si aucun bloc n'est détecté comme sélectionné, utiliser une approche alternative
    // Vérifier les URL ou paramètres qui pourraient indiquer la sélection
    this.detectFromUrl();
  }

  /**
   * Détecte la sélection depuis l'URL ou d'autres indicateurs
   */
  detectFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const blockId = urlParams.get('block_id') || urlParams.get('selected_block');

    if (blockId) {
      // Chercher le bloc correspondant
      const blockElement = this.querySelector(`[data-shopify-editor-block*="${blockId}"]`);
      if (blockElement) {
        this.navigateToBlockStep(blockElement);
      }
    }
  }

  /**
   * Navigue vers l'étape correspondant au bloc donné
   * @param {HTMLElement} blockElement - L'élément bloc
   */
  navigateToBlockStep(blockElement) {
    try {
      const blockData = blockElement.getAttribute('data-shopify-editor-block');
      if (!blockData) return;

      const parsedData = JSON.parse(blockData.replace(/&quot;/g, '"'));
      const blockType = parsedData.type;

      const stepMapping = {
        step_infos: 'infos',
        step_type: 'type',
        step_details: 'details',
        step_photos: 'photos',
        step_coordinates: 'coordinates',
        step_recap: 'recap',
      };

      const stepName = stepMapping[blockType];
      if (stepName && stepName !== this.currentStep) {
        this.switchToStep(stepName);
      }
    } catch (error) {
      // Ignorer les erreurs de parsing silencieusement
    }
  }

  /**
   * Gère la sélection d'un bloc depuis l'éditeur Shopify
   * @param {CustomEvent} event - L'événement de sélection de bloc
   */
  handleBlockSelection(event) {
    try {
      const { blockId, sectionId } = event.detail || {};

      // Vérifier que c'est bien notre section
      if (sectionId !== this.dataset.sectionId) return;

      // Trouver l'élément bloc correspondant
      const blockElement = this.querySelector(`[data-shopify-editor-block*="${blockId}"]`);
      if (blockElement) {
        this.navigateToBlockStep(blockElement);
      }
    } catch (error) {
      // Ignorer les erreurs de parsing silencieusement
    }
  }

  /**
   * Configure le toggle radio/select pour "Autre"
   */
  setupYearToggle() {
    const yearRadios = this.querySelectorAll('.syb-funnel__year-input');
    const otherRadio = this.querySelector('.syb-funnel__year-input--other');
    const otherOption = this.querySelector('.syb-funnel__year-option--other');

    if (!otherRadio || !otherOption) return;

    yearRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio === otherRadio && radio.checked) {
          this.toggleOtherSelectMode(true);
        } else if (radio.checked && radio !== otherRadio) {
          this.toggleOtherSelectMode(false);
          // Mettre à jour la bike card avec la valeur de l'année
          const yearBikeCardField = this.getBikeCardFieldName(radio.name) || 'annee';
          this.updateBikeCardField(yearBikeCardField, radio.value);
        }

        // Toujours mettre à jour les données du formulaire
        this.updateFormData(radio);
      });
    });
  }

  /**
   * Toggle entre radio et select pour "Autre"
   * @param {boolean} selectMode
   */
  toggleOtherSelectMode(selectMode) {
    const otherOption = this.querySelector('.syb-funnel__year-option--other');
    const selectWrapper = this.querySelector('.syb-funnel__year-select-wrapper');
    const otherSelect = this.querySelector('.syb-funnel__year-other-select');

    if (selectMode) {
      otherOption.classList.add('syb-funnel__year-option--select-mode');

      // Ajouter l'écouteur pour le select "Autre"
      if (otherSelect && !otherSelect.hasAttribute('data-listener-added')) {
        otherSelect.addEventListener('change', () => {
          if (otherSelect.value) {
            // Utiliser la méthode de mapping dynamique pour l'année
            const yearFieldName = otherSelect.name || 'bike_year';
            const bikeCardField = this.getBikeCardFieldName(yearFieldName) || 'annee';
            this.updateBikeCardField(bikeCardField, otherSelect.value);
            this.updateFormData(otherSelect);
          }
        });
        otherSelect.setAttribute('data-listener-added', 'true');
      }

      setTimeout(() => {
        otherSelect.focus();
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true,
        });
        otherSelect.dispatchEvent(clickEvent);
      }, 150);
    } else {
      otherOption.classList.remove('syb-funnel__year-option--select-mode');
    }
  }

  /**
   * Récupère la valeur d'année sélectionnée (radio ou select)
   * @returns {string} La valeur de l'année sélectionnée
   */
  getSelectedYear() {
    // Vérifier d'abord les radios
    const selectedRadio = this.querySelector('.syb-funnel__year-input:checked');
    if (selectedRadio && selectedRadio.value !== 'other') {
      return selectedRadio.value;
    }

    // Si "Autre" est sélectionné, récupérer la valeur du select
    const otherSelect = this.querySelector('.syb-funnel__year-other-select');
    if (selectedRadio && selectedRadio.value === 'other' && otherSelect && otherSelect.value) {
      return otherSelect.value;
    }

    return '';
  }

  /**
   * Gère les clics
   * @param {Event} event
   */
  handleClick(event) {
    const target = event.target.closest('[data-action]');
    if (!target) return;

    const action = target.dataset.action;

    switch (action) {
      case 'next-step':
        event.preventDefault();
        this.handleNextStep();
        break;
      case 'prev-step':
      case 'back':
        event.preventDefault();
        this.handlePrevStep();
        break;
      case 'quit':
        event.preventDefault();
        this.handleQuit();
        break;
      case 'edit-bike':
        event.preventDefault();
        this.handleEditBikeFromTarget(target);
        break;
      case 'delete-bike':
        event.preventDefault();
        this.handleDeleteBikeFromTarget(target);
        break;
      case 'edit-coordinates':
        event.preventDefault();
        this.isModifying = true;
        this.switchToStep('coordinates');
        break;
      case 'add-bike':
        event.preventDefault();
        this.handleAddBike();
        break;
      case 'close-photo-error':
        event.preventDefault();
        this.hidePhotoErrorPopover();
        break;
      case 'submit':
        event.preventDefault();
        this.handleFinalSubmission();
        break;
      default:
    }

    // Gérer les clics sur les bike cards pour édition
    const bikeCard = event.target.closest('.syb-funnel__bike-card--filled');
    if (bikeCard && !target) {
      event.preventDefault();
      this.handleEditBikeFromTarget(bikeCard);
    }
  }

  /**
   * Gère les changements de formulaire - VERSION UNIFIÉE
   * @param {Event} event
   */
  handleChange(event) {
    if (this.isPrefillingForm) {
      return;
    }

    try {
      const input = event.target;

      // Validation de base
      if (!input || !this.isValidFormInput(input)) {
        return;
      }

      // Actions communes
      this.clearError(input);
      this.updateFormData(input);

      // Logique de mapping centralisée
      this.processFieldMapping(input, 'change');
    } catch (error) {}
  }

  /**
   * Gère les saisies - VERSION UNIFIÉE
   * @param {Event} event
   */
  handleInput(event) {
    try {
      const input = event.target;

      // Validation de base
      if (!input || !this.isValidFormInput(input)) {
        return;
      }

      // Actions communes
      this.clearError(input);

      // Logique de mapping centralisée
      this.processFieldMapping(input, 'input');
    } catch (error) {}
  }

  /**
   * Valide qu'un input de formulaire est valide pour le traitement
   * @param {HTMLElement} input - Élément input
   * @returns {boolean} True si valide
   */
  isValidFormInput(input) {
    if (!input || !input.tagName) {
      return false;
    }

    const tagName = input.tagName.toLowerCase();
    const validTags = ['input', 'select', 'textarea'];

    return validTags.includes(tagName);
  }

  /**
   * Traite le mapping des champs de manière centralisée
   * @param {HTMLElement} input - Élément input
   * @param {string} eventType - Type d'événement ('change' ou 'input')
   */
  processFieldMapping(input, eventType) {
    // Récupération du nom de champ avec fallbacks
    const fieldName = this.getFieldName(input);
    if (!fieldName) {
      return;
    }

    // Récupération de la valeur avec validation
    const value = this.getFieldValue(input, fieldName);
    if (value === null) {
      return; // Pas de traitement si pas de valeur valide
    }

    // Mapping vers champ bike card
    const bikeCardField = this.getBikeCardFieldName(fieldName);
    if (!bikeCardField) {
      // Pas de mapping configuré pour ce champ
      return;
    }

    // Validation du champ bike avant mise à jour
    if (!this.isValidBikeField(bikeCardField)) {
      return;
    }

    // Mise à jour de la bike card
    this.updateBikeCardField(bikeCardField, value);

    // Actions spéciales selon le type d'événement
    this.handleSpecialFieldActions(input, fieldName, value, eventType);
  }

  /**
   * Récupère le nom de champ avec fallbacks
   * @param {HTMLElement} input - Élément input
   * @returns {string|null} Nom du champ ou null
   */
  getFieldName(input) {
    // Ordre de priorité: name, data-field, id
    return input.name || input.dataset.field || input.id || null;
  }

  /**
   * Récupère la valeur d'un champ avec validation
   * @param {HTMLElement} input - Élément input
   * @param {string} fieldName - Nom du champ
   * @returns {string|null} Valeur valide ou null
   */
  getFieldValue(input, fieldName) {
    const tagName = input.tagName.toLowerCase();
    const inputType = input.type?.toLowerCase();

    let value = null;

    // Récupération selon le type d'input
    switch (tagName) {
      case 'input':
        if (inputType === 'radio' || inputType === 'checkbox') {
          value = input.checked ? input.value : null;
        } else {
          value = input.value;
        }
        break;
      case 'select':
      case 'textarea':
        value = input.value;
        break;
    }

    // Gestion spéciale pour les années avec "other"
    if (fieldName === 'year' && value === 'other') {
      const yearSelect = this.querySelector('.syb-funnel__year-other-select');
      value = yearSelect?.value || '';
    }

    // Validation et nettoyage basique
    if (value !== null && typeof value === 'string') {
      value = value.trim();
    }

    return value;
  }

  /**
   * Gère les actions spéciales selon le champ et l'événement
   * @param {HTMLElement} input - Élément input
   * @param {string} fieldName - Nom du champ
   * @param {string} value - Valeur du champ
   * @param {string} eventType - Type d'événement
   */
  handleSpecialFieldActions(input, fieldName, value, eventType) {
    // Actions spécifiques au type d'événement change
    if (eventType === 'change') {
      // Gestion spéciale pour les types de vélo (mise à jour du placeholder taille)
      if ((fieldName === 'typevelo' || fieldName === 'bike_type') && value) {
        this.updateBikeSizePlaceholder(value);
      }

      // Autres actions spéciales peuvent être ajoutées ici
    }

    // Actions communes aux deux types d'événements
    this.handleCommonFieldActions(input, fieldName, value);
  }

  /**
   * Gère les actions communes à tous les événements
   * @param {HTMLElement} input - Élément input
   * @param {string} fieldName - Nom du champ
   * @param {string} value - Valeur du champ
   */
  handleCommonFieldActions(input, fieldName, value) {
    // Déclencher événement personnalisé pour les extensions
    const event = new CustomEvent('field:processed', {
      detail: {
        fieldName,
        value,
        inputElement: input,
        timestamp: Date.now(),
      },
    });
    this.dispatchEvent(event);
  }

  /**
   * Gère la soumission de formulaire
   * @param {Event} event
   */
  handleSubmit(event) {
    event.preventDefault();
  }

  /**
   * Gère les raccourcis clavier
   * @param {Event} event
   */
  handleKeydown(event) {
    if (event.key === 'Escape') {
      const drawer = this.querySelector('.syb-funnel__drawer[open]');
      if (drawer) {
        drawer.removeAttribute('open');
      }
    }
  }

  /**
   * Gère l'étape suivante
   */
  async handleNextStep() {
    if (!(await this.validateCurrentStep())) return;

    if (this.currentStep === 'infos') {
      this.saveBikeData();
      this.switchToStep('type');
    } else if (this.currentStep === 'type') {
      this.saveBikeData();
      this.switchToStep('details');
    } else if (this.currentStep === 'details') {
      this.saveBikeData();
      this.switchToStep('photos');
    } else if (this.currentStep === 'photos') {
      const isFirstBike = this.currentBikeIndex === 0 && !this.isModifying;

      this.saveBikeData();

      if (isFirstBike) {
        this.switchToStep('coordinates');
      } else {
        this.switchToStep('recap');
      }
    } else if (this.currentStep === 'coordinates') {
      this.saveBikeData();
      this.switchToStep('recap');
    } else {
      this.dispatchStepComplete();
    }
  }

  /**
   * Gère l'étape précédente
   */
  handlePrevStep() {
    if (this.currentStep === 'type') {
      this.switchToStep('infos');
    } else if (this.currentStep === 'details') {
      this.switchToStep('type');
    } else if (this.currentStep === 'photos') {
      this.switchToStep('details');
    } else if (this.currentStep === 'coordinates') {
      this.switchToStep('photos');
    } else if (this.currentStep === 'recap') {
      this.switchToStep('coordinates');
    } else {
      this.dispatchStepBack();
    }
  }

  /**
   * Bascule vers une étape spécifique
   * @param {string} step
   */
  switchToStep(step) {
    if (step === 'type') this.syncElectricYearNotice();

    const currentStepContent = this.querySelector(`[data-step="${this.currentStep}"]`);
    const nextStepContent = this.querySelector(`[data-step="${step}"]`);

    if (currentStepContent) {
      currentStepContent.style.display = 'none';
    }

    if (nextStepContent) {
      nextStepContent.style.display = 'flex';
    } else {
    }

    // Update mobile drawer actions
    const currentMobileActions = this.querySelector(`.syb-funnel__drawer-actions[data-step="${this.currentStep}"]`);
    const nextMobileActions = this.querySelector(`.syb-funnel__drawer-actions[data-step="${step}"]`);

    if (currentMobileActions) {
      currentMobileActions.style.display = 'none';
    }

    if (nextMobileActions) {
      nextMobileActions.style.display = 'flex';
    }

    this.currentStep = step;

    // Update data attribute for external progress bar control
    this.dataset.currentStep = step;

    // Configuration spécifique pour certaines étapes
    if (step === 'infos') {
      // Charger les données du vélo courant si c'est une modification
      if (!this.isAddingNewBike) {
        setTimeout(() => {
          this.prefillFormData();
        }, 100);
      }
    } else if (step === 'coordinates') {
      // Petit délai pour s'assurer que le DOM est mis à jour
      setTimeout(() => {
        this.prefillUserData();
        this.setupCoordinatesValidationListeners();
      }, 100);
    } else if (step === 'details') {
      // Initialiser le mode bike_size selon le type de vélo
      setTimeout(() => {
        this.updateBikeSizePlaceholder();
        this.setupBikeSizeValidation();
      }, 100);
    } else if (step === 'photos') {
      // Reset conditionnel : uniquement lors de l'ajout d'un nouveau vélo
      setTimeout(() => {
        if (this.isAddingNewBike) {
          this.resetPhotoPreviews();
          this.isAddingNewBike = false; // Reset flag après utilisation
        }
        this.loadCurrentBikePhotos();
      }, 100);
    } else if (step === 'recap') {
      // Réinitialiser le flag de modification quand on arrive au recap
      this.isModifying = false;

      // Initialiser le récapitulatif avec injection des données
      setTimeout(() => {
        this.setupStepRecap();
      }, 100);
    }
  }

  /**
   * Gère la sortie du funnel
   */
  handleQuit() {
    if (confirm("Êtes-vous sûr de vouloir quitter l'estimation ?")) {
      this.dispatchQuit();

      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    }
  }

  /**
   * Valide l'étape actuelle
   * @returns {Promise<boolean>}
   */
  async validateCurrentStep() {
    if (this.currentStep === 'infos') {
      return this.validateStepInfos();
    } else if (this.currentStep === 'type') {
      return this.validateStepType();
    } else if (this.currentStep === 'details') {
      return this.validateStepDetails();
    } else if (this.currentStep === 'photos') {
      return this.validateStepPhotos();
    } else if (this.currentStep === 'coordinates') {
      return this.validateStepCoordinates();
    } else if (this.currentStep === 'recap') {
      return true; // Pas de validation pour recap
    }

    return true;
  }

  /**
   * Valide l'étape infos
   * @returns {boolean}
   */
  validateStepInfos() {
    const form = this.querySelector('.syb-funnel__form[data-form="step-infos"]');
    if (!form) return true;

    const requiredFields = form.querySelectorAll('[required]');
    let isValid = true;

    for (const field of requiredFields) {
      if (!this.validateField(field)) {
        isValid = false;
      }
    }

    // Validation stricte pour l'autocomplete marque
    const brandHiddenInput = form.querySelector('[data-hidden-input="bike-brand"]');
    const brandVisibleInput = form.querySelector('[data-autocomplete="bike-brand"]');

    if (brandHiddenInput && brandVisibleInput) {
      if (!brandHiddenInput.value || brandHiddenInput.value.trim() === '') {
        this.showError(brandVisibleInput, 'Veuillez sélectionner une marque dans la liste');
        isValid = false;
      } else {
        this.clearError(brandVisibleInput);
      }
    }

    // Cibler seulement la bike card courante pour la validation année
    const currentBikeCard = this.getBikeCard(this.currentBikeIndex);
    const yearRadios = currentBikeCard ? currentBikeCard.querySelectorAll('input[name*="bike_year"]') : [];
    const yearSelect = currentBikeCard ? currentBikeCard.querySelector('.syb-funnel__year-other-select') : null;

    if (yearRadios.length > 0 && !this.validateYearSelection(yearRadios)) {
      isValid = false;
    } else if (yearSelect && !this.validateYearSelect(yearSelect)) {
      isValid = false;
    }

    return isValid;
  }

  /**
   * Valide l'étape type
   * @returns {boolean}
   */
  validateStepType() {
    const form = this.querySelector('.syb-funnel__form[data-form="step-type"]');
    if (!form) return true;

    let isValid = true;

    // Validation choix d'énergie
    const energyInputs = form.querySelectorAll('.syb-funnel__energy-input');
    const selectedEnergy = form.querySelector('.syb-funnel__energy-input:checked');

    if (!selectedEnergy) {
      const energyOptions = form.querySelector('.syb-funnel__energy-options');
      this.showError(energyOptions, "Veuillez sélectionner un type d'énergie");
      isValid = false;
    } else if (this.syncElectricYearNotice()) {
      isValid = false;
    }

    // Validation type de vélo
    const bikeTypeInputs = form.querySelectorAll('.syb-funnel__bike-type-input');
    const selectedBikeType = form.querySelector('.syb-funnel__bike-type-input:checked');

    if (!selectedBikeType) {
      const bikeTypes = form.querySelector('.syb-funnel__bike-types');
      this.showError(bikeTypes, 'Veuillez sélectionner un type de vélo');
      isValid = false;
    }

    return isValid;
  }

  /**
   * Affiche l'encart de refus quand un vélo électrique est plus ancien que l'année minimum
   * @returns {boolean} True si le vélo est refusé
   */
  syncElectricYearNotice() {
    const form = this.querySelector('.syb-funnel__form[data-form="step-type"]');
    if (!form) return false;

    const energyOptions = form.querySelector('.syb-funnel__energy-options');
    const selectedEnergy = form.querySelector('.syb-funnel__energy-input:checked');
    const isRefused = selectedEnergy?.value === 'electrique' && !this.isElectricYearAccepted(form);

    const notice = form.querySelector('[data-electric-year-notice]');
    if (notice) notice.hidden = !isRefused;

    // Barre rouge sans infobulle : l'encart porte le message, la classe bloque aussi le tracking d'étape.
    if (isRefused) {
      energyOptions.closest('.syb-funnel__field')?.classList.add('syb-funnel__field--error');
    } else if (selectedEnergy) {
      this.clearError(energyOptions);
    }

    return isRefused;
  }

  /**
   * Vérifie que l'année saisie à l'étape infos respecte le minimum des vélos électriques
   * @param {HTMLFormElement} stepTypeForm
   * @returns {boolean}
   */
  isElectricYearAccepted(stepTypeForm) {
    const minYear = parseInt(stepTypeForm.dataset.electricMinYear, 10);
    if (isNaN(minYear)) return true;

    const selectedYear = this.querySelector('.syb-funnel__year-input:checked');
    if (!selectedYear) return true;

    const yearValue =
      selectedYear.value === 'other' ? this.querySelector('.syb-funnel__year-other-select')?.value : selectedYear.value;

    return parseInt(yearValue, 10) >= minYear;
  }

  /**
   * Valide l'étape photos
   * @returns {boolean}
   */
  validateStepPhotos() {
    return true;
  }

  /**
   * Valide l'étape coordonnées
   * @returns {boolean}
   */
  validateStepCoordinates() {
    const form = this.querySelector('.syb-funnel__form[data-form="step-coordinates"]');
    if (!form) return true;

    let isValid = true;

    // Récupérer tous les champs requis
    const requiredFields = form.querySelectorAll('[data-field][required]');

    for (const field of requiredFields) {
      const fieldType = field.dataset.field;
      let fieldIsValid = true;

      // Nettoyer les erreurs précédentes
      this.clearFieldError(field);

      // Validation spécifique selon le type de champ
      switch (fieldType) {
        case 'firstname':
        case 'lastname':
          fieldIsValid = this.validateNameFieldForSubmit(field);
          break;

        case 'email':
          fieldIsValid = this.validateEmailFieldForSubmit(field);
          break;

        case 'phone':
          fieldIsValid = this.validatePhoneFieldForSubmit(field);
          break;

        default:
          // Validation générique pour les autres champs
          const value = field.value.trim();
          if (!value) {
            this.showFieldError(field, 'Ce champ est requis');
            fieldIsValid = false;
          }
          break;
      }

      if (!fieldIsValid) {
        isValid = false;
      }
    }

    return isValid;
  }

  /**
   * Validation finale prénom/nom pour soumission
   * @param {HTMLElement} field - Champ à valider
   * @returns {boolean}
   */
  validateNameFieldForSubmit(field) {
    const value = field.value.trim();
    const fieldType = field.dataset.field;

    if (!value) {
      this.showFieldError(field, fieldType === 'firstname' ? 'firstname_required' : 'lastname_required');
      return false;
    }

    if (/\d/.test(value)) {
      this.showFieldError(field, 'Le nom ne doit pas contenir de chiffres');
      return false;
    }

    return true;
  }

  /**
   * Validation finale email pour soumission
   * @param {HTMLElement} field - Champ à valider
   * @returns {boolean}
   */
  validateEmailFieldForSubmit(field) {
    const value = field.value.trim();

    if (!value) {
      this.showFieldError(field, 'email_required');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      this.showFieldError(field, 'email_invalid');
      return false;
    }

    return true;
  }

  /**
   * Validation finale téléphone pour soumission
   * @param {HTMLElement} field - Champ à valider
   * @returns {boolean}
   */
  validatePhoneFieldForSubmit(field) {
    const value = field.value.trim();

    if (!value) {
      this.showFieldError(field, 'phone_required');
      return false;
    }

    // Nettoyer la valeur
    const cleanValue = value.replace(/[\s\-\.]/g, '');

    // Vérifier format et absence de lettres
    const phoneRegex = /^(\+\d{1,3})?\d{8,15}$/;
    if (!phoneRegex.test(cleanValue) || /[a-zA-Z]/.test(cleanValue)) {
      this.showFieldError(field, 'phone_invalid');
      return false;
    }

    return true;
  }

  /**
   * Valide un champ
   * @param {HTMLElement} field
   * @returns {boolean}
   */
  validateField(field) {
    const value = field.value.trim();

    if (!value) {
      this.showError(field, 'Ce champ est requis');
      return false;
    }

    this.clearError(field);
    return true;
  }

  /**
   * Valide la sélection d'année (radios)
   * @param {NodeList} yearRadios
   * @returns {boolean}
   */
  validateYearSelection(yearRadios) {
    // Rechercher parmi les radios fournis, pas dans tout le DOM
    const selectedYear = Array.from(yearRadios).find((radio) => radio.checked);

    if (!selectedYear) {
      const yearGrid = this.querySelector('.syb-funnel__year-grid');
      this.showError(yearGrid, 'Veuillez sélectionner une année');
      return false;
    }

    return true;
  }

  /**
   * Valide le select d'année
   * @param {HTMLSelectElement} yearSelect
   * @returns {boolean}
   */
  validateYearSelect(yearSelect) {
    if (!yearSelect.value) {
      this.showError(yearSelect, 'Veuillez sélectionner une année');
      return false;
    }

    return true;
  }

  /**
   * Met à jour les données du formulaire dans le vélo actuel
   * @param {HTMLElement} input
   */
  updateFormData(input) {
    const name = input.name || input.id;
    if (!name) return;

    const currentBike = this.getCurrentBike();
    if (!currentBike) return;

    let value;

    // Gestion spéciale pour les inputs file
    if (input.type === 'file') {
      if (input.files && input.files.length > 0) {
        value = input.files[0]; // Stocker l'objet File
      } else {
        value = null;
      }
    } else if (input.type === 'radio' || input.type === 'checkbox') {
      value = input.checked ? input.value : '';
    } else {
      value = input.value;
    }

    // Mettre à jour la donnée dans le vélo actuel
    currentBike[name] = value;

    // Mettre à jour l'affichage de la bike card si nécessaire (sauf pour les files)
    if (input.type !== 'file') {
      const bikeCardField = this.getBikeCardFieldName(name);
      if (bikeCardField && value) {
        this.updateBikeCardField(bikeCardField, value);
      }
    }

    // Stocker dans le tableau global
    this.bikes[this.currentBikeIndex] = currentBike;

    // Mettre à jour l'état de la bike card
    this.toggleBikeCardState();

    // Garder aussi formData pour la compatibilité (mais pas les files)
    if (input.type !== 'file') {
      this.formData[name] = value;
    }
  }

  /**
   * Sauvegarde les données du vélo
   */
  /**
   * Sauvegarde les données du vélo - VERSION DYNAMIQUE SÉCURISÉE
   * @returns {BikeData|null} Données sauvegardées ou null en cas d'erreur
   */
  saveBikeDataDynamic() {
    try {
      // Sauvegarde préventive des données existantes
      const backupData = this.backupBikeData();

      // Initialiser ou récupérer l'objet vélo
      let bikeData = this.initializeBikeData();

      // Collecte dynamique des données depuis le formulaire
      const formData = this.collectFormDataDynamically();

      // Validation des données avant sauvegarde
      if (!this.validateBikeData(formData)) {
        return null;
      }

      // Fusion des données collectées
      bikeData = { ...bikeData, ...formData };

      // Sauvegarde dans le store
      this.storeBikeDataSafely(bikeData);

      // Validation post-sauvegarde
      if (!this.verifyStoredData(bikeData)) {
        this.restoreBikeData(backupData);
        return null;
      }

      // Mise à jour des interfaces
      this.updateMobileCounter();
      this.updateBikeCardDynamic();

      return bikeData;
    } catch (error) {
      return this.saveBikeDataLegacy();
    }
  }

  /**
   * Collecte dynamiquement les données depuis le formulaire de l'étape actuelle
   * @returns {Object} Données collectées
   */
  collectFormDataDynamically() {
    const stepFields = this.getStepFields(this.currentStep);
    const formData = {};

    for (const [formFieldName, bikeFieldName] of Object.entries(stepFields)) {
      try {
        const value = this.extractFieldValueDynamically(formFieldName);
        if (value !== null && value !== undefined) {
          formData[bikeFieldName] = value;
        }
      } catch (error) {}
    }

    return formData;
  }

  /**
   * Extrait la valeur d'un champ spécifique de manière dynamique
   * @param {string} formFieldName - Nom du champ dans le formulaire
   * @returns {string|null} Valeur extraite
   */
  extractFieldValueDynamically(formFieldName) {
    // Gestion spéciale pour les années avec select "other"
    if (formFieldName === 'year') {
      return this.extractYearValueSpecial();
    }

    // Recherche par attribut name
    let element = this.querySelector(`[name="${formFieldName}"]`);

    // Si pas trouvé, recherche par data-field
    if (!element) {
      element = this.querySelector(`[data-field="${formFieldName}"]`);
    }

    // Si pas trouvé, recherche par ID avec section
    if (!element && this.dataset.sectionId) {
      element = this.querySelector(`#${formFieldName}-${this.dataset.sectionId}`);
    }

    if (!element) {
      return null;
    }

    return this.getElementValue(element);
  }

  /**
   * Gestion spéciale pour l'extraction de l'année
   * @returns {string} Valeur de l'année
   */
  extractYearValueSpecial() {
    const selectedYear = this.querySelector('input[name*="bike_year"]:checked');
    if (!selectedYear) return '';

    if (selectedYear.value === 'other') {
      const otherOption = this.querySelector('.syb-funnel__year-option--other');
      const yearSelect = this.querySelector('.syb-funnel__year-other-select');

      if (otherOption?.classList.contains('syb-funnel__year-option--select-mode') && yearSelect) {
        return yearSelect.value || '';
      }
    }

    return selectedYear.value || '';
  }

  /**
   * Initialise l'objet vélo avec structure dynamique
   * @returns {BikeData} Objet vélo initialisé
   */
  initializeBikeData() {
    if (this.currentBikeIndex < this.bikes.length) {
      return { ...this.bikes[this.currentBikeIndex] };
    }

    // Créer un vélo vide avec tous les champs disponibles
    const bikeData = {
      id: Date.now().toString(),
      priceEstimate: null,
    };

    // Ajouter dynamiquement tous les champs configurés
    for (const fieldName of this.getAllBikeFields()) {
      bikeData[fieldName] = '';
    }

    return bikeData;
  }

  /**
   * Valide les données du vélo avant sauvegarde
   * @param {Object} bikeData - Données à valider
   * @returns {boolean} True si les données sont valides
   */
  validateBikeData(bikeData) {
    if (!bikeData || typeof bikeData !== 'object') {
      return false;
    }

    // Validation que les champs critiques ne sont pas tous vides
    const criticalFields = ['marque', 'brand']; // Support ancien/nouveau naming
    const hasCriticalData = criticalFields.some(
      (field) => bikeData[field] && typeof bikeData[field] === 'string' && bikeData[field].trim() !== '',
    );

    return hasCriticalData;
  }

  /**
   * Sauvegarde les données dans le store de manière sécurisée
   * @param {BikeData} bikeData - Données à sauvegarder
   */
  storeBikeDataSafely(bikeData) {
    if (this.currentBikeIndex < this.bikes.length) {
      this.bikes[this.currentBikeIndex] = bikeData;
    } else {
      this.bikes.push(bikeData);
    }
  }

  /**
   * Vérifie que les données ont été correctement stockées
   * @param {BikeData} expectedData - Données attendues
   * @returns {boolean} True si vérification réussie
   */
  verifyStoredData(expectedData) {
    const storedData = this.getCurrentBike();
    if (!storedData) return false;

    // Vérifier que l'ID correspond
    return storedData.id === expectedData.id;
  }

  /**
   * Sauvegarde préventive des données existantes
   * @returns {Object} Backup des données
   */
  backupBikeData() {
    return {
      bikes: JSON.parse(JSON.stringify(this.bikes)),
      currentBikeIndex: this.currentBikeIndex,
    };
  }

  /**
   * Restaure les données depuis le backup
   * @param {Object} backupData - Données de backup
   */
  restoreBikeData(backupData) {
    if (backupData) {
      this.bikes = backupData.bikes;
      this.currentBikeIndex = backupData.currentBikeIndex;
    }
  }

  /**
   * Méthode de fallback vers l'ancienne logique
   * @returns {BikeData|null} Données sauvegardées
   */
  saveBikeDataLegacy() {
    try {
      this.saveBikeData();
      return this.getCurrentBike();
    } catch (error) {
      return null;
    }
  }

  saveBikeData() {
    let bikeData;

    if (this.currentBikeIndex < this.bikes.length) {
      bikeData = { ...this.bikes[this.currentBikeIndex] };
    } else {
      bikeData = {
        id: Date.now().toString(),
        marque: '',
        modele: '',
        annee: '',
        energie: '',
        type: '',
        priceEstimate: null,
      };
    }

    if (this.currentStep === 'infos') {
      const marqueSelect = this.querySelector('#BikeMarque-' + this.dataset.sectionId);
      const modeleSelect = this.querySelector('#BikeModele-' + this.dataset.sectionId);
      const selectedYear = this.querySelector('input[name*="bike_year"]:checked');
      const yearSelect = this.querySelector('.syb-funnel__year-other-select');
      const otherOption = this.querySelector('.syb-funnel__year-option--other');

      let year = '';
      if (selectedYear) {
        if (selectedYear.value === 'other' && otherOption.classList.contains('syb-funnel__year-option--select-mode')) {
          year = yearSelect.value;
        } else {
          year = selectedYear.value;
        }
      }

      bikeData.marque = marqueSelect?.value || '';
      bikeData.modele = modeleSelect?.value || '';
      bikeData.annee = year;
    } else if (this.currentStep === 'type') {
      const selectedEnergy = this.querySelector('.syb-funnel__energy-input:checked');
      const selectedBikeType = this.querySelector('.syb-funnel__bike-type-input:checked');
      const kmsInput = this.querySelector('input[data-field="bike_kms"]');

      bikeData.energie = selectedEnergy?.value || '';
      bikeData.type = selectedBikeType?.value || '';

      // Sauvegarder le kilométrage si l'énergie est électrique
      if (bikeData.energie === 'electrique' && kmsInput?.value) {
        bikeData.kilometrage = parseInt(kmsInput.value, 10) || null;
      } else {
        bikeData.kilometrage = null;
      }
    } else if (this.currentStep === 'coordinates') {
      // Sauvegarder les données utilisateur dans formData
      const firstnameInput = this.querySelector('[data-field="firstname"]');
      const lastnameInput = this.querySelector('[data-field="lastname"]');
      const emailInput = this.querySelector('[data-field="email"]');
      const phoneInput = this.querySelector('[data-field="phone"]');

      if (firstnameInput) this.formData.firstname = firstnameInput.value;
      if (lastnameInput) this.formData.lastname = lastnameInput.value;
      if (emailInput) this.formData.email = emailInput.value;
      if (phoneInput) this.formData.phone = phoneInput.value;
    }

    if (this.currentBikeIndex < this.bikes.length) {
      this.bikes[this.currentBikeIndex] = bikeData;
    }

    this.updateMobileCounter();
  }

  /**
   * Met à jour le compteur mobile
   */
  updateMobileCounter() {
    const counter = this.querySelector('.syb-funnel__counter-value');
    if (counter) {
      counter.textContent = this.bikes.length.toString();
    }
  }

  /**
   * Met à jour la carte vélo - VERSION DYNAMIQUE OPTIMISÉE
   * @returns {boolean} True si mise à jour réussie
   */
  updateBikeCardDynamic() {
    try {
      const currentBike = this.getCurrentBike();

      // Utiliser le système de toggle automatique
      this.toggleBikeCardState();

      if (!currentBike) {
        return true; // Pas de vélo = état vide valide
      }

      // Récupération dynamique de tous les champs configurés
      const allBikeFields = this.getAllBikeFields();
      const renderResults = [];

      // Batch rendering pour optimiser les performances
      const fieldUpdates = this.prepareBatchFieldUpdates(currentBike, allBikeFields);

      // Exécuter les mises à jour par batch
      const batchResult = this.executeBatchFieldUpdates(fieldUpdates);

      // Gestion spéciale du prix
      const priceResult = this.renderPriceField(currentBike);

      // Validation que tous les champs ont été rendus
      const renderSuccess = batchResult && priceResult;

      return renderSuccess;
    } catch (error) {
      return this.updateBikeCardLegacy();
    }
  }

  /**
   * Prépare les mises à jour de champs en mode batch
   * @param {BikeData} bikeData - Données du vélo
   * @param {string[]} fields - Liste des champs à traiter
   * @returns {Array} Array des mises à jour à effectuer
   */
  prepareBatchFieldUpdates(bikeData, fields) {
    const updates = [];

    for (const fieldName of fields) {
      const value = bikeData[fieldName];
      const displayFieldName = this.mapBikeFieldToDisplayField(fieldName);

      if (displayFieldName) {
        updates.push({
          fieldName: displayFieldName,
          value: value,
          originalField: fieldName,
        });
      }
    }

    return updates;
  }

  /**
   * Mappe un champ bike vers son champ d'affichage
   * @param {string} bikeField - Nom du champ bike
   * @returns {string|null} Nom du champ d'affichage
   */
  mapBikeFieldToDisplayField(bikeField) {
    // Mapping des champs bike vers les champs d'affichage
    const mapping = {
      brand: 'marque',
      model: 'modele',
      year: 'annee',
      energy: 'energie',
      type: 'type',
      marque: 'marque',
      modele: 'modele',
      annee: 'annee',
      energie: 'energie',
    };

    return mapping[bikeField] || bikeField;
  }

  /**
   * Exécute les mises à jour de champs par batch
   * @param {Array} fieldUpdates - Array des mises à jour
   * @returns {boolean} True si batch exécuté avec succès
   */
  executeBatchFieldUpdates(fieldUpdates) {
    try {
      // Grouper les mises à jour pour optimiser le DOM
      const fragment = document.createDocumentFragment();
      let successCount = 0;

      for (const update of fieldUpdates) {
        const success = this.renderBikeFieldAutomatically(update.fieldName, update.value);
        if (success) successCount++;
      }

      // Considérer comme succès si au moins 50% des champs sont mis à jour
      return fieldUpdates.length === 0 || successCount / fieldUpdates.length >= 0.5;
    } catch (error) {
      return false;
    }
  }

  /**
   * Rend un champ de bike card automatiquement
   * @param {string} fieldName - Nom du champ à afficher
   * @param {string} value - Valeur à afficher
   * @returns {boolean} True si rendu réussi
   */
  renderBikeFieldAutomatically(fieldName, value) {
    try {
      // Recherche de tous les éléments d'affichage pour le vélo actuel
      const fieldElements = this.querySelectorAll(
        `[data-bike-index="${this.currentBikeIndex}"] [data-field="${fieldName}"]`,
      );
      if (!fieldElements.length) {
        return false; // Pas d'élément = pas d'erreur mais pas de succès
      }

      // Validation et nettoyage de la valeur
      const cleanValue = this.validateAndCleanDisplayValue(value);

      if (cleanValue && cleanValue.trim() !== '') {
        fieldElements.forEach((fieldElement) => {
          fieldElement.textContent = cleanValue;
        });
        this.showBikeField(fieldName);
        return true;
      } else {
        this.hideBikeField(fieldName);
        return true; // Masquer un champ vide est un succès
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Valide et nettoie la valeur pour affichage
   * @param {any} value - Valeur à valider
   * @returns {string} Valeur nettoyée
   */
  validateAndCleanDisplayValue(value) {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value !== 'string') {
      value = String(value);
    }

    return value.trim();
  }

  /**
   * Rend le champ prix avec logique spéciale
   * @param {BikeData} bikeData - Données du vélo
   * @returns {boolean} True si rendu du prix réussi
   */
  renderPriceField(bikeData) {
    try {
      if (bikeData.priceEstimate && bikeData.priceEstimate > 0) {
        const success = this.renderBikeFieldAutomatically('priceValue', `${bikeData.priceEstimate}€`);
        if (success) {
          this.showBikeField('price');
        }
        return success;
      } else {
        this.hideBikeField('price');
        return true;
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Méthode de fallback vers l'ancienne logique
   * @returns {boolean} Résultat de la mise à jour legacy
   */
  updateBikeCardLegacy() {
    try {
      this.updateBikeCard();
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Met à jour la carte vélo unique
   */
  updateBikeCard() {
    const currentBike = this.getCurrentBike();

    // Utiliser le système de toggle automatique
    this.toggleBikeCardState();

    if (currentBike) {
      // Mettre à jour tous les champs
      this.updateBikeField('marque', currentBike.marque);
      this.updateBikeField('modele', currentBike.modele);
      this.updateBikeField('annee', currentBike.annee);
      this.updateBikeField('energie', currentBike.energie);
      this.updateBikeField('type', currentBike.type);

      if (currentBike.priceEstimate) {
        this.updateBikeField('priceValue', `${currentBike.priceEstimate}€`);
        this.showBikeField('price');
      } else {
        this.hideBikeField('price');
      }
    }
  }

  /**
   * Met à jour un champ spécifique dans toutes les cartes vélo pour l'index donné
   * @param {string} fieldName - Nom du champ interne
   * @param {string} value - Valeur à afficher
   */
  updateBikeField(fieldName, value) {
    const htmlFieldName = this.mapInternalFieldToHtmlField(fieldName);

    this.updateAllBikeCardsForIndex(this.currentBikeIndex, htmlFieldName, value);
  }

  /**
   * Convertit un nom de champ interne vers le nom de champ HTML (data-field)
   * @param {string} internalFieldName - Nom du champ interne (ex: 'marque')
   * @returns {string} Nom du champ HTML (ex: 'bike_brand')
   */
  mapInternalFieldToHtmlField(internalFieldName) {
    if (!this.allFieldMappings) return internalFieldName;

    for (const [htmlFieldName, bikeFieldName] of Object.entries(this.allFieldMappings)) {
      if (bikeFieldName === internalFieldName) {
        return htmlFieldName;
      }
    }

    return internalFieldName;
  }

  /**
   * Met à jour un champ dans toutes les bike-cards (desktop + mobile) pour un index donné
   * @param {number} bikeIndex - Index du vélo
   * @param {string} fieldName - Nom du champ
   * @param {string} value - Valeur à afficher
   */
  updateAllBikeCardsForIndex(bikeIndex, fieldName, value) {
    const allBikeCards = this.querySelectorAll(`[data-bike-index="${bikeIndex}"]`);

    if (!allBikeCards.length) {
      return;
    }

    allBikeCards.forEach((bikeCard, index) => {
      const fieldElement = bikeCard.querySelector(`[data-field="${fieldName}"]`);

      if (!fieldElement) return;

      if (value && value.trim()) {
        fieldElement.textContent = value;
        this.showBikeFieldInCard(bikeCard, fieldName);
      } else {
        this.hideBikeFieldInCard(bikeCard, fieldName);
      }
    });
  }

  /**
   * Affiche un champ de la carte vélo courante
   * @param {string} fieldName - Nom du champ
   */
  showBikeField(fieldName) {
    const allBikeCards = this.querySelectorAll(`[data-bike-index="${this.currentBikeIndex}"]`);
    allBikeCards.forEach((bikeCard) => {
      this.showBikeFieldInCard(bikeCard, fieldName);
    });
  }

  /**
   * Affiche un champ dans une bike-card spécifique
   * @param {HTMLElement} bikeCard - Élément bike-card
   * @param {string} fieldName - Nom du champ
   */
  showBikeFieldInCard(bikeCard, fieldName) {
    if (!bikeCard) return;

    const fieldElement = bikeCard.querySelector(`[data-field="${fieldName}"]`);
    if (fieldElement) {
      fieldElement.style.display = '';
    }
  }

  /**
   * Cache un champ de la carte vélo courante
   * @param {string} fieldName - Nom du champ
   */
  hideBikeField(fieldName) {
    const allBikeCards = this.querySelectorAll(`[data-bike-index="${this.currentBikeIndex}"]`);
    allBikeCards.forEach((bikeCard) => {
      this.hideBikeFieldInCard(bikeCard, fieldName);
    });
  }

  /**
   * Cache un champ dans une bike-card spécifique
   * @param {HTMLElement} bikeCard - Élément bike-card
   * @param {string} fieldName - Nom du champ
   */
  hideBikeFieldInCard(bikeCard, fieldName) {
    if (!bikeCard) return;

    const fieldElement = bikeCard.querySelector(`[data-field="${fieldName}"]`);
    if (fieldElement) {
      fieldElement.style.display = 'none';
    }
  }

  /**
   * Récupère le vélo actuellement sélectionné
   * @returns {BikeData|null}
   */
  getCurrentBike() {
    return this.bikes[this.currentBikeIndex] || null;
  }

  /**
   * Gère l'édition d'un vélo
   * @param {HTMLElement} target
   */
  handleEditBikeFromTarget(target) {
    const bikeCard = target.closest('.syb-funnel__bike-card--filled');
    if (!bikeCard) return;

    // Récupérer l'index depuis le data-attribute ou calculer depuis la position
    let bikeIndex = parseInt(bikeCard.dataset.bikeIndex);

    if (isNaN(bikeIndex)) {
      // Fallback: calculer l'index depuis la position dans la liste
      const allBikeCards = this.querySelectorAll('.syb-funnel__bike-card--filled');
      bikeIndex = Array.from(allBikeCards).indexOf(bikeCard);
    }

    if (bikeIndex === -1 || bikeIndex >= this.bikes.length) return;

    // Appeler la fonction principale d'édition
    this.handleEditBike(bikeIndex);
  }

  /**
   * Gère la suppression d'un vélo depuis un élément DOM
   * @param {HTMLElement} target - Élément DOM cliqué
   */
  handleDeleteBikeFromTarget(target) {
    const bikeCard = target.closest('.syb-funnel__bike-card--filled');
    if (!bikeCard) return;

    // Récupérer l'index depuis le data-attribute ou calculer depuis la position
    let bikeIndex = parseInt(bikeCard.dataset.bikeIndex, 10);

    if (isNaN(bikeIndex)) {
      // Fallback: calculer l'index depuis la position dans la liste
      const allBikeCards = this.querySelectorAll('.syb-funnel__bike-card--filled');
      bikeIndex = Array.from(allBikeCards).indexOf(bikeCard);
    }

    if (bikeIndex === -1 || bikeIndex >= this.bikes.length) {
      return;
    }

    if (confirm('Êtes-vous sûr de vouloir supprimer ce vélo ?')) {
      this.handleDeleteBike(bikeIndex);
    }
  }

  /**
   * Rempli le formulaire avec les données d'un vélo - VERSION DYNAMIQUE SÉCURISÉE
   * @param {BikeData} bike - Données du vélo
   * @returns {boolean} True si population réussie
   */
  populateFormDynamic(bike) {
    try {
      if (!bike || typeof bike !== 'object') {
        return false;
      }

      // Sauvegarde préventive de l'état du formulaire
      const formBackup = this.backupFormState();

      // Validation des données avant population
      if (!this.validateBikeDataForEdit(bike)) {
        return false;
      }

      const populationResults = {};

      // Récupérer tous les champs configurés
      const allBikeFields = this.getAllBikeFields();

      // Parcourir tous les champs du vélo et les populer dynamiquement
      for (const bikeField of allBikeFields) {
        if (bike[bikeField] !== undefined && bike[bikeField] !== null) {
          const formFieldName = this.getFormFieldName(bikeField);
          if (formFieldName) {
            const result = this.populateFieldDynamically(formFieldName, bike[bikeField], bikeField);
            populationResults[bikeField] = result;
          }
        }
      }

      // Validation que tous les champs ont été populés correctement
      const populationSuccess = this.validatePopulationResults(populationResults);

      if (!populationSuccess) {
        this.restoreFormState(formBackup);
        return false;
      }

      // Validation post-population
      if (!this.verifyFormPopulation(bike)) {
        this.restoreFormState(formBackup);
        return false;
      }

      return true;
    } catch (error) {
      return this.populateFormLegacy(bike);
    }
  }

  /**
   * Popule un champ spécifique de manière dynamique
   * @param {string} formFieldName - Nom du champ dans le formulaire
   * @param {string} value - Valeur à assigner
   * @param {string} bikeFieldName - Nom du champ bike (pour gestion spéciale)
   * @returns {boolean} True si population réussie
   */
  populateFieldDynamically(formFieldName, value, bikeFieldName) {
    try {
      // Gestion spéciale pour certains champs
      if (this.isSpecialField(formFieldName)) {
        return this.populateSpecialField(formFieldName, value, bikeFieldName);
      }

      // Recherche de l'élément form
      const element = this.findFormElement(formFieldName);
      if (!element) {
        return false;
      }

      // Population selon le type d'élément
      return this.populateElementByType(element, value, formFieldName);
    } catch (error) {
      return false;
    }
  }

  /**
   * Trouve l'élément de formulaire correspondant au champ
   * @param {string} formFieldName - Nom du champ
   * @returns {HTMLElement|null} Élément trouvé
   */
  findFormElement(formFieldName) {
    // Recherche par attribut name
    let element = this.querySelector(`[name="${formFieldName}"]`);

    // Si pas trouvé, recherche par data-field
    if (!element) {
      element = this.querySelector(`[data-field="${formFieldName}"]`);
    }

    // Si pas trouvé, recherche par ID avec section
    if (!element && this.dataset.sectionId) {
      element = this.querySelector(`#${formFieldName}-${this.dataset.sectionId}`);
    }

    return element;
  }

  /**
   * Popule un élément selon son type
   * @param {HTMLElement} element - Élément DOM
   * @param {string} value - Valeur à assigner
   * @param {string} formFieldName - Nom du champ
   * @returns {boolean} True si population réussie
   */
  populateElementByType(element, value, formFieldName) {
    const tagName = element.tagName.toLowerCase();
    const inputType = element.type?.toLowerCase();

    try {
      switch (tagName) {
        case 'select':
          return this.populateSelectElement(element, value, formFieldName);

        case 'input':
          if (inputType === 'radio') {
            return this.populateRadioField(formFieldName, value);
          } else if (inputType === 'checkbox') {
            element.checked = Boolean(value);
            return true;
          } else {
            element.value = value;
            return true;
          }

        case 'textarea':
          element.value = value;
          return true;

        default:
          return false;
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Popule un élément select
   * @param {HTMLSelectElement} selectElement - Élément select
   * @param {string} value - Valeur à sélectionner
   * @param {string} formFieldName - Nom du champ
   * @returns {boolean} True si population réussie
   */
  populateSelectElement(selectElement, value, formFieldName) {
    selectElement.value = value;

    // Déclencher l'événement change pour les selects dynamiques (comme marque/modèle)
    if (formFieldName === 'BikeMarque' || formFieldName === 'brand') {
      selectElement.dispatchEvent(new Event('change'));
    }

    return true;
  }

  /**
   * Popule un champ radio
   * @param {string} formFieldName - Nom du champ
   * @param {string} value - Valeur à sélectionner
   * @returns {boolean} True si population réussie
   */
  populateRadioField(formFieldName, value) {
    const radioElement = this.querySelector(`input[name="${formFieldName}"][value="${value}"]`);
    if (radioElement) {
      radioElement.checked = true;
      return true;
    }

    // Recherche alternative avec patterns plus flexibles
    const allRadios = this.querySelectorAll(`input[name*="${formFieldName}"]`);
    for (const radio of allRadios) {
      if (radio.value === value) {
        radio.checked = true;
        return true;
      }
    }

    return false;
  }

  /**
   * Vérifie si un champ nécessite une gestion spéciale
   * @param {string} formFieldName - Nom du champ
   * @returns {boolean} True si champ spécial
   */
  isSpecialField(formFieldName) {
    const specialFields = ['year', 'bike_year', 'annee'];
    return specialFields.includes(formFieldName);
  }

  /**
   * Popule un champ spécial avec logique particulière
   * @param {string} formFieldName - Nom du champ
   * @param {string} value - Valeur à assigner
   * @param {string} bikeFieldName - Nom du champ bike
   * @returns {boolean} True si population réussie
   */
  populateSpecialField(formFieldName, value, bikeFieldName) {
    if (formFieldName === 'year' || formFieldName === 'bike_year' || bikeFieldName === 'annee') {
      return this.populateYearField(value);
    }

    return false;
  }

  /**
   * Popule le champ année avec gestion de l'option "Autre"
   * @param {string} yearValue - Année à sélectionner
   * @returns {boolean} True si population réussie
   */
  populateYearField(yearValue) {
    if (!yearValue) return true;

    // Recherche d'un radio avec cette valeur d'année
    const yearRadio = this.querySelector(`input[name*="bike_year"][value="${yearValue}"]`);

    if (yearRadio) {
      yearRadio.checked = true;
      return true;
    } else {
      // Année dans "Autre"
      const otherRadio = this.querySelector('.syb-funnel__year-input--other');
      const yearSelect = this.querySelector('.syb-funnel__year-other-select');

      if (otherRadio && yearSelect) {
        otherRadio.checked = true;
        this.toggleOtherSelectMode(true);
        yearSelect.value = yearValue;
        return true;
      }
    }

    return false;
  }

  /**
   * Valide les données du vélo pour édition
   * @param {BikeData} bike - Données du vélo
   * @returns {boolean} True si validation réussie
   */
  validateBikeDataForEdit(bike) {
    if (!bike.id) {
      return false;
    }

    return true;
  }

  /**
   * Valide les résultats de population
   * @param {Object} results - Résultats field par field
   * @returns {boolean} True si validation réussie
   */
  validatePopulationResults(results) {
    if (!results || typeof results !== 'object') return false;

    const totalFields = Object.keys(results).length;
    const successfulFields = Object.values(results).filter((r) => r === true).length;

    // Accepter si au moins 80% des champs ont été populés avec succès
    return totalFields === 0 || successfulFields / totalFields >= 0.8;
  }

  /**
   * Vérifie que la population s'est bien déroulée
   * @param {BikeData} expectedBike - Données attendues
   * @returns {boolean} True si vérification réussie
   */
  verifyFormPopulation(expectedBike) {
    // Vérification basique que les champs principaux sont populés
    if (expectedBike.marque || expectedBike.brand) {
      const marqueSelect = this.querySelector('#BikeMarque-' + this.dataset.sectionId);
      if (marqueSelect && !marqueSelect.value) {
        return false;
      }
    }

    return true;
  }

  /**
   * Sauvegarde l'état actuel du formulaire
   * @returns {Object} État du formulaire
   */
  backupFormState() {
    const backup = {};

    // Sauvegarder tous les inputs, selects, textareas
    const formElements = this.querySelectorAll('input, select, textarea');
    formElements.forEach((element) => {
      const key = element.name || element.id || element.dataset.field;
      if (key) {
        if (element.type === 'radio' || element.type === 'checkbox') {
          backup[key] = element.checked;
        } else {
          backup[key] = element.value;
        }
      }
    });

    return backup;
  }

  /**
   * Restaure l'état du formulaire depuis un backup
   * @param {Object} backup - État sauvegardé
   */
  restoreFormState(backup) {
    if (!backup) return;

    for (const [key, value] of Object.entries(backup)) {
      let element =
        this.querySelector(`[name="${key}"]`) ||
        this.querySelector(`#${key}`) ||
        this.querySelector(`[data-field="${key}"]`);

      if (element) {
        if (element.type === 'radio' || element.type === 'checkbox') {
          element.checked = value;
        } else {
          element.value = value;
        }
      }
    }
  }

  /**
   * Méthode de fallback vers l'ancienne logique
   * @param {BikeData} bike - Données du vélo
   * @returns {boolean} Résultat de la population legacy
   */
  populateFormLegacy(bike) {
    try {
      this.populateFormWithBikeData(bike);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Rempli le formulaire avec les données d'un vélo
   * @param {BikeData} bike
   */
  populateFormWithBikeData(bike) {
    // Étape infos
    const marqueSelect = this.querySelector('#BikeMarque-' + this.dataset.sectionId);
    const modeleSelect = this.querySelector('#BikeModele-' + this.dataset.sectionId);

    if (marqueSelect && bike.marque) {
      marqueSelect.value = bike.marque;
      marqueSelect.dispatchEvent(new Event('change'));
    }

    if (modeleSelect && bike.modele) {
      // Attendre que les modèles se chargent
      setTimeout(() => {
        modeleSelect.value = bike.modele;
      }, 600);
    }

    // Année
    if (bike.annee) {
      const yearRadio = this.querySelector(`input[name*="bike_year"][value="${bike.annee}"]`);
      if (yearRadio) {
        yearRadio.checked = true;
      } else {
        // Année dans "Autre"
        const otherRadio = this.querySelector('.syb-funnel__year-input--other');
        const yearSelect = this.querySelector('.syb-funnel__year-other-select');

        if (otherRadio && yearSelect) {
          otherRadio.checked = true;
          this.toggleOtherSelectMode(true);
          yearSelect.value = bike.annee;
        }
      }
    }

    // Étape type
    if (bike.energie) {
      const energyRadio = this.querySelector(`input[name="type"][value="${bike.energie}"]`);
      if (energyRadio) energyRadio.checked = true;
    }

    if (bike.type) {
      const typeRadio = this.querySelector(`input[name="typevelo"][value="${bike.type}"]`);
      if (typeRadio) typeRadio.checked = true;
    }
  }

  /**
   * Affiche une erreur avec barre rouge latérale
   * @param {HTMLElement} field
   * @param {string} message
   */
  showError(field, message) {
    let fieldContainer = field.closest('.syb-funnel__field');

    // Si pas de .syb-funnel__field trouvé, chercher .syb-funnel__photo-main pour les photos
    if (!fieldContainer) {
      fieldContainer = field.closest('.syb-funnel__photo-main');
    }

    if (fieldContainer) {
      fieldContainer.classList.add('syb-funnel__field--error');

      // Afficher le message d'erreur dans l'élément d'erreur
      const errorElement = fieldContainer.querySelector('.syb-funnel__field-error');
      if (errorElement && message) {
        if (!fieldContainer.closest('.syb-funnel__field--bike-size')) {
          errorElement.textContent = message;
        }

        errorElement.style.display = 'block';
      }
    }
    field.setAttribute('aria-invalid', 'true');
    field.focus();
  }

  /**
   * Efface une erreur
   * @param {HTMLElement} field
   */
  clearError(field) {
    let fieldContainer = field.closest('.syb-funnel__field');

    // Si pas de .syb-funnel__field trouvé, chercher .syb-funnel__photo-main pour les photos
    if (!fieldContainer) {
      fieldContainer = field.closest('.syb-funnel__photo-main');
    }

    if (fieldContainer) {
      fieldContainer.classList.remove('syb-funnel__field--error');

      // Cacher le message d'erreur
      const errorElement = fieldContainer.querySelector('.syb-funnel__field-error');
      if (errorElement) {
        errorElement.style.display = 'none';

        if (!fieldContainer.closest('.syb-funnel__field--bike-size')) {
          errorElement.textContent = '';
        }
      }
    }
    field.removeAttribute('aria-invalid');
  }

  /**
   * Dispatche l'événement de completion d'étape
   */
  dispatchStepComplete() {
    const event = new CustomEvent('step:complete', {
      detail: {
        step: this.currentStep,
        bikes: this.bikes,
        formData: this.formData,
      },
    });
    this.dispatchEvent(event);
  }

  /**
   * Dispatche l'événement de retour d'étape
   */
  dispatchStepBack() {
    const event = new CustomEvent('step:back', {
      detail: {
        step: this.currentStep,
      },
    });
    this.dispatchEvent(event);
  }

  /**
   * Dispatche l'événement de sortie
   */
  dispatchQuit() {
    const event = new CustomEvent('funnel:quit', {
      detail: {
        step: this.currentStep,
        bikes: this.bikes,
      },
    });
    this.dispatchEvent(event);
  }

  /**
   * COLLECTE DE DONNÉES POUR API
   */

  /**
   * Collecte les données utilisateur depuis l'étape step_user_info
   * @returns {Object|null} Données utilisateur formatées selon le schéma API ou null si erreur
   */
  collectUserData() {
    try {
      const userInfoStep = this.querySelector('[data-step="coordinates"]');

      if (!userInfoStep) {
        return null;
      }

      // Collecte des champs requis
      const lastnameField = userInfoStep.querySelector('[data-field="lastname"]');
      const firstnameField = userInfoStep.querySelector('[data-field="firstname"]');
      const emailField = userInfoStep.querySelector('[data-field="email"]');
      const phoneField = userInfoStep.querySelector('[data-field="phone"]');
      const buyerField = userInfoStep.querySelector('[data-field="want_new_bike"]');
      const isProfessional = userInfoStep.querySelector('[data-field="is_professional"]');

      // Validation des champs requis
      if (!lastnameField?.value?.trim()) {
        return null;
      }

      if (!firstnameField?.value?.trim()) {
        return null;
      }

      if (!emailField?.value?.trim()) {
        return null;
      }

      if (!phoneField?.value?.trim()) {
        return null;
      }

      if (!buyerField) {
        return null;
      }

      if (!isProfessional) {
        return null;
      }

      const userData = {
        name: lastnameField.value.trim(),
        prenom: firstnameField.value.trim(),
        email: emailField.value.trim(),
        phone: phoneField.value.trim(),
        buyer: buyerField?.checked || false,
        is_professional: isProfessional?.checked || false,
      };

      return userData;
    } catch (error) {
      return null;
    }
  }

  /**
   * Collecte les données d'un vélo spécifique par index
   * @param {number} bikeIndex - Index du vélo dans this.bikes
   * @returns {Promise<Object|null>} Données du vélo formatées selon le schéma API ou null si erreur
   */
  async collectBikeData(bikeIndex) {
    try {
      if (typeof bikeIndex !== 'number' || bikeIndex < 0 || bikeIndex >= this.bikes.length) {
        return null;
      }

      const bikeData = this.bikes[bikeIndex];
      if (!bikeData) {
        return null;
      }

      const bikeCard = this.getBikeCard(bikeIndex);
      if (!bikeCard) {
        return null;
      }

      // Mapping des champs vers le schéma API
      const brand = bikeData.marque || bikeData.brand;
      const model = bikeData.modele || bikeData.model;
      const year = bikeData.annee || bikeData.year;
      const type = bikeData.energie || bikeData.type;
      const typevelo = bikeData.type || bikeData.typevelo;
      const bikeSize = bikeData.bike_size || '';
      const serialNumber = bikeData.marquage_number || '';
      const changesMade = bikeData.additional_details || '';
      const kms = bikeData.bike_kms || '';

      // Validation des champs requis
      if (!brand?.trim()) {
        return null;
      }
      if (!model?.trim()) {
        return null;
      }
      if (!year?.toString().trim()) {
        return null;
      }

      if (!type?.trim()) {
        return null;
      }
      if (!typevelo?.trim()) {
        return null;
      }

      // Collecte des photos depuis this.bikes[bikeIndex].photos.
      // picture_names est aligné par index avec pictures : il porte un nom de slot
      // (ex. vue-de-cote) pour identifier chaque photo côté n8n / ERP Achat.
      const pictures = [];
      const pictureNames = [];
      if (bikeData.photos && typeof bikeData.photos === 'object') {
        for (const [fieldKey, photoFile] of Object.entries(bikeData.photos)) {
          if (photoFile instanceof File) {
            try {
              const reader = new FileReader();
              const base64 = await new Promise((resolve, reject) => {
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(photoFile);
              });
              pictures.push(base64);
              pictureNames.push(this.getPhotoExportName(fieldKey));
            } catch (error) {
              continue;
            }
          }
        }
      }

      return {
        id: bikeData.id,
        brand: brand.trim(),
        model: model.trim(),
        year: year.toString().trim(),
        type: type.trim() === 'electrique' ? 'electrique' : 'musculaire',
        typevelo: typevelo.trim(),
        kms: kms.toString().trim(),
        bike_size: bikeSize.trim(),
        serial_number: serialNumber.trim(),
        changes_made: changesMade.trim(),
        pictures: pictures,
        picture_names: pictureNames,
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Nom d'export d'une photo selon son slot, pour identification côté ERP Achat.
   * @param {string} fieldKey - Clé de slot (main_photo, additional_photos_1..4)
   * @returns {string} Nom lisible sans extension (ex. vue-de-cote)
   */
  getPhotoExportName(fieldKey) {
    const map = {
      main_photo: 'vue-de-cote',
      additional_photos_1: 'complementaire-1',
      additional_photos_2: 'complementaire-2',
      additional_photos_3: 'complementaire-3',
      additional_photos_4: 'complementaire-4',
    };
    return map[fieldKey] || fieldKey;
  }

  /**
   * Collecte les données de tous les vélos valides
   * @returns {Promise<Array>} Array des vélos valides formatés selon le schéma API
   */
  async collectAllBikesData() {
    const validBikes = [];

    for (let i = 0; i < this.bikes.length; i++) {
      const bikeData = await this.collectBikeData(i);

      if (bikeData) {
        validBikes.push(bikeData);
      } else {
      }
    }

    return validBikes;
  }

  /**
   * SYSTÈME DE VALIDATION ET TESTS DE COMPARAISON GLOBAUX
   */

  /**
   * API publique - Ajouter un vélo
   * @param {BikeData} bikeData
   */
  addBike(bikeData) {
    this.bikes.push(bikeData);
    this.updateMobileCounter();
    this.updateBikeCard();
  }

  /**
   * API publique - Supprimer un vélo
   * @param {string} bikeId
   */
  removeBike(bikeId) {
    this.bikes = this.bikes.filter((bike) => bike.id !== bikeId);
    this.updateMobileCounter();
    this.updateBikeCard();
  }

  /**
   * API publique - Obtenir les vélos
   * @returns {BikeData[]}
   */
  getBikes() {
    return [...this.bikes];
  }

  /**
   * API publique - Réinitialiser le funnel
   */
  reset() {
    this.bikes = [];
    this.currentBikeIndex = 0;
    this.formData = {};
    this.updateMobileCounter();

    const forms = this.querySelectorAll('form');
    forms.forEach((form) => form.reset());

    const errors = this.querySelectorAll('.syb-funnel__error');
    errors.forEach((error) => (error.textContent = ''));
  }

  /**
   * Prérempli les formulaires avec les données de test
   */
  prefillFormData() {
    this.isPrefillingForm = true;

    setTimeout(() => {
      const currentBike = this.getCurrentBike();

      if (!currentBike) {
        this.isPrefillingForm = false;
        return;
      }

      Object.entries(currentBike).forEach(([field, value]) => {
        const input = this.querySelector(`[data-field="${field}"]`);
        if (input && value && input.type !== 'file') {
          input.value = value;
        }
      });

      this.toggleBikeCardState();
      this.isPrefillingForm = false;
    }, 100);
  }

  /**
   * Envoie les données d'un vélo unique vers l'API
   * @param {Object} bikeData - Données du vélo formatées selon le schéma API
   * @param {Object} userData - Données utilisateur
   * @returns {Promise<Object>} Résultat de l'envoi {success: boolean, bikeIndex: number, data?: any, error?: string}
   */
  async sendSingleBike(bikeData, userData, bikeIndex = null) {
    if (!bikeData || !userData) {
      return {
        success: false,
        bikeIndex,
        error: 'Données vélo ou utilisateur manquantes',
      };
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      const combinedData = {
        ...bikeData,
        ...userData,
      };

      const response = await fetch(this.dataset.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(combinedData),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.status === 200) {
        let responseData;

        try {
          responseData = await response.json();
        } catch (e) {
          responseData = { message: 'Workflow started' };
        }

        // n8n renvoie juste un message "Workflow was started"
        return {
          success: true,
          bikeIndex,
          data: responseData,
        };
      } else {
        const errorText = await response.text();
        return {
          success: false,
          bikeIndex,
          error: `Erreur HTTP ${response.status}: ${errorText}`,
        };
      }
    } catch (error) {
      return this.handleAPIError(error, bikeIndex);
    }
  }

  /**
   * Envoie tous les vélos valides vers l'API de manière séquentielle
   * @returns {Promise<Object>} Résultats de l'envoi {totalBikes: number, successCount: number, failures: array}
   */
  async sendAllBikes() {
    try {
      const userData = this.collectUserData();

      if (!userData) {
        return {
          totalBikes: 0,
          successCount: 0,
          failures: [
            {
              bikeIndex: null,
              error: 'Données utilisateur invalides ou manquantes',
            },
          ],
        };
      }

      const allBikesData = await this.collectAllBikesData();

      if (allBikesData.length === 0) {
        return {
          totalBikes: 0,
          successCount: 0,
          failures: [
            {
              bikeIndex: null,
              error: 'Aucun vélo valide trouvé',
            },
          ],
        };
      }

      const results = {
        totalBikes: allBikesData.length,
        successCount: 0,
        failures: [],
      };

      for (let i = 0; i < allBikesData.length; i++) {
        const bikeData = allBikesData[i];
        const targetBike = this.bikes.find((b) => b.id === bikeData.id);
        if (targetBike?.submissionStatus === 'sent') {
          results.successCount++;
          continue;
        }
        const result = await this.sendSingleBike(bikeData, userData, i);

        if (result.success) {
          results.successCount++;
          if (targetBike) targetBike.submissionStatus = 'sent';
        } else {
          results.failures.push({
            bikeIndex: i,
            error: result.error,
            bikeData: bikeData,
          });
          if (targetBike) targetBike.submissionStatus = 'failed';
        }

        if (i < allBikesData.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      }

      return results;
    } catch (error) {
      return {
        totalBikes: 0,
        successCount: 0,
        failures: [
          {
            bikeIndex: null,
            error: `Erreur lors de l'envoi: ${error.message}`,
          },
        ],
      };
    }
  }

  /**
   * Affiche l'état de chargement sur le bouton submit
   */
  showLoadingState() {
    this.querySelectorAll('.syb-funnel__btn[type="submit"], [data-action="submit"], button[type="submit"]').forEach(
      (btn) => {
        btn.disabled = true;
        btn.classList.add('syb-funnel__btn--loading');
        btn.dataset.originalText = btn.textContent;
        btn.textContent = 'Envoi en cours...';
      },
    );
  }

  /**
   * Affiche l'état d'erreur avec popover temporaire
   * @param {Array} failures - Liste des échecs de l'envoi API
   */
  showErrorState(failures) {
    if (Array.isArray(failures)) {
      failures.forEach((f) => console.error('[SYB] Échec envoi vélo', f.bikeIndex, ':', f.error));
    }
    const first = Array.isArray(failures) && failures[0] && failures[0].error;
    let message = window.theme?.strings?.error_send_bikes || "Une erreur s'est produite lors de l'envoi";
    if (first && /timeout|secondes/i.test(first)) {
      message = "L'envoi a expiré (photos trop lourdes ou connexion lente). Réessayez.";
    } else if (first && /réseau|connexion/i.test(first)) {
      message = 'Problème de connexion. Vérifiez votre réseau et réessayez.';
    }
    this.createErrorPopover(message);
    this.resetSubmitState();
  }

  /**
   * Crée et affiche le popover d'erreur avec progress bar
   * @param {string} message - Message d'erreur à afficher
   */
  createErrorPopover(message) {
    const existingPopover = this.querySelector('.syb-error-popover');
    if (existingPopover) {
      existingPopover.remove();
    }

    const popover = document.createElement('div');
    popover.className = 'syb-error-popover';
    popover.setAttribute('popover', 'manual');
    popover.innerHTML = `
      <div class="syb-error-popover__content">
        <p>${message}</p>
        <div class="syb-error-popover__progress">
          <div class="syb-error-popover__progress-bar"></div>
        </div>
      </div>
    `;

    this.appendChild(popover);
    popover.showPopover();

    const progressBar = popover.querySelector('.syb-error-popover__progress-bar');
    if (progressBar) {
      progressBar.style.animation = 'syb-progress-countdown 5s linear forwards';
    }

    setTimeout(() => {
      if (popover && popover.isConnected) {
        popover.hidePopover();
        popover.remove();
      }
    }, 5000);
  }

  /**
   * Affiche l'état de succès en toggleant une classe sur les containers
   */
  showSuccessState() {
    // Container desktop
    const desktopContainer = this.querySelector('[data-step="recap"] .syb-funnel__actions');
    if (desktopContainer) {
      desktopContainer.classList.add('syb-funnel__actions--success');
    }

    // Container mobile
    const mobileContainer = this.querySelector('.syb-funnel__footer-actions');
    if (mobileContainer) {
      mobileContainer.classList.add('syb-funnel__footer-actions--success');
    }

    this.querySelectorAll('.syb-funnel__btn[type="submit"], [data-action="submit"], button[type="submit"]').forEach(
      (btn) => {
        btn.disabled = true;
        btn.textContent = '✓ Envoyé';
        btn.classList.remove('syb-funnel__btn--loading');
        btn.classList.add('syb-funnel__btn--success');
      },
    );
  }

  /**
   * Remet le bouton submit en état initial
   */
  resetSubmitState() {
    this.isSubmitting = false;
    this.querySelectorAll('.syb-funnel__btn[type="submit"], [data-action="submit"], button[type="submit"]').forEach(
      (btn) => {
        btn.disabled = false;
        btn.classList.remove('syb-funnel__btn--loading', 'syb-funnel__btn--success');
        if (btn.dataset.originalText) {
          btn.textContent = btn.dataset.originalText;
          delete btn.dataset.originalText;
        }
      },
    );
  }

  /**
   * Gère les erreurs d'API de manière standardisée
   * @param {Error} error - Erreur capturée
   * @param {number|null} bikeIndex - Index du vélo concerné (optionnel)
   * @returns {Object} Objet d'erreur structuré {success: false, bikeIndex: number|null, error: string}
   */
  handleAPIError(error, bikeIndex) {
    let errorMessage = 'Erreur inconnue';

    if (error.name === 'AbortError') {
      errorMessage = 'Timeout de la requête (60 secondes dépassées)';
    } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
      errorMessage = 'Erreur de réseau - Vérifiez votre connexion internet';
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      bikeIndex: bikeIndex,
      error: errorMessage,
    };
  }

  /**
   * Initialise l'autocomplete pour les champs supportés
   */
  setupAutocomplete() {
    // Autocomplete pour bike-brand
    const brandInputs = this.querySelectorAll('[data-autocomplete="bike-brand"]');
    brandInputs.forEach((input) => {
      this.initializeAutocompleteField(input, 'bike-brand');
    });

    // Autocomplete pour bike-size
    const sizeInputs = this.querySelectorAll('[data-autocomplete="bike-size"]');
    sizeInputs.forEach((input) => {
      this.initializeAutocompleteField(input, 'bike-size');
    });
  }

  /**
   * Prefill un champ d'autocomplete avec une valeur donnée
   * @param {string} type - Type d'autocomplete ('bike-brand' ou 'bike-size')
   * @param {string} value - Valeur à définir
   * @returns {boolean} True si le prefill a réussi
   */
  prefillAutocompleteField(type, value) {
    if (!value) return false;

    const input = this.querySelector(`[data-autocomplete="${type}"]`);
    if (!input) return false;

    const wrapper = input.closest('.syb-funnel__autocomplete-wrapper');
    if (!wrapper) return false;

    const hiddenInput = wrapper.querySelector(`[data-hidden-input="${type}"]`);
    const listbox = wrapper.querySelector('.syb-funnel__autocomplete-list');

    if (!hiddenInput || !listbox) return false;

    // INTERCEPTER TOUTES LES MODIFICATIONS DE L'INPUT POUR DEBUG
    this.setupValueTracing(input, type);

    // Chercher l'option correspondante dans la liste
    const options = Array.from(listbox.querySelectorAll('.syb-funnel__autocomplete-option'));
    const matchingOption = options.find((option) => {
      const optionValue = option.dataset.value;
      const optionText = option.textContent.trim();

      // Correspondance par valeur ou par texte (insensible à la casse)
      return (
        optionValue === value ||
        optionText.toLowerCase() === value.toLowerCase() ||
        optionValue.toLowerCase() === value.toLowerCase()
      );
    });

    if (matchingOption) {
      // Utiliser le texte de l'option pour l'input visible
      const displayText = matchingOption.textContent.trim();
      const optionValue = matchingOption.dataset.value;

      // SOLUTION: Marquer temporairement l'input comme en cours de prefill pour éviter les interférences
      input.dataset.prefilling = 'true';

      // Définir les valeurs dans l'ordre correct (hidden en premier)
      hiddenInput.value = optionValue;
      input.value = displayText;

      // Déclencher l'événement change sur l'input hidden
      hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));

      // Retirer le marqueur de prefill après un délai pour permettre le fonctionnement normal
      setTimeout(() => {
        delete input.dataset.prefilling;
      }, 200);

      return true;
    } else {
      // Fallback: définir directement la valeur
      hiddenInput.value = value;
      input.value = value;

      hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));

      return true;
    }
  }

  /**
   * Configure un tracing complet des modifications de valeur d'un input pour debug
   * @param {HTMLInputElement} input - L'input à tracer
   * @param {string} type - Type d'autocomplete pour identification
   */
  setupValueTracing(input, type) {
    if (input._valueTraced) return; // Éviter le double tracing

    // Sauvegarder la propriété value originale
    const originalDescriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');

    // Créer une propriété personnalisée pour tracer les changements
    Object.defineProperty(input, 'value', {
      get() {
        return originalDescriptor.get.call(this);
      },
      set(newValue) {
        const oldValue = originalDescriptor.get.call(this);

        // Appliquer la nouvelle valeur
        originalDescriptor.set.call(this, newValue);
      },
      configurable: true,
      enumerable: true,
    });

    input._valueTraced = true;
  }

  /**
   * Initialise un champ autocomplete spécifique
   * @param {HTMLElement} input - Input element
   * @param {string} type - Type d'autocomplete ('bike-brand' ou 'bike-size')
   */
  initializeAutocompleteField(input, type) {
    const wrapper = input.closest('.syb-funnel__autocomplete-wrapper');
    if (!wrapper) return;

    const hiddenInput = wrapper.querySelector(`[data-hidden-input="${type}"]`);
    const listbox = wrapper.querySelector('.syb-funnel__autocomplete-list');
    const icon = wrapper.querySelector('.syb-funnel__autocomplete-icon');

    if (!hiddenInput || !listbox || !icon) return;

    // Collecter toutes les options disponibles
    const options = Array.from(listbox.querySelectorAll('.syb-funnel__autocomplete-option')).map((option) => ({
      value: option.dataset.value,
      text: option.textContent.trim(),
      element: option,
    }));

    // State management
    let isOpen = false;
    let filteredOptions = [...options];
    let selectedIndex = -1;
    let isSelectingOption = false;
    let mousedownStartTime = 0;
    let blurTimer = null;

    /**
     * Filtre les options selon la valeur de recherche
     * @param {string} searchValue - Valeur de recherche
     */
    const filterOptions = (searchValue) => {
      if (!searchValue.trim()) {
        filteredOptions = [...options];
      } else {
        if (type === 'bike-size') {
          // Filtrage spécial pour bike-size (numérique)
          const searchNumber = parseFloat(searchValue);
          filteredOptions = options.filter((option) => {
            const optionNumber = parseFloat(option.value);
            // Correspondance exacte ou début de nombre
            return (
              !isNaN(searchNumber) &&
              !isNaN(optionNumber) &&
              (optionNumber === searchNumber || option.value.startsWith(searchValue))
            );
          });
        } else {
          // Filtrage par texte pour bike-brand
          const searchLower = searchValue.toLowerCase();
          filteredOptions = options.filter((option) => option.text.toLowerCase().includes(searchLower));
        }
      }

      selectedIndex = -1;
      renderOptions();
    };

    /**
     * Render les options filtrées avec highlighting
     */
    const renderOptions = () => {
      // Masquer toutes les options d'abord
      options.forEach((option) => {
        option.element.style.display = 'none';
        option.element.setAttribute('aria-selected', 'false');
      });

      // Afficher et mettre à jour les options filtrées
      filteredOptions.forEach((option, index) => {
        const element = option.element;
        element.style.display = 'block';

        // Highlighting
        const searchValue = input.value.trim();
        if (searchValue) {
          const regex = new RegExp(`(${searchValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
          element.innerHTML = option.text.replace(regex, '<mark>$1</mark>');
        } else {
          element.textContent = option.text;
        }

        // Selection state
        if (index === selectedIndex) {
          element.setAttribute('aria-selected', 'true');
          input.setAttribute('aria-activedescendant', element.id);
        }
      });

      // Mettre à jour la visibilité de la liste
      if (filteredOptions.length === 0) {
        listbox.style.display = 'none';
        isOpen = false;
      } else if (isOpen) {
        listbox.style.display = 'block';
      }
    };

    /**
     * Ouvre la liste d'options
     */
    const openList = () => {
      if (isOpen) return;

      isOpen = true;
      input.setAttribute('aria-expanded', 'true');
      listbox.style.display = 'block';
      filterOptions(input.value);
    };

    /**
     * Ferme la liste d'options
     */
    const closeList = () => {
      if (!isOpen) return;

      isOpen = false;
      selectedIndex = -1;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      listbox.style.display = 'none';
    };

    /**
     * Sélectionne une option
     * @param {Object} option - Option sélectionnée
     */
    const selectOption = (option) => {
      if (!option) return;

      isSelectingOption = true;

      input.value = option.text;
      hiddenInput.value = option.value;
      closeList();

      // Trigger change event
      const changeEvent = new Event('change', { bubbles: true });
      hiddenInput.dispatchEvent(changeEvent);

      // Déclencher la mise à jour de la bike card si applicable
      if (this.handleChange) {
        this.handleChange({ target: hiddenInput });
      }

      setTimeout(() => {
        isSelectingOption = false;
      }, 50);
    };

    /**
     * Navigation clavier
     * @param {KeyboardEvent} event - Événement clavier
     */
    const handleKeydown = (event) => {
      if (!isOpen && !['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) {
            openList();
          } else {
            selectedIndex = Math.min(selectedIndex + 1, filteredOptions.length - 1);
            renderOptions();
          }
          break;

        case 'ArrowUp':
          event.preventDefault();
          if (isOpen) {
            selectedIndex = Math.max(selectedIndex - 1, -1);
            renderOptions();
          }
          break;

        case 'Enter':
          event.preventDefault();
          if (isOpen && selectedIndex >= 0) {
            selectOption(filteredOptions[selectedIndex]);
          } else if (!isOpen) {
            openList();
          }
          break;

        case 'Escape':
          event.preventDefault();
          closeList();
          break;

        case 'Tab':
          closeList();
          break;
      }
    };

    /**
     * Gestion du mousedown sur une option
     * @param {MouseEvent} event - Événement mousedown
     */
    const handleOptionMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();

      const optionElement = event.target.closest('.syb-funnel__autocomplete-option');
      if (optionElement) {
        mousedownStartTime = Date.now();
        optionElement.dataset.mousedownActive = 'true';

        // Annuler tout timer de blur en cours
        if (blurTimer) {
          clearTimeout(blurTimer);
          blurTimer = null;
        }

        // Empêcher le blur de l'input
        input.focus();
      }
    };

    /**
     * Gestion du mouseup sur une option
     * @param {MouseEvent} event - Événement mouseup
     */
    const handleOptionMouseUp = (event) => {
      const optionElement = event.target.closest('.syb-funnel__autocomplete-option');
      if (!optionElement || !optionElement.dataset.mousedownActive) return;

      delete optionElement.dataset.mousedownActive;
      mousedownStartTime = 0;

      const option = filteredOptions.find((opt) => opt.element === optionElement);
      if (option) {
        selectOption(option);
      }
    };

    /**
     * Gestion hover sur les options
     * @param {MouseEvent} event - Événement hover
     */
    const handleOptionHover = (event) => {
      const optionElement = event.target.closest('.syb-funnel__autocomplete-option');
      if (!optionElement) return;

      const optionIndex = filteredOptions.findIndex((opt) => opt.element === optionElement);
      if (optionIndex >= 0) {
        selectedIndex = optionIndex;
        renderOptions();
      }
    };

    // Event listeners
    input.addEventListener('input', (event) => {
      const value = event.target.value;

      // Nettoyer la valeur cachée si le texte ne correspond à aucune option exacte
      const exactMatch = options.find((option) => option.text === value);
      if (!exactMatch) {
        hiddenInput.value = '';
      }

      if (value.trim()) {
        openList();
        filterOptions(value);
      } else {
        filterOptions('');
        if (isOpen) {
          openList();
        }
      }
    });

    input.addEventListener('focus', () => {
      openList();
    });

    input.addEventListener('blur', (event) => {
      // Annuler tout timer de blur précédent
      if (blurTimer) {
        clearTimeout(blurTimer);
      }

      // Délai étendu pour gérer les clics longs (2 secondes)
      blurTimer = setTimeout(() => {
        // Ignorer la logique de validation au blur si on est en cours de prefill
        if (input.dataset.prefilling === 'true') {
          return;
        }

        // Vérifier si un mousedown est actif sur une option
        const activeMousedown = wrapper.querySelector('[data-mousedown-active]');
        if (activeMousedown) return;

        // Vérifier si une interaction est en cours (clic long)
        if (mousedownStartTime > 0 && Date.now() - mousedownStartTime < 2000) {
          return;
        }

        // Vérifier si on est en cours de sélection
        if (isSelectingOption) return;

        if (!wrapper.contains(document.activeElement)) {
          closeList();

          // Valider la sélection au blur
          const currentValue = input.value.trim();
          if (currentValue) {
            const exactMatch = options.find((option) => option.text.toLowerCase() === currentValue.toLowerCase());

            if (exactMatch) {
              selectOption(exactMatch);
            } else {
              // Réinitialiser complètement si pas de correspondance exacte
              input.value = '';
              hiddenInput.value = '';

              // Optionnel: afficher une erreur temporaire
              if (this.showFieldError) {
                this.showFieldError(input, 'Veuillez sélectionner une marque valide dans la liste');

                // Effacer l'erreur après 3 secondes
                setTimeout(() => {
                  if (this.clearFieldError) {
                    this.clearFieldError(input);
                  }
                }, 3000);
              }
            }
          } else {
            // Si vide, s'assurer que hiddenInput est aussi vide
            hiddenInput.value = '';
          }
        }

        blurTimer = null;
      }, 10000);
    });

    input.addEventListener('keydown', handleKeydown);

    // Gestion des événements de souris sur les options
    listbox.addEventListener('mousedown', handleOptionMouseDown);
    listbox.addEventListener('mouseup', handleOptionMouseUp);
    listbox.addEventListener('mouseover', handleOptionHover);

    // Gestionnaire global pour mouseup (au cas où l'utilisateur relâche en dehors)
    document.addEventListener('mouseup', (event) => {
      if (mousedownStartTime > 0) {
        // Nettoyer l'état si mouseup en dehors des options
        const optionElement = event.target.closest('.syb-funnel__autocomplete-option');
        if (!optionElement) {
          // Nettoyer tous les états mousedown actifs dans ce wrapper
          const activeElements = wrapper.querySelectorAll('[data-mousedown-active]');
          activeElements.forEach((el) => delete el.dataset.mousedownActive);
          mousedownStartTime = 0;
        }
      }
    });

    // Gestion du clic sur l'icône pour toggle
    icon.addEventListener('click', () => {
      if (isOpen) {
        closeList();
      } else {
        input.focus();
        openList();
      }
    });

    // Click outside pour fermer
    document.addEventListener('click', (event) => {
      if (isSelectingOption) return;

      // Ignorer si clic sur une option en cours
      const isOptionClick = event.target.closest('.syb-funnel__autocomplete-option');
      const isWrapperClick = wrapper.contains(event.target);

      if (!isWrapperClick && !isOptionClick) {
        closeList();
      }
    });
  }
}

if (!customElements.get('sell-your-bikes-funnel')) {
  customElements.define('sell-your-bikes-funnel', SellYourBikesFunnel);
}
